import { ApplyChartTemplateToDoc } from "../utils/ApplyChartTemplateToDoc";
import { appstr } from "../utils/appstr";
import { appstrp } from "../utils/appstrp";
import { convertStdDateString } from "../utils/convertStdDateString";
import { CPCompare } from "../utils/CPCompare";
import { DetermineBranchStyleNoChildren } from "../utils/DetermineBranchStyleNoChildren";
import { DoAutoZoom } from "../utils/DoAutoZoom";
import { DoOptimizePage } from "../utils/DoOptimizePage";
import { DoOptimizeHelper } from "../utils/DoOptimizeHelper";
import {
  enumAssistOption,
  enumAutoTextType,
  enumBoxDragMode,
  enumBoxHeightMode,
  enumBoxOverlay,
  enumBoxStyleSearch,
  enumBoxSubTypes,
  enumBoxType,
  enumBoxWidthMode,
  enumBranchStyle,
  enumCellContentType,
  enumChartBoxInteractionMode,
  enumChartElement,
  enumChartRenderOrder,
  enumContainerType,
  enumDropType,
  enumElementBorderStyle,
  enumElementLayer,
  enumFileMgrMode,
  enumFillMode,
  enumHierarchyTypes,
  enumHorzCentering,
  enumKeyboardTargetEnterAction,
  enumObjClassNames,
  enumOverrideType,
  enumPageSizeUnits,
  enumPanelMode,
  enumPanelRenderMode,
  enumPictureType,
  enumScalingMethod,
  enumSubChartFlag,
  enumUIModes,
  enumUnitVal,
  enumUpdateProperty,
  enumVertCentering,
  enumWrapTextMode,
  gBrightnessThreshold,
  gOptimizeDebug,
  kAutoTextStrs,
  kBackspaceKey,
  kCBChartBoxContent,
  kDeleteKey,
  kDownArrow,
  kDPIScreen,
  kIndexPageLabelWidth,
  kIndexSCID,
  kInitialPicPosX,
  kInitialPicPosY,
  kLeftArrow,
  kMasterSCID,
  kReturnKey,
  kSpaceCharCode,
  kRightArrow,
  kROUser,
  kSelectAllShortCut,
  kTabKey,
  kUpArrow,
  enumEmptyPositionLayoutType,
  PositionStatus,
  kPositionTitleID,
  enumObjectTypes
} from "../constants";
import { GetTouchDefaultZoom } from "../utils/GetTouchDefaultZoom";
import { GetStdHeaderHeight } from "../utils/GetStdHeaderHeight";
import { GetStdMargins } from "../utils/GetStdMargins";
import { isInViewer } from "../utils/isInViewer";
import { IsRightStyle } from "../utils/IsRightStyle";
import { PageNodesToBoxList } from "../utils/PageNodesToBoxList";
import { ParentInChain } from "../utils/ParentInChain";
import { QAlert } from "./QAlert";
import { QAuxLineElement } from "./QAuxLineElement";
import { QBasePanel } from "./QBasePanel";
import { QBoxGroup } from "./QBoxGroup";
import { QBoxLayout } from "./QBoxLayout";
import { QBoxOverlay_Wand } from "./QBoxOverlay_Wand";
import { QChartBox } from "./QChartBox";
import { QChartBoxCell } from "./QChartBoxCell";
import { QClipboardBuffer } from "./QClipboardBuffer";
import { QElement } from "./QElement";
import { QMarginRect } from "./QMarginRect";
import { QNodeArray } from "./QNodeArray";
import { QPageProps } from "./QPageProps";
import { QPoint } from "./QPoint";
import { ValidPeerSub } from "../utils/ValidPeerSub";
import { kMaxRecSinglePage, uFocusRingColor } from "../whitelabel/brand";
import { Globals } from "./Globals";
import { GetToolbarTargetBox, ShowChartBoxToolbar } from "../utils/handleMouseUp";
import { CenterIt } from "../utils/CenterIt";
import { killHoverPanel } from "../utils/killHoverPanel";
import { DetermineBrightness } from "../utils/DetermineBrightness";
import { DoOptimizePages } from "../utils/DoOptimizePages";
import { FindFirstMRB } from "../utils/FindFirstMRB";
import { isInStandardSubmode } from "../utils/isInStandardSubmode";
import { IsAssistantStyle } from "../utils/IsAssistantStyle";
import { IsLeftStyle } from "../utils/IsLeftStyle";
import { QColorPickerResult } from "./QColorPickerResult";
import { GetNameDefForObj } from "../utils/GetNameDefForObj";
import { ApplyChartTemplateToBoxes } from "../utils/ApplyChartTemplateToBoxes";
import { QDragBoxElement } from "./QDragBoxElement";
import { TestSubChartBreaksBelow } from "../utils/TestSubChartBreaksBelow";
import { GetStdFooterHeight } from "../utils/GetStdFooterHeight";
import { SetupTableCell } from "../utils/SetupTableCell";
import { MeasureTextHeight } from "../utils/MeasureTextHeight";
import { formatArray } from "../utils/formatArray";
import { isInPlanning } from "../utils/isInPlanning";
import { ReRenderCanvas } from "../../UI/src/Utils/StateLib";
import { isPositionDocument } from "../../UI/src/Utils/DataLib";
import { OverrideFactory } from "./OverrideFactory";

/**
 * @typedef {import('./QXDoc').QXDoc} QXDoc
 */

// Guards against reference cycles in nested MRB cleanup, not a chart-depth business rule.
const kMaxMRBDepth = 8;

const isArrowKey = keyCode => [kLeftArrow, kRightArrow, kUpArrow, kDownArrow].includes(keyCode);

const kOffAxisWeight = 2;
const kFocusRingWidth = 2;
const kFocusRingGap = 3;

const rangeGap = (startA, endA, startB, endB) => Math.max(0, startB - endA, startA - endB);

export class QCanvasPanel extends QBasePanel {
  constructor() {
    super();
    this.fCanvasSizeX = null;
    this.fCanvasSizeY = null;

    /**
     *  @type {(QXDoc | null)}
     */
    this.fDoc = null;
    this.fWSMgr = null;
    this.fChartToolbarOpen = false;
    this.fKeyboardNavActive = false;
    this.fKeyboardEntered = false;
    this.fKeyboardFocusDrill = null;
    this.fKeyboardRefocus = null;
    this.fDragSource = null;
    this.fDropElement = null;

    this.fHoverHighlightEnabled = false;
    this.fBoxSelectEnabled = false;
    this.fBoxDragEnabled = false;
    this.fBoxCmdCode = false;
    this.fDropType = enumDropType.none;
    this.fBoxDragMode = enumBoxDragMode.dragModeOff;
    this.fDropMode = false;
    this.fTempID = 0;

    //	this.fPageBoundry = null;
    this.fMarginBoundry = null;
    //this.fWatermark = null;

    this.fObjectName = "QCanvasPanel";
    this.fCurCursor = "default";
    this.fNextAutoText = null;

    this.fOrphanList = null;
    this.fRenderBoxSpacing = false;
    this.fZLayer = 1;
    this.fCtrlExtendsSelection = false;

    this.fInitialHighlightIds = [];

    this.fRenderPhoenix = true;
  }

  SetContainerNumCols(numCols) {
    var selectedBoxes = this.GetSelectedBoxes(true);
    if (selectedBoxes == null) return;

    var n = selectedBoxes.length;
    if (n != 1) return;

    var mrBox = FindFirstMRB(selectedBoxes);
    if (mrBox == null) return;

    var basePageNode = mrBox["nodeRef"];
    basePageNode.SetContainerNumCols(numCols);
    this.RecalcChartLayoutForRoots(null);
  }

  GetMRBLayout(containerType) {
    return this.fDoc.GetLayoutMgr().GetMRBLayout(containerType);
  }

  DoRemoveMRB(aBox, selectedRoots) {
    var boxType = aBox.GetBoxType();
    var isMRB =
      boxType == enumBoxType.nonstandard && aBox.GetBoxSubType() == enumBoxSubTypes.refMRBox;
    if (!isMRB) return false;

    var associatedNodes = aBox.GetAssociatedNodes();
    var associatedBoxes = PageNodesToBoxList(associatedNodes);

    var n = associatedBoxes.length;
    for (var i = 0; i < n; i++) {
      this.PDeleteBox(associatedBoxes[i], selectedRoots);
    }

    return true;
  }

  DoRemoveFromMRBGroup(aBox) {
    if (!aBox) return;
    var container = aBox.GetContainerNode();
    var nodeRef = aBox["nodeRef"];
    if (container != null) {
      container.UnassociateNode(nodeRef);
      aBox.GetLayout()?.SetContainerObjID(null);
      var remainingMembers = container.GetAssociatedNodes();
      var plm = this.GetPageLayoutMgr();
      var scm = this.GetSubChartMgr();
      var curSCID = aBox.GetLayout()?.GetSubChartID();
      var doc = this.fDoc;
      if (remainingMembers.length == 0) {
        plm.RemoveNode(container, true);
        var chartBox = container.GetChartBox();
        // Capture the outer container BEFORE PRemoveChartBox marks chartBox invalid.
        var outerContainer = chartBox?.GetContainerNode?.() ?? null;
        var outerNodeRef = chartBox?.["nodeRef"] ?? null;
        this.PRemoveChartBox(chartBox);
        this._cascadeCleanupOuterMRBs(outerContainer, outerNodeRef, plm);
      }
      // Container nodes have no data object and no subchart entries.
      if (!aBox.GetDataObj()) return;
      var dataObjID = aBox.GetDataObjID();
      var index = scm.FindSCSubNodeIndex(dataObjID, curSCID);
      var childSCNode = scm.GetIndexSCNode(index);
      if (container.GetSubChartFlag() == enumSubChartFlag.drilldown) {
        var prevSubChartID = childSCNode.GetSubChartID();
        if (aBox.GetDataObj().GetNumChildren() > 0) {
          if (aBox.GetSubChartFlag() == enumSubChartFlag.drilldown) {
            //The top node should have been drill up already, but this got lost in one case.
            childSCNode.GetLayout().SetTopOptions();
            //var otherSubChart = scm.FindSubChart(prevSubChartID);
            scm.ReformContainerSubchart(childSCNode, curSCID);
          } else {
            doc.GroupNodes([childSCNode], true, null);
            scm.RemoveSCNode(aBox.GetDataObjID(), prevSubChartID);
            var oldSubChart = scm.FindSubChart(prevSubChartID);
            oldSubChart.RemoveTopDBObj(aBox.GetDataObj());
            scm.UpdateSubChartID(aBox, prevSubChartID, curSCID);
          }
        } else {
          doc.GroupNodes([childSCNode], true, null);
          scm.RemoveSCNode(aBox.GetDataObjID(), childSCNode.GetSubChartID());
          var oldSubChart = scm.FindSubChart(childSCNode.GetSubChartID());
          oldSubChart.RemoveTopDBObj(aBox.GetDataObj());
        }
      } else if (container.GetSubChartFlag() == enumSubChartFlag.drillup) {
        var curSubChart = scm.FindSubChart(curSCID);
        var prevSubChartID = childSCNode.GetSubChartID();
        doc.GroupNodes([childSCNode], true, null);
        scm.RemoveSCNode(aBox.GetDataObjID(), prevSubChartID);
        aBox.GetLayout().SetSubChartFlag(enumSubChartFlag.nobreak);
        curSubChart.RemoveTopDBObj(aBox.GetDataObj());
        if (curSubChart.GetTopDBObjID() == aBox.GetDataObjID()) {
          if (curSubChart.fDataObjArr && curSubChart.fDataObjArr.length > 0) {
            curSubChart.SetTopDBObj(curSubChart.fDataObjArr[0]);
          } else {
            //remove subchart - this shouldn't be possible.
            console.log("unhandled subchart removal-bad condition");
            //this.RemoveSubChart(childrenSCID)
          }
        }
      } else {
        //check to see if I was in the top of a subchart, and reassign the top db stuff accordingly.
        //This should only be in the manual case- handled elsewhere when done programatically.
        //TODO: I know this shares some code with 'reformContainerSubchart' - should be merged together.
        var curSubChart = scm.FindSubChart(curSCID);
        if (curSubChart.RemoveTopDBObj(aBox.GetDataObj())) {
          if (curSubChart.fDataObjArr.length > 0)
            curSubChart.SetTopDBObj(curSubChart.fDataObjArr[0]);
        }
      }
    }
  }

  _cascadeCleanupOuterMRBs(outerContainer, outerNodeRef, plm) {
    var depth = 0;
    while (outerContainer != null && outerNodeRef != null && depth < kMaxMRBDepth) {
      outerContainer.UnassociateNode(outerNodeRef);
      if (outerContainer.GetAssociatedNodes().length !== 0) break;
      plm.RemoveNode(outerContainer, true);
      var outerBox = outerContainer.GetChartBox();
      var nextOuter = outerBox?.GetContainerNode?.() ?? null;
      var nextOuterRef = outerBox?.["nodeRef"] ?? null;
      this.PRemoveChartBox(outerBox);
      outerContainer = nextOuter;
      outerNodeRef = nextOuterRef;
      depth++;
    }
  }

  AddToMRB(mrBox, selectedBoxes) {
    var n = selectedBoxes.length;
    if (n < 1) return;

    var basePageNode = mrBox["nodeRef"];
    for (var i = 0; i < n; i++) {
      var b = selectedBoxes[i];
      if (b.GetBoxType() == enumBoxType.standard) {
        var childNode = b["nodeRef"];
        var containerNode = childNode.GetContainerNode();
        if (containerNode == null) basePageNode.AssociateNode(childNode);
        var layout = b.GetLayout();
        layout.SetContainerObjID(mrBox.GetDataObjID());
        //layout.SetContainerRef();
      }
    }
  }

  GroupPeers(selectedBoxes, unGroupOnly, MRBlayout) {
    //Do we have an existing MRB
    var fullSelection = [];

    for (var boxIdx = 0; boxIdx < selectedBoxes.length; boxIdx++) {
      //Sanitize the selection list to be only pure chart boxes.
      var aBox = selectedBoxes[boxIdx];
      var boxType = aBox.GetContainerType();
      if (boxType !== enumContainerType.nonmrb) {
        //Grab any boxes from any selected MRB's that we don't have selected already.
        var associatedNodes = aBox.GetAssociatedNodes();
        var associatedBoxes = PageNodesToBoxList(associatedNodes);
        for (
          var associatedBoxIdx = 0;
          associatedBoxIdx < associatedBoxes.length;
          associatedBoxIdx++
        ) {
          var anAssociatedBox = associatedBoxes[associatedBoxIdx];
          if (fullSelection.indexOf(anAssociatedBox) === -1) {
            fullSelection.push(anAssociatedBox);
          }
        }
      } else if (
        boxType !== enumBoxType.nonstandard &&
        aBox.GetBoxSubType() !== enumBoxSubTypes.refMRBox
      ) {
        if (fullSelection.indexOf(aBox) === -1) fullSelection.push(aBox);
      }
    }

    var curContainer = null;

    for (boxIdx = fullSelection.length - 1; boxIdx >= 0; boxIdx--) {
      aBox = fullSelection[boxIdx];
      //Make sure lone boxes are not in a group
      //if (curContainer == null) //CB 2020-03-29 - I put this if statement in here for the curContainer but I don't think its helpful anymore.
      //I think this is better- prior way caused more harm than good
      this.DoRemoveFromMRBGroup(aBox);
      if (aBox.CanBeGrouped() && unGroupOnly) {
        //Ungrouping and I have no parent and I am not the only top box.
        var dataObj = aBox.GetDataObj();
        this.SetAsOrphan(dataObj, true);
      }
    }

    if (!unGroupOnly) {
      //var existingBranchStyle = fullSelection[0].GetBranchStyle();
      //TODO: find the right place for this code to live. Also, I think we want to control rows instead?
      //var colResult = this.DetermineContainerColSetting(existingBranchStyle);
      //if (colResult > fullSelection.length)
      //	colResult = fullSelection.length;
      var newBaseMRB = this.fWSMgr.DoAddChartBox(this, enumBoxSubTypes.refMRBox, null, false);

      //Fullselection may need to be reordered to get the right result. That's why it is called first - index0 is super important.
      if (MRBlayout) this.fWSMgr.ApplyLayoutToBoxList(MRBlayout, [newBaseMRB], null, false);

      this.AddToMRB(newBaseMRB, fullSelection);
      // Recalculate layout now that fAssociatedObjIDs is populated, so cell sizes and
      // QChartBoxCell objects are initialized with the correct employee data objects.
      if (MRBlayout) newBaseMRB.ForceLayoutRecalc(true);
      //Use result of advanced column AI to make the box more appropriate.
      //newBaseMRB.SetContainerNumCols(colResult);

      return newBaseMRB;
    } else {
      //Special handling for coheads
      //Try for coheads
      for (boxIdx = 0; boxIdx < fullSelection.length; boxIdx++) {
        aBox = fullSelection[boxIdx];
        dataObj = aBox.GetDataObj();
        if (dataObj == null) {
          console.log("Ungroup called on non-DB obj box - why? ");
          continue;
        }
        var scm = this.GetSubChartMgr();
        var curSubChart = scm.GetCurrentSubChart();
        var rootDBObj = curSubChart.GetTopDBObj();
        if (dataObj.IsOrphan() && dataObj !== rootDBObj) {
          //This was a cohead that got ungrouped. Special case - take it to parking lot.
          this.SelectBranch(aBox, true);
          var selectedBoxes = this.GetSelectedBoxes(false);
          this.DoOrphanSelection(selectedBoxes, true);
          //TODO: Parking Panel DNE but may need to be replaced later on.
          // var parkingPanel = this.fWSMgr.GetTabPanel("Parking");
          // if (parkingPanel) {
          //   parkingPanel.SetLevelToTop();
          //   parkingPanel.UpdatePanel(null);
          // }
          return false;
        }
      }
    }
    return true;
  }

  DetermineContainerColSetting(existingBranchStyle) {
    var colResult = 1;
    switch (existingBranchStyle) {
      case enumBranchStyle.tree:
        colResult = 4;
        break;
      case enumBranchStyle.twocolumn:
      case enumBranchStyle.fourcolumn:
      case enumBranchStyle.sixcolumn:
      case enumBranchStyle.eightcolumn:
      case enumBranchStyle.twelvecolumn:
      case enumBranchStyle.sixteencolumn:
      case enumBranchStyle.twentycolumn:
        colResult = 2;
        break;
    }
    return colResult;
  }

  InitCanvasPanel(backgroundPanel, theDoc, wsMgr) {
    this.fDoc = theDoc;
    this.fWSMgr = wsMgr;

    var pageMgr = this.GetPageMgr();
    this.fCanvasSizeX = pageMgr.GetMinPageWidth();
    this.fCanvasSizeY = pageMgr.GetMinPageHeight();
    // mobile default zoom to optimize chart viewing; phone screens get a bit more (see GetTouchDefaultZoom)
    if (Globals._mobileDevice) this.SetScaleFactor(GetTouchDefaultZoom());
    this.InitBasePanel(backgroundPanel);
    this.DoCustomResize();
    this.PositionAltBackground();

    this.SetPanelToolMode("ToolMode1");
    this.SetPanelClickCmd("CanvasClick");
    this.SetPanelDblClickCmd("CanvasDblClick");
    this.SetMarqueeEnabled(true);

    this.SetDropInfo(false, "Masters", ["jpg", "png"], null, null, this, false, true);
  }

  /*
  TODO performance enhancement that needs more testing.
  IsShown() {
    var restrictedModes = [
      enumUIModes.boxlayouteditor,
      enumUIModes.settings,
      enumUIModes.directory,
      enumUIModes.reports
    ];
    if (restrictedModes.includes(Globals._uiConfig.GetUIMode())) return false;
    return this.fShown;
  };
   */

  GetDropEnabled() {
    return (
      Globals._uiConfig.InBackgroundEditorMode() &&
      Globals._accountInfo.GetUserRole() !== kROUser &&
      Globals._wsMgr.fFileMgr === null
    );
  }

  ReportRenderInfo() {
    //return true;
    return false;
  }

  HandlePostDropUpload(dropInfo, dropTarget) {
    var srcElem = null;
    var chartBoxTarget = dropTarget != null && dropTarget instanceof QChartBox;
    if (chartBoxTarget) {
      var roMode = Globals._accountInfo.ReadOnlyMode();
      if (dropTarget.IsStaticPicture() && !roMode) srcElem = dropTarget;
    }

    var theDoc = this.fDoc;
    // clear cache to get latest version
    theDoc.fPictureMgr.RemovePicture(dropInfo["directory"], dropInfo["fileName"]);

    var callback =
      Globals._uiConfig.InBackgroundEditorMode() && !chartBoxTarget
        ? {
            ImageLoaded: pRef => this.fWSMgr.BroadcastTopic("DropBackgroundPhoto", { pRef })
          }
        : this.fWSMgr;

    theDoc.LoadPicture(dropInfo["directory"], dropInfo["fileName"], callback, this, srcElem, true);
  }

  DoHoverBox() {
    var width = this.GetWidth();
    var height = this.GetHeight();
    var posX = 5;
    var posY = 5;
    if (this.fHoverBox) {
      this.RemoveElement(this.fHoverBox);
    }
    this.fHoverBox = this.AddHoverBox(
      this,
      posX,
      posY,
      width,
      height,
      enumChartRenderOrder.overlay,
      enumElementLayer.master
    );
  }

  HideHoverBox() {
    if (this.fHoverBox) {
      this.fHoverBox.SetShownState(false);
    }
  }

  GainFocus() {
    var defaultRenderMode = enumPanelRenderMode.all;
    if (Globals._uiConfig.InBackgroundEditorMode())
      defaultRenderMode = enumPanelRenderMode.masterOnly;
    this.ToggleViewMode(defaultRenderMode, true);
  }

  GetFillColor() {
    var docMgr = this.fWSMgr.GetDocMgr();
    return docMgr.GetBackgroundColor();
  }

  GetFillColor2() {
    var docMgr = this.fWSMgr.GetDocMgr();
    return docMgr.GetBackgroundColor2();
  }

  GetFillMode() {
    return this.GetBackgroundFillMode();
  }

  GetBackgroundFillMode() {
    var docMgr = this.fWSMgr.GetDocMgr();
    return docMgr.GetBackgroundFillMode();
  }

  DoClosePanel() {
    // this.fDoc = null;
    this.fMarginBoundry = null;
    //this.fWatermark = null;
    this.fAltBackground = null;
    this.ClearSelection();
    this.fWSMgr.CloseLayoutHoverToolbar();
  }

  //===============================================
  HandleRightClickCmd(cmdCode, aCmd) {
    var setToolMode = "ToolMode1";
    var clearSelection = true;
    //  var selectedPict =  null;
    var w = this.fWSMgr;
    var eventTracker = w.GetUsageTracker();
    switch (cmdCode) {
      case "selecttool":
        setToolMode = "ToolMode1";
        // clearSelection = false;
        break;
      case "pantool":
        setToolMode = "ToolMode2";
        break;
      case "moveobj":
        setToolMode = "ToolMode3";
        break;
      case "moveboxtool":
        setToolMode = "ToolMode5";
        break;
      case "addsubtool":
        setToolMode = "ToolMode6";
        break;
      case "branchstyles":
        var sourcePanel = aCmd.GetSourcePanel();
        var x = sourcePanel.GetPositionX();
        var y = sourcePanel.GetPositionY();
        w.DoBranchStylePicker(x, y);
        break;
      case "AddAssistant":
      case "addsub":
        var isAssistant = cmdCode == "AddAssistant";
        var selectedBox = this.GetFirstSelectedBox(false);
        if (selectedBox) w.DoAddSub(selectedBox, isAssistant);
        break;
      case "AddHolder":
        var selectedBox = this.GetFirstSelectedBox(false);
        if (selectedBox) w.DoAddHolder(selectedBox);
        break;
      case "insertbreak":
        w.InsertSubChartBreak();
        break;
      case "removebreak":
        w.RemoveSubChartBreak();
        break;
      case "boxlayouteditor":
        w.DoBoxLayoutEditor(null, null, false);
        break;
      case "groupprops":
        var sourcePanel = aCmd.GetSourcePanel();
        var x = sourcePanel.GetPositionX();
        var y = sourcePanel.GetPositionY();
        //w.DoGroupProperties(x,y);
        break;
      case "ungroup":
        w.GroupingCmd("ungrouppeer");
        break;
      case "lockbox":
        var sourcePanel = aCmd.GetSourcePanel();
        var x = sourcePanel.GetPositionX();
        var y = sourcePanel.GetPositionY();
        w.DoBoxLocking(x, y);
        break;
      case "maketop":
        w.DoMakeTop();
        break;
      case "doprofile":
        clearSelection = false;
        var element = this.GetFirstSelectedElement(enumChartElement.chartBox);
        w.ShowProfile(element);
        break;
      case "Record Details":
        var element = this.GetFirstSelectedElement(enumChartElement.chartBox);
        w.ShowRecordInfo(element);
        break;
      case "Source Info":
        var element = this.GetFirstSelectedElement(enumChartElement.chartBox);
        w.ShowSourceInfo(element);
        break;
      case "cut":
        w.DoCutCopy(null, true);
        break;
      case "copy":
        w.DoCutCopy(null, false);
        break;
      case "paste":
        w.DoPaste();
        var curMode = w.GetToolMode();
        if (curMode == setToolMode) setToolMode = null;
        break;
      case "copyformat":
        w.DoCopyFormat(aCmd.fSourceElement);
        break;
      case "DeleteSelection":
        this.DeleteSelection(false);
        setToolMode = null;
        break;
      case "SelectAutoText":
        var element = this.GetFirstSelectedElement(null);
        if (element) aCmd.fSourceElement = element;
        aCmd.fSourcePanel = this;
        var autoTextDD = this.fWSMgr.DoAutoTextSelector(aCmd, element);
        autoTextDD.fSourceBox = element;
        break;
      case "imageprops":
        this.fWSMgr.DoImageProperties(aCmd);
        setToolMode = null;
        break;
      case "imageinfo":
        var selectedPict = this.GetFirstSelectedElement(null);
        w.HandleImageInfo(selectedPict);
        setToolMode = null;
        break;
      case "SelectImageFile":
        var selectedImage = this.GetFirstSelectedElement(null);
        var filemgrMode =
          selectedImage != null ? enumFileMgrMode.replaceimage : enumFileMgrMode.selectimage;
        w.DoFileMgr(filemgrMode);
        break;
      case "UploadNewFile":
        var selectedImage = this.GetFirstSelectedElement(null);
        aCmd["dropTarget"] = selectedImage;
        aCmd["directory"] = "Masters";
        aCmd["fileTypes"] = ["jpg", "png"];
        aCmd["skipFM"] = true;
        w.UploadFile(aCmd);
        break;
      case "reloadpic":
        var selectedBox = this.GetFirstSelectedBox(false);
        if (selectedBox) {
          var nestedElements = selectedBox.fElements;
          if (nestedElements != null) {
            for (var i = 0; i < nestedElements.length; i++) {
              var anElement = nestedElements[i];
              if (anElement instanceof QChartBoxCell) {
                if (anElement.GetPictureType() == enumPictureType.fieldPicture) {
                  anElement.ReloadPic();
                  break;
                }
              }
            }
          }
        }
        break;
      case "ToolMode9":
      case "ToolMode13":
      case "ToolMode14":
      case "ToolMode15":
      case "ToolMode16":
        setToolMode = cmdCode;
        break;
      default:
        setToolMode = null;
        break;
    }
    if (setToolMode != null) w.SetToolMode(setToolMode, clearSelection);
  }

  // here we target the parent element if the current target is not selected for right click actions on canvas
  GetRightClickTarget(hitElement) {
    var target = hitElement;

    var isCell = this.IsElementBoxCell(hitElement);
    if (isCell && !hitElement.IsSelected()) target = hitElement.GetParentBox();

    // var isBox = this.IsElementChartBox(hitElement, true);

    // if (isBox) {
    //   var containerNode = hitElement.GetContainerNode();
    //   if (containerNode != null && !hitElement.IsSelected()) target = containerNode.GetChartBox();
    // }

    return target;
  }

  DoHandleRightClick(hitElement, x, y, event) {
    var s = null;
    var roMode = Globals._accountInfo.ReadOnlyMode();
    var w = this.fWSMgr;
    if (hitElement) {
      hitElement = this.GetRightClickTarget(hitElement);

      var isBox = this.IsElementChartBox(hitElement, true);
      var elementType = hitElement?.GetElementType();

      if (isBox) {
        w.SetToolMode("ToolMode1", true);
        this.DoSelectElement(hitElement, true);
      }

      var eSelected = hitElement.IsSelected();

      if (isBox) {
        var subType = hitElement.GetBoxSubType();
        killHoverPanel(true);
        // Employee/position box actions live in the click toolbar (ChartBoxContextToolbar).
        if (roMode) return;
        switch (subType) {
          case enumBoxSubTypes.textBox:
          case enumBoxSubTypes.enumeratedTable:
          case enumBoxSubTypes.refChart:
          case enumBoxSubTypes.picture:
          case enumBoxSubTypes.autoText:
          case enumBoxSubTypes.refBoxLegend:
          case enumBoxSubTypes.refBoxTable:
            w.fContextMenuHandler?.("background", event, { box: hitElement });
            return;
          default:
            return;
        }
      } else {
        if (elementType === enumChartElement.boxCell) {
          if (eSelected || roMode) {
            w.fContextMenuHandler?.("cell", event, {
              box: hitElement.GetParentBox(),
              cell: hitElement
            });
          }
        }
      }
    } else if (
      Globals._uiConfig.GetUIMode() == enumUIModes.edit ||
      Globals._uiConfig.InBackgroundEditorMode()
    ) {
      w.fContextMenuHandler?.("canvas", event);
    }
  }

  InitalBoxPlacement(defLayout) {
    // Return the middle of the box (not the upper left hand corner)
    var p = new QPoint();

    var pageMgr = this.GetPageMgr();
    var r = pageMgr.GetPageMarginsInt(true);
    var topMargin = r.fTop;

    //	var lX = defLayout.GetBoxWidth();
    var cW = this.GetWidth();
    var lY = defLayout.GetBoxHeight(false);

    p.fX = Math.floor(cW / 2); // - Math.floor(lX/2);

    // Rounding up means that centerchart(AlignChartTop) will match placement
    p.fY = topMargin + Math.round(lY / 2);

    return p;
  }

  IsElementBox(anElement) {
    return this.PIsElementBox(anElement, false);
  }

  PIsElementBox(anElement, includeMRB) {
    var elementType = anElement.GetElementType();
    var isBox;

    var isBox = elementType == enumChartElement.chartBox || elementType == enumChartElement.textBox;
    if (isBox && !includeMRB) {
      var elementSubType = anElement.GetBoxSubType();
      isBox = elementSubType != enumBoxSubTypes.refMRBox;
    }
    /*
	switch (elementType)
	{
		case enumChartElement.chartBox:
		case enumChartElement.textBox:
			isBox = true;
			break;
	}
	*/

    return isBox;
  }

  IsElementBoxCell(anElement) {
    var elementType = anElement.GetElementType();
    var isBoxCell = elementType == enumChartElement.boxCell;
    return isBoxCell;
  }

  IsElementTextBox(anElement) {
    var elementType = anElement.GetElementType();
    var isTBox = elementType == enumChartElement.textBox;
    return isTBox;
  }

  IsElementChartBox(anElement, includeTextBox) {
    var elementType = anElement.GetElementType();
    var isBox = false;
    switch (elementType) {
      case enumChartElement.chartBox:
        isBox = true;
        break;
      case enumChartElement.textBox:
        isBox = includeTextBox;
        break;
    }

    return isBox;
  }

  GetPageMgr() {
    return this.fDoc.GetPageMgr();
  }

  GetPageLayoutMgr() {
    return this.fDoc.GetPageLayoutMgr();
  }

  PositionMargins() {
    if (Globals._accountInfo.ReadOnlyMode() || (isInViewer() && isInStandardSubmode())) {
      return;
    }

    var pageMgr = this.GetPageMgr();
    var marginR = pageMgr.GetPageMarginsInt(false);
    if (this.fMarginBoundry == null) {
      this.fMarginBoundry = new QMarginRect();
      this.fMarginBoundry.InitMarginRect(pageMgr, this);
      this.fMarginBoundry.SetLayer(enumElementLayer.master);
      this.PushElement(this.fMarginBoundry);
    }

    var canvasWidth = this.fCanvasSizeX;
    var canvasHeight = this.fCanvasSizeY;
    this.fMarginBoundry.PositionElement(marginR.fLeft, marginR.fTop);
    var marginRWidth = canvasWidth - (marginR.fRight + marginR.fLeft);
    var marginRHeight = canvasHeight - (marginR.fTop + marginR.fBottom);
    this.fMarginBoundry.SizeElement(marginRWidth, marginRHeight);
    this.fWSMgr.BroadcastTopic("BoundaryChange");
  }

  ClearGradientCache() {
    this.fGradient = null;
  }

  DoGetGradientVals() {
    var p = new QPoint();
    p.InitPoint(this.GetFillColor(), this.GetFillColor2());
    return p;
  }

  ResizeCanvasCenter(priorScaleFactor) {
    var priorPanX = this.GetPanX();
    var priorPanY = this.GetPanY();

    if (Globals._mobileDevice) {
      priorPanX = 0;
      priorPanY = 0;
    }

    var priorPortWidth = this.fPortWidth;
    var priorPortHeight = this.fPortHeight;

    this.DoResizeCanvas();
    // Now calculate the pan to center

    this.UpdatePanOffset(priorPanX, priorPanY, priorPortWidth, priorPortHeight, priorScaleFactor);

    this.PositionMargins();
    this.PositionAltBackground();
  }

  PanToTopCenter() {
    var portWidth = this.fPortWidth;
    var scaleFactor = this.GetScaleFactor();
    var canvasWidth = this.fWidth;
    var unscaledPortWidth = Math.round(portWidth / scaleFactor);
    var maxPanX = canvasWidth - unscaledPortWidth;

    var panY = 0;
    var panX = Math.round(maxPanX / 2);

    if (panX < 0) panX = 0;

    this.SetPanX(panX);
    this.SetPanY(panY);
  }

  HandleAutoZoom() {
    if (Globals._uiConfig.fDoAutoZoom) this.DoCustomResize();
    this.PanToTopCenter();
  }

  DoAutoZoom() {
    if (!Globals._uiConfig.fDoAutoZoom) return;

    DoAutoZoom(this.fBackgroundPanel, this);
  }

  DoFitToScreen() {
    //TODO: This function needs a better name. And shouldn't be called outside of a doc existing, probably.
    if (Globals._publishRef) return;
    var w = this.fWSMgr;
    var docMgr = this.fDoc.GetDocMgr();
    if (!this.fDoc) return;
    var basepm = this.fDoc.GetPageMgr();
    var plm = this.GetPageLayoutMgr();
    let fitToScreen = plm.FitToScreen();
    if (!fitToScreen) return;
    var pageProps = basepm.GetPageProps();
    var docPrefs = docMgr.GetDocPrefs();
    var pageOpt = docPrefs.GetPageOpt(false);

    //pageOpt.SetAllowSort(true);
    var targetWidthPX = this.fBackgroundPanel.GetWidth();
    var targetHeightPX = this.fBackgroundPanel.fHeight;

    var currentZoom = this.GetScaleFactor();
    if (currentZoom < 1) {
      targetWidthPX = Math.round(targetWidthPX / currentZoom);
      targetHeightPX = Math.round(targetHeightPX / currentZoom);
    }

    pageProps.fPageHeight = targetHeightPX;
    pageProps.fPageWidth = targetWidthPX;
    plm.fBranchMgr.InitBranchMgr(
      pageProps.fPageWidth - pageProps.fMargins.fLeft - pageProps.fMargins.fRight
    );
    pageOpt.SetRealAspectRatio(pageProps.fPageWidth / pageProps.fPageHeight);
    this.ChangePageProps(pageProps, enumPageSizeUnits.pixels);

    //Check to see if the current subchart should be optimized to make best use of the new space.
    var scm = this.fDoc.GetSubChartMgr();
    var cursc = scm.GetCurrentSubChart();

    if (w.GetDoc() && !cursc?.fBranchStyleOverride)
      //No need to optimize until we have the doc attached to the workspace manager
      DoOptimizePages(w);
  }

  DoCustomResize() {
    this.DoAutoZoom();
    this.DoFitToScreen();
    this.DoResizeCanvas();
    this.PositionMargins();
    this.PositionAltBackground();
    this.fWSMgr.fBackgroundPanel.PostStandardResize();
  }

  UpdateOverrideSpacing(theElement, parameter, whichSpacing, doOverride) {
    var spacing = theElement.GetBoxSpacing();
    switch (whichSpacing) {
      case enumUpdateProperty.leftSpacing:
        spacing.fLeft = parameter;
        break;
      case enumUpdateProperty.rightSpacing:
        spacing.fRight = parameter;
        break;
      case enumUpdateProperty.bottomSpacing:
        spacing.fBottom = parameter;
        break;
      case enumUpdateProperty.topSpacing:
        spacing.fTop = parameter;
        break;
    }
    if (doOverride) {
      theElement.SetOverrideBoxSpacing(spacing);
    } else {
      theElement.SetBoxSpacing(spacing);
    }
  }

  UpdateOverrideMargin(theElement, parameter, whichMargin) {
    var margins = theElement.GetBoxMargins();
    switch (whichMargin) {
      case enumUpdateProperty.leftMargin:
        margins.fLeft = parameter;
        break;
      case enumUpdateProperty.rightMargin:
        margins.fRight = parameter;
        break;
      case enumUpdateProperty.bottomMargin:
        margins.fBottom = parameter;
        break;
      case enumUpdateProperty.topMargin:
        margins.fTop = parameter;
        break;
    }
  }

  UpdateChartBox(theElement, parameter, theProperty, doOverrides) {
    var needPageRecalc = false;
    //It is me, home of the deltas.
    //How do deltas work? There's only a small 'persistent' portion of each layout built up dynamically.
    //Only that part is saved/loaded and it's applied last to a box's properties.
    if (doOverrides) theElement.fLayout.fOverrides = theElement.fLayout.fOverrides || {};
    let isOverrideChange = true;
    var layout = theElement.fLayout;
    switch (theProperty) {
      case enumUpdateProperty.boxFillColor:
      case enumUpdateProperty.boxBorderColor:
      case enumUpdateProperty.boxShapeType:
      case enumUpdateProperty.boxShadow:
      case enumUpdateProperty.boxBorderWeight:
      case enumUpdateProperty.boxBorderStyle:
      case enumUpdateProperty.boxDashStyle:
      case enumUpdateProperty.mgrVSpacing1:
      case enumUpdateProperty.mgrVSpacing2:
      case enumUpdateProperty.resetspacing:
      case enumUpdateProperty.spacingMode:
      case enumUpdateProperty.boxFlag:
        let layout = theElement.GetLayout();
        needPageRecalc = layout.UpdateOverride(parameter, theProperty, doOverrides);
        break;
      case enumUpdateProperty.leftSpacing:
      case enumUpdateProperty.rightSpacing:
      case enumUpdateProperty.bottomSpacing:
      case enumUpdateProperty.topSpacing:
        this.UpdateOverrideSpacing(theElement, parameter, theProperty, doOverrides);
        needPageRecalc = true;
        break;
      case enumUpdateProperty.leftMargin:
      case enumUpdateProperty.rightMargin:
      case enumUpdateProperty.bottomMargin:
      case enumUpdateProperty.topMargin:
        this.UpdateOverrideMargin(theElement, parameter, theProperty);
        needPageRecalc = true;
        break;
      case enumUpdateProperty.cellWrapText:
      case enumUpdateProperty.cellTextItalic:
      case enumUpdateProperty.fieldDisplayMode:
      case enumUpdateProperty.fontName:
      case enumUpdateProperty.fontSize:
      case enumUpdateProperty.cellTextBold:
      case enumUpdateProperty.cellExpandRow:
      case enumUpdateProperty.picFrame:
      case enumUpdateProperty.picMask:
      case enumUpdateProperty.cellFillColor:
        needPageRecalc = theElement.UpdateBoxCells(parameter, theProperty, doOverrides);
        break;
      case enumUpdateProperty.cellRefType: {
        let layout = theElement.GetLayout();
        if (layout?.IsLayoutCellRefType()) {
          needPageRecalc = layout.UpdateOverride(parameter, theProperty, doOverrides);
        } else {
          needPageRecalc = theElement.UpdateBoxCells(parameter, theProperty, doOverrides);
        }
        break;
      }

      case enumUpdateProperty.cellTextUnderline:
      case enumUpdateProperty.cellFontColor:
      case enumUpdateProperty.cellTextAlign:
      case enumUpdateProperty.cellTextVAlign:
        theElement.UpdateBoxCells(parameter, theProperty, doOverrides);
        //theElement.UpdateCellProperty(parameter, theProperty,false);
        break;
      case enumUpdateProperty.branchStyle:
        // Root always has a tree branch style - which cannot be changed
        if (!theElement.IsRoot()) {
          theElement.SetBranchStyle(parameter, doOverrides);
          this.FixChildBranchStyles(theElement, parameter, doOverrides);
          theElement.SmartAlign();
          if (!IsAssistantStyle(parameter)) {
            let scm = this.GetSubChartMgr();
            let cursc = scm.GetCurrentSubChart();
            cursc.fBranchStyleOverride = true;
          }
        }
        needPageRecalc = true;
        break;
      case enumUpdateProperty.contentLock:
        theElement.SetContentLock(parameter);
        needPageRecalc = true; // Why is needPageRecalc true??
        break;
      case enumUpdateProperty.layoutLock:
        theElement.SetLayoutLock(parameter);
        needPageRecalc = true;
        break;
      case enumUpdateProperty.hierLock:
        theElement.SetPositionLock(parameter);
        needPageRecalc = true;
        break;
      case enumUpdateProperty.boxLayout:
        var curLayout = theElement.GetLayout();
        var newLayout = parameter.Clone();
        this.fWSMgr.LayoutChanged(theElement, curLayout, newLayout);
        theElement.ResetBoxLayout(false);
        isOverrideChange = false;
        needPageRecalc = true;
        break;
      case enumUpdateProperty.boxOverrides:
        var curLayout = theElement.GetLayout();
        var newLayout = parameter.Clone();
        this.fWSMgr.LayoutChanged(theElement, curLayout, newLayout);
        theElement.ResetBoxLayout(false);
        needPageRecalc = true;
        break;
    }
    if (isOverrideChange && doOverrides) {
      this.fDoc.GetLayoutMgr().UpdateOverrideInfo(layout);
    }
    if (doOverrides) {
      theElement.RemoveOverlay(QBoxOverlay_Wand);
      if (layout.HasOverrides()) {
        theElement.AddElement(enumBoxOverlay.wand);
      }
    }
    return needPageRecalc;
  }

  FixChildBranchStyles(theBox, parentStyle, doOverrides) {
    var dbObj = theBox.GetDataObj();
    var n = dbObj.GetNumChildren();
    for (var i = 0; i < n; i++) {
      var c = dbObj.GetIndexChild(i);
      var childBox = this.GetChartBoxFromDBObj(c);
      if (childBox) {
        var subBranchStyle = childBox.GetBranchStyle();
        var validStyle = ValidPeerSub(theBox, subBranchStyle);
        if (!validStyle) {
          if (subBranchStyle === enumBranchStyle.assistantpeerleft) {
            if (IsRightStyle(parentStyle)) subBranchStyle = enumBranchStyle.assistantpeerright;
            else subBranchStyle = enumBranchStyle.assistantright;
          } else if (subBranchStyle === enumBranchStyle.assistantpeerright) {
            if (IsLeftStyle(parentStyle)) subBranchStyle = enumBranchStyle.assistantpeerleft;
            else subBranchStyle = enumBranchStyle.assistantleft;
          }
          childBox.SetBranchStyle(subBranchStyle, doOverrides);
        }
      }
    }
  }

  UpdateConnectingLine(theElement, parameter, theProperty) {
    switch (theProperty) {
      case enumUpdateProperty.connectingLineColor:
        theElement.SetConnectingLineColor(parameter.fSelectedColor);
        theElement.SetConnectingLineFillMode(parameter.fFillMode);
        break;
      case enumUpdateProperty.connectingLineThickness:
        theElement.SetConnectingLineThickness(parameter);
        break;
      case enumUpdateProperty.connectingLineDash:
        theElement.SetConnectorDashStyle(parameter);
        break;
      case enumUpdateProperty.subGroupLineDash:
        theElement.SetParentConnectorDashStyle(parameter);
        break;
    }

    return false;
  }

  GetPositionBox(element) {
    var positionElement = null;
    if (element == null) return null;

    var dataObj = element.GetDataObj();
    if (dataObj == null) return null;

    // is position element
    if (!dataObj?.IsHolderObj()) return element;

    // is holder element, return parent container box
    var nodeRef = element.nodeRef;
    var containerNode = nodeRef.GetContainerNode();
    if (containerNode) positionElement = containerNode.fChartBox;

    return positionElement;
  }

  AddSelectedRoot(selectedRoots, theElement) {
    var elementType = theElement.GetElementType();
    if (elementType == enumChartElement.boxCell) theElement = theElement.GetParentBox();

    if (this.fDoc.IsPositionDocument()) {
      theElement = this.GetPositionBox(theElement);
      if (!theElement) return null;
    }

    var rootNode = this.GetPLMRootNode(theElement);

    if (selectedRoots.indexOf(rootNode) == -1) selectedRoots.push(rootNode);

    return rootNode;
  }

  //REDPIN
  RecalcChartLayoutForRoots(selectedRoots) {
    var numRoots = 0;
    if (selectedRoots != null) numRoots = selectedRoots.length;

    if (numRoots == 0) this.RecalculateChartLayout(null);
    else {
      for (var i = 0; i < numRoots; i++) this.RecalculateChartLayout(selectedRoots[i]);
    }
  }

  // only returns one root per container
  GetRootBoxes(selectedOnly, includeTextBoxes) {
    var selectedRoots = [];
    if (selectedOnly) {
      var selCache = this.GetSelectionCache();
      for (var i = 0; i < selCache.length; i++) {
        var theElement = selCache[i];
        if (this.IsElementChartBox(theElement, includeTextBoxes))
          this.AddSelectedRoot(selectedRoots, theElement);
      }
    } else {
      var nodeList = [];
      var containerList = [];
      var plm = this.GetPageLayoutMgr();
      plm.GetRootNodes(nodeList, false);
      for (i = 0; i < nodeList.length; i++) {
        var node = nodeList[i];
        var box = node.GetChartBox();
        if (this.IsElementChartBox(box, includeTextBoxes)) {
          var containerNode = node.GetContainerNode();
          if (!containerNode) {
            selectedRoots.push(node);
          } else if (containerList.indexOf(containerNode) === -1) {
            selectedRoots.push(node);
            containerList.push(containerNode);
          }
        }
      }
    }

    return selectedRoots;
  }

  PositionTextBox(textBox, hCentering, vCentering) {
    var l = textBox.GetLayout();
    var theNode = this.GetPLMNode(textBox);
    var plm = this.GetPageLayoutMgr();

    //plm.ResetStretch(theNode);
    if (hCentering == null) hCentering = l.GetChartHorzCentering();
    else l.SetChartHorzCentering(hCentering);

    if (vCentering == null) vCentering = l.GetChartVertCentering();
    else l.SetChartVertCentering(vCentering);
    var delta = plm.DeltaToCenter(theNode, hCentering, vCentering, true);
    if (delta.fX != 0 || delta.fY != 0) {
      plm.MoveChart(theNode, delta.fX, delta.fY);
      textBox.AdjustCellPosition();
      // plm.AutoStretchNode(theNode);
    }
  }

  CalculateScale(scaleW, scaleH, scalingMethod, aBox) {
    var plm = this.GetPageLayoutMgr();
    var aLayout = aBox.GetLayout();
    var p = plm.CalculateScale(scaleW, scaleH, scalingMethod, aLayout);
    this.PositionTextBox(aBox, null, null);
    return p;
  }

  PositionTextBoxFromCmd(textBox, positionCmd) {
    //var l = textBox.GetLayout();
    var hC, vC;

    switch (positionCmd) {
      case "pinLeftTop":
        hC = enumHorzCentering.pinLeft;
        vC = enumVertCentering.pinTop;
        break;
      case "pinCenterTop":
        hC = enumHorzCentering.pinCenter;
        vC = enumVertCentering.pinTop;
        break;
      case "pinRightTop":
        hC = enumHorzCentering.pinRight;
        vC = enumVertCentering.pinTop;
        break;
      case "pinLeftBottom":
        hC = enumHorzCentering.pinLeft;
        vC = enumVertCentering.pinBottom;
        break;
      case "pinCenterBottom":
        hC = enumHorzCentering.pinCenter;
        vC = enumVertCentering.pinBottom;
        break;
      case "pinRightBottom":
        hC = enumHorzCentering.pinRight;
        vC = enumVertCentering.pinBottom;
        break;
      case "pinOff":
        hC = enumHorzCentering.userplacement;
        vC = enumVertCentering.userplacement;
        break;
    }

    this.PositionTextBox(textBox, hC, vC);
  }

  PositionSelectedTextBox(positionCmd) {
    var textBox = this.GetSelectedTextBox(true);
    if (textBox == null) return;

    this.PositionTextBoxFromCmd(textBox, positionCmd);
  }

  GetSelectedTextBox(selectBox) {
    var selCache = this.GetSelectionCache();
    var numElements = selCache.length;
    if (numElements != 1) return null;

    var theElement = selCache[0];
    if (this.IsElementTextBox(theElement)) return theElement;

    var isBoxCell = this.IsElementBoxCell(theElement);
    if (isBoxCell) {
      var parentBox = theElement.GetParentBox();
      if (selectBox) this.DoSelectElement(parentBox, true);
      return parentBox;
    }

    return null;
  }

  GetSelectedNonRootBoxes(firstOnly) {
    var selectedNonRoots = new Array();
    var numElements = this.GetNumElements();
    for (var i = 0; i < numElements; i++) {
      var theElement = this.fElements[i];
      if (this.IsElementBox(theElement) && theElement.IsSelected()) {
        if (!theElement.IsRoot()) {
          selectedNonRoots.push(theElement);
          if (firstOnly) return selectedNonRoots;
        }
      }
    }

    return selectedNonRoots;
  }

  EnableBranchSelector() {
    var numElements = this.GetNumElements();
    var enable = false;

    for (var i = 0; i < numElements; i++) {
      var theElement = this.fElements[i];

      if (this.IsElementBox(theElement) && theElement.IsSelected()) {
        if (!this.IsTopOfSubChart(theElement)) enable = true;
      }
    }
    return enable;
  }

  OnlyOneBoxSelected() {
    var s = this.GetSelectionCache();
    var numElements = s.length;
    var count = 0;
    for (var i = 0; i < numElements; i++) {
      var theElement = s[i];
      if (this.IsElementBox(theElement)) {
        var theSubType = theElement.GetBoxSubType();
        if (theSubType === enumBoxSubTypes.chartBox || theSubType === enumBoxSubTypes.refMRBox)
          count++;
        if (count > 1) return false;
      }
    }
    return count == 1;
  }

  RecalcChartLayoutForSelection() {
    var selectedRoots = [];
    var selectedBoxes = this.GetSelectedBoxes(true);
    var n = selectedBoxes.length;
    for (var i = 0; i < n; i++) this.AddSelectedRoot(selectedRoots, selectedBoxes[i]);

    this.RecalcChartLayoutForRoots(selectedRoots);
  }

  RecalcChartLayout(aBox) {
    var selectedRoots = [];
    this.AddSelectedRoot(selectedRoots, aBox);
    this.RecalcChartLayoutForRoots(selectedRoots);
  }

  UpdateLayoutBackgroundMgr(updatedLayout) {
    var wsMgr = this.fWSMgr;
    var bgMgr = wsMgr.fBackgroundMgr;
    bgMgr.UpdateLayout(updatedLayout);
  }

  UpdateSelectedProperty(parameter, theProperty) {
    var needPageRecalc = false;
    var selectedRoots = null;

    var s = this.GetSelectionCache();
    var numElements = s.length;
    var targetBox = null;
    let shouldOverride = true;
    for (var i = 0; i < numElements; i++) {
      var theElement = s[i];
      var forceChartRecalc = false;

      var elementType = theElement.GetElementType();
      if (elementType == enumChartElement.boxCell) {
        let parentBox = theElement.GetParentBox();
        shouldOverride = parentBox.ShouldDoOverrides();

        switch (theProperty) {
          case enumUpdateProperty.boxFillColor:
          case enumUpdateProperty.boxBorderColor:
          case enumUpdateProperty.boxShapeType:
          case enumUpdateProperty.connectingLineDash:
          case enumUpdateProperty.subGroupLineDash:
          case enumUpdateProperty.boxShadow:
          case enumUpdateProperty.boxBorderWeight:
          case enumUpdateProperty.boxBorderStyle:
          case enumUpdateProperty.boxDashStyle:
          case enumUpdateProperty.boxFlag:
            if (theElement.InEditMode()) theElement.ExitEditMode();
            theElement = theElement.GetParentBox();
            elementType = theElement.GetElementType();
            break;
          case enumUpdateProperty.cellRefType: {
            let parentLayout = parentBox.GetLayout();
            if (parentLayout?.IsLayoutCellRefType()) {
              theElement = parentBox;
              elementType = parentBox.GetElementType();
            }
            break;
          }
        }
      }

      switch (elementType) {
        case enumChartElement.boxCell:
          forceChartRecalc = theElement.UpdateCellProperty(parameter, theProperty, shouldOverride);
          if (forceChartRecalc) {
            targetBox = theElement.GetParentBox();
            if (theElement.InEditMode()) {
              if (theElement.ExitEditMode()) {
                // If the content of the text edit changed - the layout is recalculated - no need to do it again
                targetBox["ForceChartLayout"] = false;
                targetBox = null;
              }
            }
          }
          break;
        case enumChartElement.gutterCell:
          break;
        case enumChartElement.connectingLine:
          forceChartRecalc = this.UpdateConnectingLine(theElement, parameter, theProperty);
          break;
        case enumChartElement.chartBox:
        case enumChartElement.textBox:
          shouldOverride = theElement.ShouldDoOverrides();
          forceChartRecalc = this.UpdateChartBox(
            theElement,
            parameter,
            theProperty,
            shouldOverride
          );

          if (forceChartRecalc) theElement.ForceLayoutRecalc(true); // ,null);
          break;
      }
      if (forceChartRecalc && theElement.GetLayer() !== enumElementLayer.master) {
        // There might be more than one root in the selection
        needPageRecalc = true;
        if (selectedRoots == null) selectedRoots = [];
        this.AddSelectedRoot(selectedRoots, theElement);
      }

      var target = theElement;
      if (targetBox) target = targetBox;
      if (target && target.GetLayer() === enumElementLayer.master) {
        this.UpdateLayoutBackgroundMgr(target.GetLayout());
      }
    }

    if (targetBox) {
      targetBox.ForceLayoutRecalc(true);
    }

    if (needPageRecalc) this.RecalcChartLayoutForRoots(selectedRoots);
  }

  SurveyConnectingLineProperty(theElement, theProperty) {
    var parameter = null;
    this.fMatchProp = true;

    switch (theProperty) {
      case enumUpdateProperty.connectingLineColor:
        var cpResult = new QColorPickerResult();
        cpResult.fSelectedColor = theElement.GetConnectingLineColor();
        cpResult.fFillMode = theElement.GetConnectingLineFillMode();
        parameter = cpResult;
        break;
      case enumUpdateProperty.connectingLineThickness:
        parameter = theElement.GetConnectingLineThickness();
        break;
      case enumUpdateProperty.connectingLineDash:
        parameter = theElement.GetConnectorDashStyle();
        break;
      case enumUpdateProperty.subGroupLineDash:
        parameter = theElement.GetParentConnectorDashStyle();
        break;
      default:
        this.fMatchProp = false;
        break;
    }

    return parameter;
  }

  SurveyBoxProperty(theElement, theProperty, selectedCells) {
    this.fMatchProp = true;
    var parameter = null;
    switch (theProperty) {
      case enumUpdateProperty.boxFillColor:
        var cpResult = new QColorPickerResult();
        cpResult.fSelectedColor = theElement.GetBoxFillColor();
        cpResult.fSelectedColor2 = theElement.GetBoxFillColorGradient();
        cpResult.fFillMode = theElement.GetBoxFillMode();
        parameter = cpResult;
        break;
      case enumUpdateProperty.boxBorderColor:
        var cpResult = new QColorPickerResult();
        cpResult.fSelectedColor = theElement.GetBoxBorderColor();
        parameter = cpResult;
        break;
      case enumUpdateProperty.boxShapeType:
        parameter = theElement.GetBoxShapeType();
        break;
      //  case enumUpdateProperty.connectingLineDash:
      //      parameter = theElement.GetConnectorDashStyle();
      //      break;
      //   case enumUpdateProperty.subGroupLineDash:
      //       parameter = theElement.GetParentConnectorDashStyle();
      //       break;
      case enumUpdateProperty.boxShadow:
        parameter = theElement.GetShadowOn();
        break;
      case enumUpdateProperty.branchStyle:
        if (theElement.IsRoot())
          this.fMatchProp = false; // Don't include root in the survey as it always has a fixed branch style (tree)
        else parameter = theElement.GetBranchStyle();
        break;
      case enumUpdateProperty.boxFlag:
        var layout = theElement.GetLayout();
        parameter = {
          fFlagType: layout.GetFlagType(),
          fFlagColor1: layout.GetFlagColor1(),
          fFlagColor2: layout.GetFlagColor2(),
          fFlagColor3: layout.GetFlagColor3(),
          fFlagLabel: layout.GetFlagLabel(),
          fTriPosition: layout.GetTriPosition()
        };
        break;
      case enumUpdateProperty.bottomMargin:
        parameter = theElement.GetBoxMargins().fBottom;
        break;
      case enumUpdateProperty.topMargin:
        parameter = theElement.GetBoxMargins().fTop;
        break;
      case enumUpdateProperty.rightMargin:
        parameter = theElement.GetBoxMargins().fRight;
        break;
      case enumUpdateProperty.leftMargin:
        parameter = theElement.GetBoxMargins().fLeft;
        break;

      case enumUpdateProperty.bottomSpacing:
        parameter = theElement.GetBoxSpacing().fBottom;
        break;
      case enumUpdateProperty.topSpacing:
        parameter = theElement.GetBoxSpacing().fTop;
        break;
      case enumUpdateProperty.rightSpacing:
        parameter = theElement.GetBoxSpacing().fRight;
        break;
      case enumUpdateProperty.leftSpacing:
        parameter = theElement.GetBoxSpacing().fLeft;
        break;
      case enumUpdateProperty.mgrVSpacing1:
        parameter = theElement.GetMgrVSpacing1();
        break;
      case enumUpdateProperty.mgrVSpacing2:
        parameter = theElement.GetMgrVSpacing2();
        break;
      case enumUpdateProperty.spacingMode:
        parameter = theElement.GetMarginMode();
        break;
      case enumUpdateProperty.boxBorderWeight:
        parameter = theElement.GetBorderThickness();
        break;
      case enumUpdateProperty.boxBorderStyle:
        parameter = theElement.GetBorderStyle();
        break;
      case enumUpdateProperty.boxDashStyle:
        parameter = theElement.GetDashStyle();
        break;
      case enumUpdateProperty.boxType:
        parameter = theElement.GetBoxType();
        break;
      case enumUpdateProperty.contentLock:
        parameter = theElement.GetContentLock();
        break;
      case enumUpdateProperty.layoutLock:
        parameter = theElement.GetLayoutLock();
        break;
      case enumUpdateProperty.hierLock:
        parameter = theElement.GetPositionLock();
        break;
      case enumUpdateProperty.persistLock:
        parameter = theElement.GetPersistLock();
        break;
      case enumUpdateProperty.cellTextBold:
      case enumUpdateProperty.cellTextItalic:
      case enumUpdateProperty.cellTextUnderline:
      case enumUpdateProperty.fieldDisplayMode:
      case enumUpdateProperty.fontName:
      case enumUpdateProperty.fontSize:
      case enumUpdateProperty.cellFontColor:
      case enumUpdateProperty.cellFillColor:
      case enumUpdateProperty.cellTextAlign:
      case enumUpdateProperty.cellWrapText:
      case enumUpdateProperty.cellTextVAlign:
      case enumUpdateProperty.cellExpandRow:
      case enumUpdateProperty.picFrame:
      case enumUpdateProperty.picMask:
        parameter = theElement.SurveyCells(theProperty, selectedCells);
        break;
      case enumUpdateProperty.cellRefType: {
        var layout = theElement.GetLayout();
        if (layout?.IsLayoutCellRefType()) {
          parameter = layout.GetCellRefType();
        } else {
          parameter = theElement.SurveyCells(theProperty, selectedCells);
        }
        break;
      }
      default:
        this.fMatchProp = false;
        break;
    }

    return parameter;
  }

  AltCompareRequired(theProperty) {
    var altCompare = false;

    switch (theProperty) {
      case enumUpdateProperty.boxFillColor:
      case enumUpdateProperty.boxBorderColor:
      case enumUpdateProperty.cellFillColor:
      case enumUpdateProperty.cellFontColor:
      case enumUpdateProperty.connectingLineColor:
      case enumUpdateProperty.boxFlag:
        altCompare = true;
        break;
    }

    return altCompare;
  }

  SurveySelectedProperty(theProperty) {
    var s = this.GetSelectionCache();
    var numElements = s.length;
    var parameter = null;
    var prevParameter = null;
    var altCompare = this.AltCompareRequired(theProperty);

    for (var i = 0; i < numElements; i++) {
      var theElement = s[i];
      var parameter1 = null;
      var elementType = theElement.GetElementType();
      switch (elementType) {
        case enumChartElement.boxCell:
          var parentBox = theElement.GetParentBox();
          parameter1 = this.SurveyBoxProperty(parentBox, theProperty, true);
          break;
        case enumChartElement.gutterCell:
          break;
        case enumChartElement.connectingLine:
          parameter1 = this.SurveyConnectingLineProperty(theElement, theProperty);
          break;
        case enumChartElement.chartBox:
        case enumChartElement.textBox:
          parameter1 = this.SurveyBoxProperty(theElement, theProperty, false);
          break;
      }

      if (this.fMatchProp) {
        parameter = parameter1;
        if (prevParameter != null) {
          if (altCompare) {
            if (!CPCompare(prevParameter, parameter)) return null;
          } else if (prevParameter != parameter) return null;
        }
        prevParameter = parameter;
      }
    }

    return parameter;
  }

  PrepRender(theWSMgr) {}

  CreateNewDBObject(createPosition = false) {
    // afterIndex
    var theDoc = this.fDoc;
    var objDB = theDoc.GetObjDB();
    var objClass = objDB.GetClassByName(
      createPosition ? enumObjClassNames.position : enumObjClassNames.box
    );
    var dataObj = objClass.AddNewObject();

    return dataObj;
  }

  GetNextObjID() {
    var theDoc = this.fDoc;
    var objDB = theDoc.GetObjDB();
    var boxClass = objDB.GetClassByName("Box");

    return boxClass.GenUID();
  }

  SetDefaults(dataObj, theLayout) {
    var f1 = dataObj ? GetNameDefForObj(dataObj) : null; // dataObj.GetFieldDefByID("Name"); // "Name"
    var f2 = dataObj ? dataObj.GetTitleFieldDef() : null; // "Title"
    var c1 = theLayout.GetCellDesc(1, 1);
    c1.SetAlignment("center");
    c1.SetWrapText(enumWrapTextMode.unlimited);

    var c2 = theLayout.GetCellDesc(1, 2);
    c2.SetAlignment("center");
    c2.SetWrapText(enumWrapTextMode.unlimited);

    if (f1) c1.SetField(f1);
    if (f2) c2.SetField(f2);
  }

  IsValidAdd(theLayout, x, y, centerOnXY) {
    if (theLayout.GetContainerType() != enumContainerType.nonmrb) return true;
    var xSize = theLayout.GetBoxWidth();
    var ySize = theLayout.GetBoxHeight(false);
    var xs, ys, xe, ye;

    if (centerOnXY) {
      xs = x - Math.round(xSize / 2);
      ys = y - Math.round(ySize / 2);
    } else {
      xs = x;
      ys = y;
    }

    var xe = xs + xSize;
    var ye = ys + ySize;

    if (xs < 0) return false;

    if (ys < 0) return false;

    if (xe > this.fCanvasSizeX) return false;

    if (ye > this.fCanvasSizeY) return false;

    return true;
  }

  UpdateLayer(theLayout) {
    // When a box is added we need to make sure it is added to the right layer (content or masterpage)
    var renderMode = this.GetRenderMode();
    var eLayer;
    if (
      renderMode == enumPanelRenderMode.masterOnly ||
      renderMode == enumPanelRenderMode.backgroundAndGhostChart
    )
      eLayer = enumElementLayer.master;
    else eLayer = enumElementLayer.content;

    theLayout.SetLayer(eLayer);
  }

  SetElementLayer(anElement) {
    var renderMode = this.GetRenderMode();
    var eLayer;
    if (
      renderMode == enumPanelRenderMode.masterOnly ||
      renderMode == enumPanelRenderMode.backgroundAndGhostChart
    )
      eLayer = enumElementLayer.master;
    else eLayer = enumElementLayer.content;

    anElement.SetLayer(eLayer);
  }

  //REDPIN
  AddChartBox(
    x,
    y,
    theLayout,
    dataObj,
    parentDataObj,
    centerOnXY,
    recalcLayout,
    forceAutoCenter,
    calcLayer,
    dbObjID,
    lightInit
  ) {
    //	console.log("QCanvasPanel:AddChartBox",dbObjID);
    var newLayout = false;
    if (theLayout == null) {
      if (dataObj == null) {
        theLayout = this.GetEmptyLayout();
      } else {
        if (dataObj.IsPositionObj()) {
          theLayout = this.GetMRBLayout();
        } else if (dataObj.IsHolderObj()) {
          theLayout = this.GetDefaultBoxLayout();
        }
      }

      // For NewDoc we want it to auto center
      // For AddBox we want user placement
      var hc = enumHorzCentering.centermiddle;
      var vc = enumVertCentering.centertop;
      if (!forceAutoCenter) {
        hc = enumHorzCentering.userplacement;
        vc = enumVertCentering.userplacement;
      }
      theLayout.SetChartHorzCentering(hc);
      theLayout.SetChartVertCentering(vc);

      newLayout = true;
    }
    theLayout.SetDocRef(this.fDoc);
    var xSize = theLayout.GetBoxWidth();
    var ySize = theLayout.GetBoxHeight(false);
    var boxType = theLayout.GetBoxType();

    var dataObjID;
    if (boxType != enumBoxType.standard) {
      dataObjID = dbObjID;
      if (dataObjID == null) dataObjID = this.GetNextObjID();
    } else if (dataObj == null) {
      dataObj = this.CreateNewDBObject();
      if (newLayout) this.SetDefaults(dataObj, theLayout);
      dataObjID = dataObj.GetObjectID();
    } else {
      dataObjID = dataObj.GetObjectID();
    }

    if (dataObj && dataObj.IsPositionObj()) theLayout.SetDBObjClassName(enumObjClassNames.position);

    var aNewBox = new QChartBox();
    var xPos = x;
    var yPos = y;
    if (centerOnXY) {
      xPos = x - Math.round(xSize / 2);
      yPos = y - Math.round(ySize / 2);
    }

    if (calcLayer) this.UpdateLayer(theLayout);
    aNewBox.InitChartBox(
      theLayout,
      this,
      xPos,
      yPos,
      enumChartBoxInteractionMode.readOnly,
      dataObj,
      dataObjID,
      false,
      this.fWSMgr,
      lightInit
    );
    var plm = this.GetPageLayoutMgr();
    var newNode = plm.RegisterNewNode(theLayout, dataObj, parentDataObj, dataObjID, aNewBox);

    if (recalcLayout) this.RecalculateChartLayout(newNode);

    plm.ResetEdgeDistance(newNode);
    return aNewBox;
  }

  AddConnectingLines() {
    var chartBoxes = [];
    var numElements = this.GetNumElements();
    for (var i = 0; i < numElements; i++) {
      var theElement = this.fElements[i];
      if (this.IsElementChartBox(theElement, true)) {
        var elementType = theElement.GetElementType();
        var subType = theElement.GetBoxSubType();
        if (elementType == enumChartElement.chartBox || subType == enumBoxSubTypes.refMRBox)
          chartBoxes.push(theElement);
      }
    }

    var aBox = chartBoxes.pop();
    while (aBox != null) {
      var plm = this.GetPageLayoutMgr();
      aBox.AddConnectingLines(plm);
      aBox = chartBoxes.pop();
    }
  }

  // Called when the parent box moves
  RepositionCells(aNode) {
    var numElements = this.GetNumElements();
    for (var i = 0; i < numElements; i++) {
      var theElement = this.fElements[i];
      if (this.PIsElementBox(theElement, true)) theElement.AdjustCellPosition();
    }
  }

  //REDPIN
  ChangePageSize(
    minPageSizeX,
    minPageSizeY,
    pageMargins,
    pageOrientation,
    headerHeight,
    footerHeight,
    units
  ) {
    var pageMgr = this.GetPageMgr();

    var newMargins = pageMargins;
    if (units == enumPageSizeUnits.inches) {
      minPageSizeX = Math.floor(minPageSizeX * kDPIScreen); // this.fDPI
      minPageSizeY = Math.floor(minPageSizeY * kDPIScreen); // this.fDPI
      newMargins = pageMargins.MultiplyRect(kDPIScreen); // this.fDPI
      newMargins.FloorRect();
      headerHeight = Math.round(headerHeight * kDPIScreen);
      footerHeight = Math.round(footerHeight * kDPIScreen);
    }

    var curMargins = pageMgr.GetPageMargins();
    pageMgr.SetMinPageSize(minPageSizeX, minPageSizeY);
    pageMgr.SetPageMargins(newMargins);
    pageMgr.SetPageOrientation(pageOrientation);
    pageMgr.SetHeaderHeight(headerHeight);
    pageMgr.SetFooterHeight(footerHeight);

    this.RecalculateChartLayout(null);

    //	if (!curMargins.Compare(newMargins))

    this.PositionMargins();
    this.PositionAltBackground();
  }

  SetShowPageMargins(showMargins) {
    var pageMgr = this.GetPageMgr();
    pageMgr.SetShowPageMargins(showMargins);
  }

  SetShowHeaderFooter(showHeaderFooter) {
    var pageMgr = this.GetPageMgr();
    pageMgr.SetShowHeaderFooter(showHeaderFooter);
  }

  SetBackgroundColor(bkGroundColor) {
    var pageMgr = this.GetPageMgr();
    pageMgr.SetBackgroundColor(bkGroundColor);
  }

  SetBackgroundColor2(bkGroundColor) {
    var pageMgr = this.GetPageMgr();
    pageMgr.SetBackgroundColor2(bkGroundColor);
  }

  SetBackgroundFillMode(fillMode) {
    var pageMgr = this.GetPageMgr();
    pageMgr.SetBackgroundFillMode(fillMode);
  }

  DoRecalculateChartLayout(aNode) {
    var plm = this.GetPageLayoutMgr();
    var canvasSize = plm.RecalculatePageLayout(aNode);
    if (canvasSize != null) {
      this.fCanvasSizeX = canvasSize.fX;
      this.fCanvasSizeY = canvasSize.fY;
      plm.SetPageSize(canvasSize.fX, canvasSize.fY);
      plm.AutoCenterChart(aNode);
      this.ResizeCanvasCenter(this.GetScaleFactor());
    } else {
      plm.AutoCenterChart(aNode);
    }
  }

  ResetBoxesToLayout() {
    var numElements = this.GetNumElements();
    for (var i = 0; i < numElements; i++) {
      var theElement = this.fElements[i];
      if (this.IsElementChartBox(theElement, true)) theElement.ResetBoxLayout(true);
    }
  }

  UpdateBoxGroups() {
    var plm = this.GetPageLayoutMgr();
    var branchMgr = plm.fBranchMgr;
    var layoutExtras = branchMgr.fLayoutExtras;
    if (!layoutExtras) return;
    var boxGroupListObj = layoutExtras.GetBoxGroupListObj();
    // clear all elements that are in layer groups
    var numElements = this.GetNumElements();
    for (var i = numElements - 1; i >= 0; i--) {
      var anElement = this.fElements[i];
      if (anElement instanceof QBoxGroup) {
        this.RemoveElement(anElement);
        //            this.fElements.splice(i, 1);
      }
    }

    // panel is whole screen presentation (only white portion)
    boxGroupListObj.UpdateBoxGroups(this);
  }

  UpdateAuxLines() {
    var plm = this.GetPageLayoutMgr();
    var auxLines = plm.GetAuxLineList();
    if (!auxLines) {
      return;
    }

    // clear all elements that are in layer groups
    var numElements = this.GetNumElements();
    for (var i = numElements - 1; i >= 0; i--) {
      var anElement = this.fElements[i];
      if (anElement instanceof QAuxLineElement) {
        this.RemoveElement(anElement);
      }
    }

    var nAuxLines = auxLines.length;
    for (i = 0; i < nAuxLines; i++) {
      var auxLine = auxLines[i];
      auxLine.UpdateElement(this);
    }
  }

  RecalculateChartLayout(aNode) {
    var plm = this.GetPageLayoutMgr();

    var allRoots = [];
    if (aNode == null) {
      allRoots = this.GetRootBoxes(false, true); // true);
      var n = allRoots.length;
      if (n === 0) this.DoRecalculateChartLayout(null);
      else {
        for (var i = 0; i < n; i++) {
          this.DoRecalculateChartLayout(allRoots[i]);
        }
      }
    } else {
      aNode.ClearCachedFieldValueByID("SubChartLevel");
      this.DoRecalculateChartLayout(aNode);
    }

    if (aNode && aNode.fChartBox) {
      this.AutoFormat(aNode);
    }

    //Note - the recalculate chart layout should be called for every single root box. Auto-center all is deprecated.
    //TODO: Remove these lines! THey shouldn't exist- unnecesary calls. Removing them will currently still cause some issues when templates are applied in WB.
    plm.AutoCenterAll();
    plm.AutoStretch(allRoots);

    this.AddConnectingLines();
    this.RepositionCells();

    this.UpdateBoxGroups();

    this.UpdateAuxLines();
  }

  // Mar29
  AddSubordinate(
    parentDataObj,
    newDataObj,
    theLayout,
    branchStyle,
    marginMode,
    recalcLayout,
    loading
  ) {
    var newLayout = false;
    if (theLayout == null) {
      theLayout = newDataObj.IsPositionObj() ? this.GetMRBLayout() : this.GetEmptyLayout();
      newLayout = true;
    }
    //	console.log("AddSubordinate:", theLayout.fUILayoutID);

    var plm = this.GetPageLayoutMgr();
    if (newDataObj == null) {
      const createPosition = this.fDoc.IsPositionDocument();
      newDataObj = this.CreateNewDBObject(createPosition);
      if (newLayout) this.SetDefaults(newDataObj, theLayout);
    }

    if (branchStyle != null) theLayout.SetBranchStyle(branchStyle);

    if (marginMode != null) theLayout.SetMarginMode(marginMode);

    this.UpdateLayer(theLayout);
    var dataObjID = newDataObj.GetObjectID();
    var aNewBox = new QChartBox();

    theLayout.SetDocRef(this.fDoc);
    aNewBox.InitChartBox(
      theLayout,
      this,
      0,
      0,
      enumChartBoxInteractionMode.readOnly,
      newDataObj,
      dataObjID,
      false,
      this.fWSMgr,
      loading
    );

    if (newDataObj.IsPositionObj()) this.CreateHolderBoxes(newDataObj);

    var newNode = plm.RegisterNewNode(theLayout, newDataObj, parentDataObj, dataObjID, aNewBox);
    if (recalcLayout) this.RecalculateChartLayout(newNode);

    return aNewBox;
  }

  GetSubchartBoxList(cSubchart, pageNode) {
    var boxList = [];
    if (cSubchart["forceTemplate"]) {
      var plm = this.GetPageLayoutMgr();
      var nodeList = plm.fActiveNodeList.ReturnFlatList();
      boxList = nodeList.map(function (a) {
        return a.GetChartBox();
      });
      delete cSubchart["forceTemplate"];
    } else {
      boxList = [pageNode.GetChartBox()];
      var newPageNode = pageNode.GetParent();
      while (newPageNode) {
        boxList.push(newPageNode.GetChartBox());
        newPageNode = newPageNode.GetParent();
      }
    }

    return boxList;
  }

  AutoFormat(pageNode, includeAllBoxesOnPage) {
    var wsMgr = this.fWSMgr;
    var activeTemplate = this.fDoc.GetActiveTemplate();
    if (activeTemplate) {
      var scm = this.GetSubChartMgr();
      var cSubchart = scm.GetCurrentSubChart();
      var cSubChartID = cSubchart.GetSubChartID();
      if (cSubChartID == 0) return true;
      var boxList = [];
      if (includeAllBoxesOnPage || pageNode == null) {
        boxList = wsMgr.GetTargetTrees(false, this.fDoc);
      }

      // Slower, but more thorough
      else boxList = this.GetSubchartBoxList(cSubchart, pageNode);

      const proxyTemplate = this.fDoc.GetProxyTemplate();
      ApplyChartTemplateToDoc(this.fDoc, proxyTemplate, boxList, false, wsMgr);

      return true;
    }

    return false;
  }

  ToggleViewMode(theMode, ignoreSwitchSubChart) {
    var scID;

    this.fWSMgr.SetToolMode("ToolMode1", true);

    var curRenderMode = this.GetRenderMode();
    if (curRenderMode == theMode) return;

    this.SetRenderMode(theMode);

    if (!ignoreSwitchSubChart) {
      var scm = this.GetSubChartMgr();

      if (
        theMode == enumPanelRenderMode.masterOnly ||
        theMode == enumPanelRenderMode.backgroundAndGhostChart
      ) {
        scm.StoreCurrentSubChart();
        scID = 0;
      } else scID = scm.RecallCurrentSubChart();

      this.SwitchToSubChart(scID);
    }
  }

  LoadMasterPageSubChart() {
    var plm = this.GetPageLayoutMgr();
    var scm = this.GetSubChartMgr();
    scm.StoreCurrentSubChart();

    scm.SetCurrentSubChart(kMasterSCID);
    this.LoadSubChart(kMasterSCID);
    plm.LoadRootOrder();
    //this.ChangePageSize(pm.GetMinPageWidth(),pm.GetMinPageHeight(), pm.GetPageMargins(), pm.GetPageOrientation(), pm.GetHeaderHeight(), pm.GetFooterHeight(), enumPageSizeUnits.pixels);
  }

  GetPositionExtraLayoutType(posNode, associatedIDs, allHoldersFiltered = false) {
    const wsmgr = this.fWSMgr;
    const doc = wsmgr.GetDoc();
    const isPlanning = doc.IsPlanChart();
    const positionStatus = posNode.GetDataObj().SmartGetFieldValue("PositionStatus");
    const isOpen = positionStatus === PositionStatus.Active;

    //base chart position with 0 or all filtered holders
    if (
      (!isPlanning || (doc.fIsPreview && isOpen)) &&
      (associatedIDs.length === 0 || allHoldersFiltered)
    ) {
      return enumEmptyPositionLayoutType.vacant;
    } else {
      //Plan position - check position status
      if (isOpen) {
        return enumEmptyPositionLayoutType.open;
      }

      if (positionStatus === PositionStatus.Closed) {
        return enumEmptyPositionLayoutType.closed;
      }
    }
  }

  HandlePositionExtras(posNode, scID, associatedIDs, allHoldersFiltered, scNodeMap) {
    const wsmgr = this.fWSMgr;
    const doc = wsmgr.GetDoc();
    const scm = this.GetSubChartMgr();
    const posLayout = posNode.GetLayout();

    // Check if the position node already has a holder area box
    for (var i = 0; i < associatedIDs.length; i++) {
      const priorHolderNode = scNodeMap.get(associatedIDs[i]) || null;
      if (
        priorHolderNode &&
        priorHolderNode.GetLayout().GetBoxSubType() === enumBoxSubTypes.holderAreaBox
      ) {
        return;
      }
    }

    // Only add extra for base chart position if no holders are present
    if ((!doc.IsPlanChart() || doc.fIsPreview) && associatedIDs.length > 0 && !allHoldersFiltered) {
      return;
    }

    const layoutType = this.GetPositionExtraLayoutType(posNode, associatedIDs, allHoldersFiltered);
    const extraLayout = wsmgr.GetNewLayoutByBoxSubType(enumBoxSubTypes.holderAreaBox, {
      type: layoutType
    });
    extraLayout.SetPosition(0, 0);
    const objID = this.GetNextObjID();
    extraLayout.SetSubChartID(scID);
    const extraNode = scm.AddSCNode(extraLayout, null, objID);

    doc.AddNodesToMRB(posLayout, [extraNode]);

    const newBox = this.AddChartBox(
      extraLayout.GetPositionX(),
      extraLayout.GetPositionY(),
      extraLayout,
      null,
      null,
      false,
      false,
      false,
      false,
      objID,
      false
    );
  }

  LoadSubChart(subChartID) {
    var wsMgr = this.fWSMgr;
    const doc = wsMgr.GetDoc();
    var bgMgr = wsMgr.fBackgroundMgr;
    var scm = this.GetSubChartMgr();
    scm.LogSubCharts("LoadSubChart: " + subChartID);
    var fullList = null;
    const isCustomChart = this.fDoc?.IsCustomChart();
    const isPreviewChart = this.fDoc?.fIsPreview;

    if (subChartID == kMasterSCID) fullList = bgMgr.GetBackgroundNodes();
    else if (typeof subChartID == "string" || subChartID == kIndexSCID) {
      var curIndexPage = scm.GetIndexPageSubChart();
      fullList = curIndexPage.fChildren[0].fSubChartNodes;
      for (var aChild = 0; aChild < curIndexPage.fChildren.length; aChild++) {
        var theChild = curIndexPage.fChildren[aChild];
        if (theChild.GetSubChartID() == subChartID) {
          fullList = theChild.fSubChartNodes;
          break;
        }
      }
    } else fullList = scm.GetNodesForSubChart(subChartID, false); //GetSCNodes(subChartID);

    if (fullList == null) {
      return;
    }

    var n = fullList.length;
    //	console.log("9.30.2015 LoadSubChart Nodes:" + n);
    var MRBs = [];
    // Hoist invariant lookups out of the per-node loop
    var curSC = scm.FindSubChart(subChartID);
    var scNodeMap = scm.GetSCNodeMapByDataObjID(subChartID);
    // Pre-compute data obj set for CheckParentFiltered (avoids O(n) .map() + .indexOf() per call)
    var fullDBObjSet = new Set();
    for (var fi = 0; fi < n; fi++) {
      var fObj = fullList[fi].GetDataObj();
      if (fObj) fullDBObjSet.add(fObj);
    }
    for (var i = 0; i < n; i++) {
      var scNode = fullList[i];
      var aLayout = scNode.GetLayout();
      var layout_subChartID = aLayout.GetSubChartID();
      var isHidden = aLayout.GetHidden();
      if (subChartID == layout_subChartID && !isHidden) {
        var objID = scNode.GetDataObjID();
        var dataObj = scNode.GetDataObj();
        var parentObj = null;
        if (dataObj != null) {
          // Skip filtered check here in the following cases:
          // 1. The record is the topbox of the current subchart
          // 2. The record is a COC record
          // 3. The record is a manager && include filtered managers is enabled
          const objIsHolderInPosChart = dataObj.IsHolderObj() && dataObj.fParentPosition != null;
          if (
            curSC != null &&
            (objIsHolderInPosChart || !curSC.IsTopObjRelated(dataObj)) &&
            !dataObj.CheckIsInChainOfCommand(curSC)
          ) {
            //If it's filtered, but not the top box, get rid of it.
            var isFiltered = dataObj.IsFiltered();
            var isParentFiltered = dataObj.CheckParentFilteredFast(
              scm,
              fullDBObjSet,
              subChartID,
              curSC
            );
            let isPositionFiltered = false;
            if (dataObj.IsHolderObj()) {
              const parentPosition = dataObj?.GetParentPosition();
              isPositionFiltered = parentPosition?.IsFiltered();
            }
            if (isFiltered || isParentFiltered || isPositionFiltered) continue;
          }
          parentObj = dataObj.GetParent();
        }

        if (aLayout.GetContainerType() !== enumContainerType.nonmrb) {
          MRBs.push(scNode);
          continue;
        }
        if (aLayout.GetBoxSubType() === enumBoxSubTypes.holderAreaBox) {
          const parentObjID = aLayout.GetContainerObjID();
          const parentPos = scNodeMap.get(parentObjID) || null;
          const parentObj = parentPos?.GetDataObj();
          if (!parentPos || (parentObj?.IsFiltered() && !curSC.IsTopObjRelated(parentObj))) {
            continue;
          }

          if (aLayout.fBoxContentSubType === enumEmptyPositionLayoutType.vacant) {
            const hasNonFilteredHolders = parentObj.fHolderList?.some(
              k => !k.fFiltered || k.IsMuted()
            );

            // Delete Vacant Box if there's at least one non-filtered holder or the pos. is not in the subchart.
            if (hasNonFilteredHolders) {
              scm.RemoveSCNode(scNode, subChartID);
              continue;
            }
          }

          // Assign boxes are vanished from Preview charts. We remove the associated Objs IDs to ensure the Vacant
          // box is added (if needed).

          if (isPreviewChart && aLayout.fBoxContentSubType === enumEmptyPositionLayoutType.open) {
            const positionLayout = scNodeMap
              .get(scNode.GetLayout().GetContainerObjID())
              ?.GetLayout();
            positionLayout?.SetAssociatedObjIDs(
              positionLayout?.GetAssociatedObjIDs().filter(id => id !== scNode.GetDataObjID())
            );

            scm.RemoveSCNode(scNode, subChartID);
            continue;
          }
        }
        var newBox = this.AddChartBox(
          aLayout.GetPositionX(),
          aLayout.GetPositionY(),
          aLayout,
          dataObj,
          parentObj,
          false,
          false,
          false,
          false,
          objID,
          false
        );
      }
    }

    //CB 2019-09-28 - We have the problem of loading MRB's before their associated boxes which causes problems. All non-grouped boxees have to be loaded first.
    for (var anMRBNode = 0; anMRBNode < MRBs.length; anMRBNode++) {
      var scNode = MRBs[anMRBNode];
      var aLayout = scNode.GetLayout();
      var layout_subChartID = aLayout.GetSubChartID();
      var isHidden = aLayout.GetHidden();

      if (subChartID == layout_subChartID && !isHidden) {
        var objID = scNode.GetDataObjID();
        var dataObj = scNode.GetDataObj();
        var parentObj = dataObj?.GetParent() ?? null;

        if (dataObj != null) parentObj = dataObj.GetParent();

        //Handle children being filtered
        var associateIDs = aLayout.GetAssociatedObjIDs(false) || [];
        if (associateIDs.length === 0 && dataObj.GetHolderList().length > 0) {
          aLayout.SetAssociatedObjIDs(dataObj.GetHolderList().map(holder => holder.GetObjectID()));
          associateIDs = aLayout.GetAssociatedObjIDs(false) || [];
        }
        if (dataObj?.IsPositionObj()) {
          // If it's 0, it will be defaulted to 1. Careful, this || is done for showing Closed Positions in Planning.
          var staffingTarget = dataObj.SmartGetFieldValue("Desired Holders") || 1;
          var allFiltered =
            dataObj.GetHolderList().every(holder => holder.IsFiltered()) ||
            (isCustomChart && dataObj.fHolderList.length === 0);

          // Only consider adding extras if positon is not filtered or if its the current top box
          if (!dataObj.IsFiltered() || curSC.IsTopObjRelated(dataObj)) {
            // Only add extras if the position is not fully allocated
            if (
              associateIDs &&
              (isCustomChart ||
                allFiltered ||
                isPreviewChart ||
                associateIDs.length < staffingTarget)
            ) {
              this.HandlePositionExtras(scNode, subChartID, associateIDs, allFiltered, scNodeMap);
            }
          }
        }

        if (typeof subChartID != "string") {
          var allFiltered = false;
          if (!curSC.IsTopObjRelated(dataObj)) {
            allFiltered = true;
            for (
              var anAssociateIDIdx = 0;
              anAssociateIDIdx < associateIDs.length;
              anAssociateIDIdx++
            ) {
              var theID = associateIDs[anAssociateIDIdx];
              var theNode = scNodeMap.get(theID) || null;
              if (!theNode) continue; //Potentially a problem, but bypassing for now.
              var theDataObj = theNode.GetDataObj();
              if (!theDataObj) {
                //its an extra with no dbobj
                allFiltered = false;
                break;
              }
              if (
                !theDataObj.IsFiltered() &&
                !theDataObj.CheckParentFilteredFast(scm, fullDBObjSet, subChartID, curSC)
              ) {
                allFiltered = false;
                break;
              }
            }
          }
        }

        if (dataObj?.IsPositionObj() || (associateIDs?.length > 0 && !allFiltered)) {
          var newBox = this.AddChartBox(
            aLayout.GetPositionX(),
            aLayout.GetPositionY(),
            aLayout,
            dataObj,
            parentObj,
            false,
            false,
            false,
            false,
            objID,
            false
          );
          var curDoc = aLayout.GetDocRef();
        } else if (!allFiltered) console.log("Container with no associatedIDs. Delete this?");
      }
    }
  }

  SwitchToSubChartByObj(subChart, resetFocus = true) {
    //console.log('Document should be doing SubChartSwitch here')
    var scID = subChart.GetSubChartID();
    this.SwitchToSubChart(scID, resetFocus);
  }

  ReloadCurrentSubChart(resetFocus = true) {
    var scm = this.GetSubChartMgr();
    var sc = scm.GetCurrentSubChart();
    sc = scm.ValidateSubChart(sc);
    if (sc == null) {
      console.log("missing subchart");
      return;
    }
    var subChartID = sc.GetSubChartID();
    var activeSelection = this.GetFirstSelectedBox(null);
    var restoreId = null;
    if (activeSelection) {
      var curDataObj = activeSelection.GetDataObj();
      restoreId = curDataObj?.GetObjectRecordID();
    }
    this.SwitchToSubChart(subChartID, resetFocus);
    if (restoreId) {
      this.FocusObject({ recordId: restoreId });
    }
  }

  // Undo a level change that was committed before the page turned out to be
  // unrenderable, so the levels selector and the ?levels= URL stop advertising a page
  // nobody can see. Only rolls back when QXDoc.SvrChartQueryResult marked a pending
  // change, so refusing a drill-down to a different subchart does not disturb levels.
  // State is restored before the broadcast: the UI writes ?levels= back, which
  // re-enters useLevelsSync, and that only no-ops when loaded levels already match.
  PRefusePage() {
    var scm = this.GetSubChartMgr();
    var subChart = scm ? scm.GetCurrentSubChart() : null;
    var prior = subChart ? subChart.fPrevLoadedLevels : null;
    var restored = null;

    // Only a real, previously rendered depth is a safe rollback target. Guard against
    // the "not loaded" sentinel in particular: -1 means "all levels" to the backend,
    // so restoring it would request the whole org and fail harder.
    if (subChart && typeof prior === "number" && prior > 0) {
      restored = prior;
      if (this.fDoc) this.fDoc.SetRequestedLevels(restored);
      if (subChart.fPrevChainLoaded != null) subChart.fChainLoaded = subChart.fPrevChainLoaded;
      // Restoring the level number is not enough. By the time a page is refused,
      // PrepSwitchSubChart and LoadSubChart have already swapped this page's boxes onto
      // the canvas, and they are the ones that cannot be drawn. Mark the subchart
      // unloaded so the next switch re-queries and rebuilds the page that did render,
      // instead of SubChartDataLoaded reporting a cache hit and leaving the broken one.
      subChart.fLoadedLevels = -1;
    }

    if (subChart) {
      subChart.fPrevLoadedLevels = null;
      subChart.fPrevChainLoaded = null;
    }

    if (this.fWSMgr) this.fWSMgr.BroadcastTopic("PageRefused", { levels: restored });

    return false;
  }

  SwitchToSubChart(subChartID, resetFocus = true) {
    var plm = this.GetPageLayoutMgr();
    var scm = this.GetSubChartMgr();
    var nodeCount = scm.GetUnfilteredNodesForSubChart(subChartID, false);
    var theDoc = this.fDoc;
    // const configuredLimit = Globals._accountInfo.GetMaxRecords(true);
    // const effectiveLimit = Math.min(configuredLimit, kMaxRecSinglePage);
    // const isActivelyExporting = Globals._publishRef instanceof QPublishPanel;
    // if (nodeCount > effectiveLimit && !isActivelyExporting) {
    if (nodeCount > kMaxRecSinglePage) {
      var overLimitAlert = new QAlert();
      overLimitAlert.InitAlert(
        appstr("Could not load page"),
        appstr("The number of records on the page exceeds the single page limit") +
          ". " +
          appstr("Maximum") +
          ": " +
          // effectiveLimit +
          kMaxRecSinglePage +
          " " +
          appstr("Current") +
          ": " +
          nodeCount +
          ". \n\n" +
          appstr(
            "To remedy this issue: reduce the number of levels shown, adjust subchart break criteria, or apply a filter"
          ),
        this,
        this.fWSMgr
      );
      scm.SetCurrentSubChart(subChartID);
      return this.PRefusePage();
    }
    var curSubChart = scm.GetCurrentSubChart();
    var initSubChart = curSubChart;
    if (curSubChart) curSubChart.SaveRootOrder();
    var oldNode = plm.GetRootNodeForSubChart();

    // Ungroup before switching so container PLM nodes don't bleed into the child page.
    // Skipped during publish/preview — those paths manage layout per-page separately.
    if (!Globals._publishRef && theDoc.IsGroupingApplied()) {
      var wsMgr = this.fWSMgr;
      var curBoxList = wsMgr.GetTargetTrees(false, theDoc, false) || [];
      if (curBoxList.length) theDoc.UngroupBoxesAndReapplyLayout(curBoxList);
    }

    this.PrepSwitchSubChart();
    scm.SetCurrentSubChart(subChartID);
    if (
      subChartID != kMasterSCID &&
      (Globals._altLayerToRender != enumElementLayer.master || Globals._publishRef !== null)
    ) {
      this.LoadSubChart(subChartID);
      plm.LoadRootOrder();
    }

    curSubChart = scm.GetCurrentSubChart();
    var node = plm.GetRootNodeForSubChart();
    // SEPT2
    var topBoxObj = curSubChart.GetTopDBObj();
    if (topBoxObj) {
      /*
        var pID = topBoxObj.GetObjectRecordID();
        Globals._accountInfo.SetTopBoxID(pID);
        if (isInViewer())       //changed from serverchart check to turn on also while in planning. This may also fix the 'bug' where cancelling mid-publish causes issue.
        {
            var isAux = topBoxObj.SmartGetFieldValue('isAux');
            Globals._accountInfo.SetTopBoxIsAux(isAux);
            //nasty logic path
        }
         */
    }
    if (theDoc.IsServerChart() && subChartID) {
      //Used to apply active template to the newly loaded page in viewer. Shares a lot of use case, but slightly different, from below else block.
      //TODO: I actually don't think these two blocks need to exist anymore.
      var wsMgr = this.fWSMgr;
      var boxList = wsMgr.GetTargetTrees(false, theDoc, true);
      ApplyChartTemplateToDoc(theDoc, null, boxList, false, wsMgr);
      theDoc.ApplyChartHighlights();
    } else if (node && subChartID !== kMasterSCID) {
      //Shared trigger for any auto-format operation. Autoformatting can be needed when switching to new subchart because SC Level for nodes on the page can change, and we suppressed
      //the prior call direclty during the recalculate step for auto-formatting (it was more trouble than it was worth due to recursion concerns)
      this.AutoFormat(node, true);
    }

    theDoc.ApplyChartGroupingToDoc();

    // Publish + grouping: force DoOptimizeHelper per page since DoOptimizePages is
    // blocked during real publish (NodePoly.Clone crash).
    const needsOptimize =
      curSubChart.GetNeedOptimize() || (Globals._publishRef && theDoc.IsGroupingApplied());
    if (needsOptimize) {
      //New subchart to the UI- need to recalculate
      var rootNodes = [];
      plm.GetRootNodes(rootNodes, true);
      var nodeLen = rootNodes.length;
      var curDoc = this.fDoc;
      DoOptimizeHelper(rootNodes[nodeLen - 1], plm, curDoc);
      plm.RecalculatePageLayout(rootNodes[nodeLen - 1]);
      curSubChart.SetNeedOptimize(false);
    }

    var pm = this.GetPageMgr();
    var minWidth = pm.GetMinPageWidth();
    var minHeight = pm.GetMinPageHeight();

    // Clear last page's verdict: DoRecalculateChartLayout skips the resize (and so
    // skips SizePanelAndPort, which sets this) when the layout returns no size, and a
    // stale true would refuse a page that actually fits.
    this.fCanvasSizeExceeded = false;

    this.ChangePageSize(
      minWidth,
      minHeight,
      pm.GetPageMargins(),
      pm.GetPageOrientation(),
      pm.GetHeaderHeight(),
      pm.GetFooterHeight(),
      enumPageSizeUnits.pixels
    );

    var isPublishing = Globals._publishRef !== null;

    // Page extent is only known once ChangePageSize has run the layout. A page can sit
    // well under every record limit and still be far too large for the browser to
    // draw - a wide tree of a few thousand boxes is hundreds of thousands of pixels
    // across - in which case the canvas discards every draw and the user gets a blank
    // chart with nothing in the console. Tell them instead. Export renders through
    // fAltCanvas rather than the offscreen buffer, so it is not subject to this.
    if (this.fCanvasSizeExceeded && !isPublishing) {
      var tooLargeAlert = new QAlert();
      tooLargeAlert.InitAlert(
        appstr("Could not load page"),
        appstr("canvastoolarge") +
          " (" +
          this.fRequestedBufferWidth +
          " x " +
          this.fRequestedBufferHeight +
          " px). \n\n" +
          appstr(
            "To remedy this issue: reduce the number of levels shown, adjust subchart break criteria, or apply a filter"
          ),
        this,
        this.fWSMgr
      );
      return this.PRefusePage();
    }

    if (resetFocus && !isPublishing) {
      this.HandleAutoZoom();
    }

    if (!isPublishing) {
      this.DoInitialHighlight();
    }

    if (subChartID !== kMasterSCID) {
      var wsMgr = this.fWSMgr;
      wsMgr.BroadcastTopic(isPublishing ? "previewSubchartSwitch" : "subchartSwitch", [
        initSubChart,
        curSubChart
      ]);
      this.RestoreKeyboardSelection();
    }

    // The page rendered, so the level change is real - drop the rollback point.
    var renderedSubChart = scm.GetCurrentSubChart();
    if (renderedSubChart) {
      renderedSubChart.fPrevLoadedLevels = null;
      renderedSubChart.fPrevChainLoaded = null;
    }

    return true;
  }

  // This function will store the current user-selected boxes as the initial selection when a new subchart is loaded
  SetCurrentHighlightAsInitial() {
    const cache = this.GetHighlightCache();
    for (const element of cache) {
      const pageNode = element.nodeRef;
      const dataObj = pageNode?.GetDataObj();
      if (dataObj) {
        this.AddToInitialHighlight(dataObj.GetObjectRecordID(), dataObj.InferObjectType());
      }
    }
  }

  DoInitialHighlight() {
    const initialHighlight = this.GetInitialHighlight();
    const plm = this.GetPageLayoutMgr();

    for (const { recordId, objType } of initialHighlight) {
      const node = plm.FindNodeFromRecordID(recordId, objType);
      const chartBox = node?.GetChartBox();

      if (chartBox) {
        this.DoHighlightElement(chartBox, true);
      }
    }

    this.ClearInitialHighlight();
  }

  GetInitialHighlight() {
    return this.fInitialHighlightIds;
  }

  AddToInitialHighlight(recordId, objType = null) {
    this.fInitialHighlightIds.push({ recordId, objType });
  }

  ClearInitialHighlight() {
    this.fInitialHighlightIds = [];
  }

  CreateRootSubChart() {
    var scm = this.GetSubChartMgr();
    return scm.CreateRootSubChart();
  }

  LoadSubCharts(theDoc, excludeList) {
    var plm = this.GetPageLayoutMgr();
    plm.SetLoading(true);

    theDoc.LoadSubCharts(excludeList);

    // Load up the master page elements
    this.LoadMasterPageSubChart();

    // Load up top subchart
    var scm = this.GetSubChartMgr();
    var topID = scm.GetRootSubChartID();

    this.SwitchToSubChart(topID);
    plm.SetLoading(false);
  }

  /*
function MapAssistantFlag(assistantFlag)
{
    var bStyle = enumBranchStyle.assistantright;
    switch (assistantFlag)
    {
        case 1:
            bStyle = enumBranchStyle.assistantright;
            break;
        case 2:
            bStyle = enumBranchStyle.assistantpeerright;
            break;
        case 3:
            bStyle = enumBranchStyle.assistantpeerleft;
            break;

    }

    return bStyle;
}
*/
  ImportSubs(aBox, assistMode, rules, parentBranchStyle) {
    var parentDataObj = aBox.GetDataObj();
    var n = parentDataObj.GetNumChildren();
    var baseStyle = DetermineBranchStyleNoChildren(parentDataObj, null);

    var theClass = parentDataObj.GetParentClass();
    var assistFieldDef = null;
    var useAssistantFlag = false;

    var useRule = false;
    var stdAssistRule = null;
    var peerAssistRule = null;
    var startLeft = rules.GetStartAssistLeft();

    switch (assistMode) {
      case enumAssistOption.manual:
        break;
      case enumAssistOption.byrule:
        useRule = true;
        if (rules.GetUseAssistantRule()) stdAssistRule = rules.GetAssistantRule();
        if (rules.GetUsePeerAssistRule()) peerAssistRule = rules.GetPeerAssistRule();
        break;
      case enumAssistOption.byfield:
        useAssistantFlag = true;
        assistFieldDef = theClass.GetFieldDefByID("isAssistant");
        break;
    }

    var peerAssist = [];
    var stdAssist = [];
    var stdSubs = [];

    for (var i = 0; i < n; i++) {
      var isAssist = false;
      var childDBObj = parentDataObj.GetIndexChild(i);
      if (useRule) {
        if (stdAssistRule != null && !stdAssistRule.IsRuleEmpty()) {
          var matchRule = stdAssistRule.MatchRule(childDBObj, this.fDoc);
          if (matchRule) {
            stdAssist.push(childDBObj);
            isAssist = true;
          }
        }

        if (peerAssistRule != null && !peerAssistRule.IsRuleEmpty()) {
          var matchRule = peerAssistRule.MatchRule(childDBObj, this.fDoc);
          if (matchRule) {
            if (IsAssistantStyle(parentBranchStyle) != null) stdAssist.push(childDBObj);
            else peerAssist.push(childDBObj);
            isAssist = true;
          }
        }
      } else if (useAssistantFlag && assistFieldDef) {
        var assistantFlag = childDBObj.GetAssistantFlag(assistFieldDef);
        if (assistantFlag != 0) {
          stdAssist.push(childDBObj);
          isAssist = true;
        }
      }

      if (!isAssist) stdSubs.push(childDBObj);
    }

    var theLayout = null;
    var marginMode = null;

    var newChild,
      i,
      n = peerAssist.length;
    var bStyle = startLeft ? enumBranchStyle.assistantpeerleft : enumBranchStyle.assistantpeerright;
    for (i = 0; i < n; i++) {
      newChild = this.AddSubordinate(
        parentDataObj,
        peerAssist[i],
        theLayout,
        bStyle,
        marginMode,
        false,
        true
      );
      this.ImportSubs(newChild, assistMode, rules, bStyle);
      if (i >= 1) bStyle = enumBranchStyle.assistantright;
      else
        bStyle = startLeft ? enumBranchStyle.assistantpeerright : enumBranchStyle.assistantpeerleft;
    }

    n = stdAssist.length;
    bStyle = startLeft ? enumBranchStyle.assistantleft : enumBranchStyle.assistantright;
    for (i = 0; i < n; i++) {
      newChild = this.AddSubordinate(
        parentDataObj,
        stdAssist[i],
        theLayout,
        bStyle,
        marginMode,
        false,
        true
      );
      this.ImportSubs(newChild, assistMode, rules, bStyle);
      if (bStyle == enumBranchStyle.assistantright) bStyle = enumBranchStyle.assistantleft;
      else bStyle = enumBranchStyle.assistantright;
    }

    n = stdSubs.length;
    for (i = 0; i < n; i++) {
      newChild = this.AddSubordinate(
        parentDataObj,
        stdSubs[i],
        theLayout,
        baseStyle,
        marginMode,
        false,
        true
      );
      this.ImportSubs(newChild, assistMode, rules, baseStyle);
    }

    /*
        if (parentBranchStyle)
        {
            // PeerRight Parent with peerLeft child or vice-versa
            if (parentBranchStyle == enumBranchStyle.assistantpeerright && assistantFlag == 3)
                assistantFlag = 1;
            else if (parentBranchStyle == enumBranchStyle.assistantpeerleft && assistantFlag == 2)
                assistantFlag = 1;
        }
    */
  }

  SetDocDirty() {
    var theDoc = this.fDoc;
    theDoc.SetDirty(true);
  }

  GetPriorDoc(subchartRules) {
    var scm = this.GetSubChartMgr();
    return scm.GetPriorDoc(subchartRules);
  }

  GetSubChartMgr() {
    return this.fDoc.GetSubChartMgr();
  }

  CreateHolderBoxes(posObj) {
    var holders = posObj.GetHolderList();
    if (holders) {
      var n = holders.length;
      for (var i = 0; i < n; i++) {
        var holder = holders[i];
        var objID = holder.GetObjectID();
        this.AddChartBox(0, 0, null, holder, null, false, false, false, false, objID, false);
      }
    }
  }

  FinishImport(subchartRules) {
    var theDoc = this.fDoc;

    var objDB = theDoc.GetObjDB();
    theDoc.fClearPicCache = false;
    theDoc.fClearPicCache = subchartRules.GetClearPicCache();
    var isPositions = theDoc.IsPositionDocument();
    let className = isPositions ? enumObjClassNames.position : enumObjClassNames.box;
    var objClass = objDB.GetClassByName(className);

    var n = objClass.GetObjectCount();
    if (n == 0) return;

    var hasHier = true;
    // not sure why this subchart is being created here. WireSubChartList also creates a root sc
    var rootSubChart = this.CreateRootSubChart();

    // We've set a top box in filter data...
    var forceTop = theDoc.GetForceTop();
    // Still need to call this to make sure orphans are tagged
    var largestTree = theDoc.GetLargestTree(objClass, true, !hasHier, forceTop); //Also tags orphans
    theDoc.ClearForceTop();

    var theRootDBObj = null;

    // Sorting - 'persist sort' overrides 'sort after refresh'
    var sortConfig = subchartRules.GetSortCriteria();
    var hasSortConfig = sortConfig != null && sortConfig.GetNumCriteria() > 0;
    var doSort = subchartRules.GetSortChart() && hasSortConfig;
    var persistSort = !doSort;

    var objID = null;
    if (hasHier) {
      theRootDBObj = largestTree;
      objID = theRootDBObj.GetObjectID();
    }

    var pageLayoutMgr = this.GetPageLayoutMgr();
    pageLayoutMgr.EnableIndexing(false);
    var defaultLayout = theRootDBObj.IsPositionObj() ? this.GetMRBLayout() : this.GetEmptyLayout();
    var p = this.InitalBoxPlacement(defaultLayout);

    var scm = this.GetSubChartMgr();
    var rootBox = this.AddChartBox(
      p.fX,
      p.fY,
      null,
      theRootDBObj,
      null,
      true,
      false,
      true,
      false,
      objID,
      true
    );
    rootSubChart.SetTopDBObj(theRootDBObj);

    if (isPositions) this.CreateHolderBoxes(theRootDBObj);

    var assistMode = subchartRules.GetAssistWizMode();
    var persistAssist = subchartRules.GetPersistAssist();
    if (persistAssist) assistMode = enumAssistOption.manual;
    // all nodes are added to root sc
    this.ImportSubs(rootBox, assistMode, subchartRules, null);
    // Copy over sort if persisting
    if (persistSort) {
      scm.CopyBoxSort(subchartRules, theRootDBObj);
      scm.ApplySortOverrides(theRootDBObj);
      scm.SyncSortOverridesFromTree(subchartRules, theRootDBObj);
    }

    // break handling assigns scNode scID refs to their appropriate subcharts
    //NOTE - THIS IS A VERY IMPORTANT FUNCTION - IT SETS THE BASELINE FOR SC NODES FOR THE ENTIRE CHART

    if (!theDoc.IsServerChart()) var breakOverflow = scm.DoAutoBreaks(subchartRules, false);

    scm.CopySubChartBreaks(subchartRules);

    scm.SortSubCharts(subchartRules);

    scm.CopyAssistBranchStyles(subchartRules);

    // Resort after refresh
    if (doSort) {
      //if (isInViewer())
      //{
      var dMgr = theDoc.GetDocMgr();
      var docPrefs = dMgr.GetDocPrefs();
      var pageOpt = docPrefs.GetPageOpt(false);
      pageOpt.SetAllowSort(false);
      //}
      var parentList = scm.GetParentListForChart();
      theDoc.PerformSort(sortConfig, parentList);
    }

    // Persist branch styles
    scm.CopyBranchStyles(subchartRules, breakOverflow);

    // Handle Positions Grouping
    // looping here is done to handle position chartDocs (such as builder) when we are loading more than just the top subchart.
    if (theDoc.GetHierarchyType() === enumHierarchyTypes.positions) {
      theDoc.HandleAllPositionGroups();
    } else if (subchartRules.IsCoHeadChart() && subchartRules.GetSpecifyTopBox()) {
      //the superID field for these boxes will be cohead. Group them together.
      var theCoHeadSCNodes = [];
      //if you turned on cohead chart. If you only have 1 top box, that's fine. We make a container with a single node.
      var topBoxIDs = subchartRules.GetTopBoxIDs();
      var topList = topBoxIDs;
      topList = topList.filter(function (a) {
        return a.trim().length > 0;
      });
      //topList = topList.map(function (a) {return a.trim()});
      //var scNodeIDList = scm.fNodeList.map(function(a){return a.fDataObj? a.fDataObj.GetObjectPersonID() : null});
      for (var anID in topList) {
        var theSCNodes = scm.FindSCSubNodesFromPersonID(topList[anID]);
        theSCNodes = theSCNodes.filter(function (a) {
          return a.GetLayout().GetSubChartFlag() !== enumSubChartFlag.drillup ? 1 : 0;
        });
        //TODO: filter out the ones that are removizzled
        if (theSCNodes.length > 0) {
          var theSCNode = theSCNodes[0];
          theSCNode.GetDataObj().SetParent(null, -1);
          theCoHeadSCNodes.push(theSCNode);
        }
      }
      if (theCoHeadSCNodes.length > 0) {
        var bsmgr = this.fWSMgr.GetBoxStylesMgr();
        //Holy hardcoded batman! Doesn't seem like a better way to do this at this time.
        var coHeadLayout = bsmgr.GetLayoutByID("302", false, enumBoxStyleSearch.allSets);
        coHeadLayout.ClearAssociatedDBObjs();
        coHeadLayout = bsmgr.CloneLayout(coHeadLayout, theDoc);
        var theNewNode = theDoc.GroupNodes(theCoHeadSCNodes, false, coHeadLayout);
        scm.UpdateMRBBreaks(theNewNode, theCoHeadSCNodes);
      }
    }

    //TODO: This entire block has serious room for optimization, and a switch call probably
    //shouldn't happen so deep nested in here.
    var startSCID = 1;
    var switchToSC = true;

    if (theDoc.IsServerChart()) {
      var svrTargetSCID = Globals._svrChartMgr.GetTargetSCID();
      if (svrTargetSCID != -1) startSCID = svrTargetSCID;
    } else {
      var filterTopBoxID = Globals._accountInfo.GetTopBoxID();
      if (filterTopBoxID != null) {
        var wsMgr = this.fWSMgr;
        switchToSC = !wsMgr.NavigateToTopBox(filterTopBoxID);
      }
      theDoc.SetDirty(Globals._uiConfig.InWorkbenchEditMode());
    }

    if (switchToSC) this.SwitchToSubChart(startSCID);

    pageLayoutMgr.EnableIndexing(true);
  }

  DoAttachOrphans(parentDataObj, childDataObj, childIndex, doOverride = true, cache = null) {
    //cache lookups/getters to avoid recursive overhead
    if (!cache) {
      cache = {
        doc: this.fDoc,
        pageLayoutMgr: this.GetPageLayoutMgr(),
        bsmgr: this.fWSMgr.GetBoxStylesMgr(),
        defaultLayout: this.fWSMgr.GetDefaultLayout()
      };
    }

    const doc = cache.doc;
    const pageLayoutMgr = cache.pageLayoutMgr;
    const bsmgr = cache.bsmgr;
    const defaultLayout = cache.defaultLayout;

    const bStyle = parentDataObj
      ? pageLayoutMgr.DetermineBranchStyle(parentDataObj, null)
      : enumBranchStyle.tree;

    const theLayout = bsmgr.CloneLayout(defaultLayout, this.fDoc);

    const newBox = this.AddSubordinate(
      parentDataObj,
      childDataObj,
      theLayout,
      bStyle,
      null,
      false,
      false
    );

    if (childIndex !== -1) {
      pageLayoutMgr.SetParent(childDataObj, parentDataObj, childIndex);
    }

    const n = childDataObj.GetNumChildren();
    for (var i = 0; i < n; i++) {
      const nextChild = childDataObj.GetIndexChild(i);
      if (nextChild) {
        this.DoAttachOrphans(childDataObj, nextChild, -1, false, cache);
      }
    }

    if (doOverride && parentDataObj) {
      const superFieldDef = childDataObj.GetRecordParentIDFieldDef();
      if (superFieldDef) {
        const superKey = superFieldDef.GetFieldName();

        doc.SetBoxOverride(childDataObj, enumOverrideType.move, {
          data: {
            [superKey]: parentDataObj.GetObjectRecordID(),
            Orphan: 0
          }
        });
      }
    }

    return newBox;
  }

  AttachOrphans(rootOrphan) {
    var attachToBox = this.GetDragOver();
    var needsSCSwitch = false;

    var dropType = this.fDropType;
    var validDrag = this.ValidMove(dropType);
    if (!validDrag) return;

    if (dropType == enumDropType.drillunder) var dropDataObj = attachToBox.fChartBox.GetDataObj();
    else if (dropType == enumDropType.drillup)
      var dropDataObj = attachToBox.fChartBox.GetDataObj().GetParent();
    else var dropDataObj = attachToBox.GetDataObj();

    this.ClearSelection();

    var parentDataObj = dropDataObj;
    var childIndex = -1;

    var isPeerDrop = !(
      dropType == enumDropType.subordinate ||
      dropType == enumDropType.drillunder ||
      dropType == enumDropType.drillup
    );

    if (isPeerDrop && parentDataObj) {
      parentDataObj = dropDataObj.GetParent();
      if (parentDataObj == null && dropType != enumDropType.mrbadd) return;
      else if (parentDataObj) {
        var afterBump = 0;
        if (dropType == enumDropType.rightcoworker) afterBump = 1;
        childIndex = parentDataObj.IndexOfChild(dropDataObj, null) + afterBump;
      }
    }

    if (dropType == enumDropType.drillunder || dropType == enumDropType.drillup) {
      var scm = this.GetSubChartMgr();
      //var layout = newBox.GetLayout();
      var cursubchartID = scm.GetCurrentSubChartID();
      var nodeIndex = scm.FindSCSubNodeIndex(dropDataObj.GetObjectID(), cursubchartID);
      var otherNode = scm.GetIndexSCNode(nodeIndex);
      var otherSCID = otherNode.GetSubChartID();
      //var otherSC = scm.FindSubChart(otherSCID);
      this.SwitchToSubChart(otherSCID);
      //needsSCSwitch = true;
    }

    var newBox = this.DoAttachOrphans(parentDataObj, rootOrphan, childIndex, true);

    var nlist = [];
    rootOrphan.GetAllChildren(nlist);
    var n = nlist.length;
    if (n > 8) {
      var scm = this.GetSubChartMgr();
      var iwizdata = this.fWSMgr.GetDocMgr().GetIWizDataInteractive();
      scm.DoInsertOrphanBreaks(rootOrphan, iwizdata, iwizdata.GetSubChartOption());
      needsSCSwitch = true;
    }

    var scm = this.GetSubChartMgr();
    if (dropType == enumDropType.mrbadd) {
      //Ugly hack to drag orphans into box.
      var containerBox = attachToBox;
      if (attachToBox.GetContainerNode()) containerBox = attachToBox.GetContainerNode().fChartBox;
      var curMRB = containerBox;
      this.AddToMRB(curMRB, [newBox]);

      needsSCSwitch = true;
    }
    if (needsSCSwitch) {
      this.SwitchToSubChart(scm.GetCurrentSubChartID());
      return;
    }

    var wsMgr = this.fWSMgr;
    if (this.fDoc.GetActiveTemplate() && wsMgr.fCanvasPanel) {
      var aList = this.GetTreeForBox(newBox);
      ApplyChartTemplateToBoxes(
        this.fDoc,
        this.fDoc.GetActiveTemplate(),
        aList,
        scm.GetCurrentSubChartID(),
        wsMgr,
        wsMgr.GetBoxStylesMgr()
      );
      //this.AutoFormat(this.GetPLMNode(newBox)); Not using autoformatting. It bubbles up- we need to start at the attached root and go down.
    }
    var rootNode = this.GetPLMRootNode(newBox);
    this.RecalculateChartLayout(rootNode);
  }

  AssembleCommand(theWSMgr) {}

  SetKeyboardTargetToCanvas() {
    var wsMgr = this.fWSMgr;
    if (wsMgr && wsMgr.fCanvasPanel) {
      // we do this because the master page creates a dummy canvas panel so we only care about the one the wsmgr has reference to (the one attached to UI)
      this.fWSMgr.SetKeyboardTarget(null, wsMgr.fCanvasPanel, enumKeyboardTargetEnterAction.none);
    }
  }

  PanelToolModeChanged() {
    var toolMode = this.GetPanelToolMode();
    this.SetHoverHighlightEnabled(false);
    this.fBoxSelectEnabled = false;
    this.fBoxDragEnabled = false;
    this.fBoxCmdCode = null;
    this.fBoxDragMode = enumBoxDragMode.dragModeOff;
    this.fCurCursor = "default";
    //return this.fParentPanel.BoxIsControl();

    switch (toolMode) {
      case "ToolMode1": // Select
        this.fBoxSelectEnabled = true;
        this.SetKeyboardTargetToCanvas();
        if (this.GetPanelMode() === enumPanelMode.implicitMode) this.fBoxDragEnabled = true;
        break;
      case "ToolMode2": // Pan
        this.fCurCursor = "pointer";
        break;
      case "ToolMode3": // Move Chart
        this.fBoxDragMode = enumBoxDragMode.moveChart;
        this.fCurCursor = "move";
        break;
      case "ToolMode4": // Add box
        this.fCurCursor = "cell";
        break;
      case "ToolMode5": // Move Branch
        this.fBoxDragMode = enumBoxDragMode.moveBranch;
        this.fBoxCmdCode = "MoveBranch";
        this.fCurCursor = "alias";
        this.fBoxDragEnabled = true;
        break;
      case "ToolMode6": // Add Sub
        this.SetHoverHighlightEnabled(true);
        this.fBoxCmdCode = "AddSub";
        this.fCurCursor = "copy";
        break;
      case "ToolMode7": // Add Assistant
        this.SetHoverHighlightEnabled(true);
        this.fBoxCmdCode = "AddAssistant";
        this.fCurCursor = "copy";
        break;
      case "ToolMode8": // Unlink
        this.fBoxDragMode = enumBoxDragMode.unlinkBranch;
        this.fCurCursor = "default";
        break;
      case "ToolMode9": // Textbox
      case "ToolMode11": // Autotext
      //case "ToolMode12":  // Picture
      case "ToolMode13": // Table
      case "ToolMode14": // Legend
      case "ToolMode15": // Legend
      case "ToolMode16": // enumerated table
        this.fCurCursor = "copy";
        break;
      case "ToolMode17": // Add holder
        this.SetHoverHighlightEnabled(true);
        this.fBoxCmdCode = "AddHolder";
        this.fCurCursor = "copy";
        break;
    }
  }

  SetHoverHighlightEnabled(enabled) {
    this.fHoverHighlightEnabled = enabled;
  }

  IsHoverHighlightEnabled() {
    return this.fHoverHighlightEnabled;
  }

  ActivateCells(theBox, theCell) {
    var boxSelected = theBox.IsSelected();
    // Box=Selected	 Cell=Null 	-> 		All cells in box go active
    // Box=!Selected Cell=Null  ->		All cells in box go inactive
    // Cell=IsSelected 			->		All cells in box go active, cells in other boxes should already be inactive
    var interactMode;

    if (theCell == null) {
      if (boxSelected) interactMode = enumChartBoxInteractionMode.content;
      else interactMode = enumChartBoxInteractionMode.readOnly;
      theBox.SetBoxMode(interactMode);
    } else {
      interactMode = enumChartBoxInteractionMode.content;
      var cellSelected = theCell.IsSelected();
      //TODO: this isn't the right check. we need a better way of handling this.
      if (cellSelected && !(isInViewer() && isInStandardSubmode())) {
        //if (!Globals._keyPadPanel) console.log('in keyboard mode now');
        Globals._keyPadPanel = true;
      }
      if (!cellSelected) {
        var nS = this.GetNumSelectedElements();
        if (nS == 0) interactMode = enumChartBoxInteractionMode.readOnly;
        //if (Globals._keyPadPanel) console.log('no longer in keyboard mode');
        Globals._keyPadPanel = false;
      }
      theBox.SetBoxMode(interactMode);
    }
  }

  ReacquireTarget(targetElement) {
    //   console.log("ReacquireTarget");
    var elementType = targetElement.GetElementType();
    if (elementType != enumChartElement.boxCell) {
      console.log("ReacquireTarget - this should never happen");
      return;
    }

    var parentBox = targetElement.GetParentBox();
    var cellDesc = targetElement.GetCellDesc();
    var theCell = parentBox.GetCellFromCellDesc(cellDesc);
    return theCell;
  }

  MakeKeyboardTarget(theElement) {
    var wsMgr = this.fWSMgr;
    var elementType = theElement.GetElementType();
    if (elementType == enumChartElement.boxCell && theElement.IsPicture()) {
      this.SetKeyboardTargetToCanvas();
      theElement = null;
    }

    if (theElement) wsMgr.SetKeyboardTarget(theElement, this, enumKeyboardTargetEnterAction.none);
  }

  PerformSelect(theElement, selectIt) {
    var wsMgr = this.fWSMgr;
    if (selectIt && !Globals._accountInfo.ReadOnlyMode()) {
      this.MakeKeyboardTarget(theElement);
    } else {
      var curTarget = wsMgr.GetKeyboardTarget();
      if (curTarget == theElement || selectIt)
        // in non-wb modes, selecting boxes should set keyboard target to canvas for arrow functionality to be triggered
        this.SetKeyboardTargetToCanvas();
    }
    if (theElement == null) console.log("Houston we have a problem!");

    var elementType = theElement.GetElementType();
    var parentBox = null;
    switch (elementType) {
      case enumChartElement.chartBox:
      case enumChartElement.textBox:
        theElement.SetSelected(selectIt);
        this.ActivateCells(theElement, null);
        break;
      case enumChartElement.boxCell:
        theElement.SetSelected(selectIt);
        parentBox = theElement.GetParentBox();
        var layout = theElement.GetLayout();
        this.ActivateCells(parentBox, theElement);
        if (
          [enumBoxSubTypes.refBoxTable, enumBoxSubTypes.refBoxLegend].includes(
            layout?.GetBoxSubType()
          )
        ) {
          this.fWSMgr.UpdateLayoutHoverToolbar(theElement, parentBox);
        }
        break;
      case enumChartElement.connectingLine:
        theElement.SetSelected(selectIt);
        break;
      case enumChartElement.scArrow:
        theElement.SetSelected(selectIt);
        break;
    }

    if (this.GetFirstSelectedCell() == null) {
      this.fWSMgr.CloseLayoutHoverToolbar();
    }

    if (parentBox && parentBox["ForceChartLayout"] === true) {
      parentBox["ForceChartLayout"] = false;
      var rootNode = this.GetPLMRootNode(parentBox); //theElement
      var theNode = this.GetPLMNode(parentBox);
      if (theNode) {
        var didFormat = this.AutoFormat(theNode);
        if (!didFormat) this.RecalculateChartLayout(rootNode);
        this.fDoc.ApplyChartGroupingToDoc();
      } else console.log("PerformSelect rootnode null");
    }
    this.fWSMgr.BroadcastTopic("CanvasSelectionChange", { theElement, selectIt });
  }

  GetFirstSelectedCell() {
    return this.GetFirstSelectedElement(enumChartElement.boxCell);
  }

  GetSelectedLayout() {
    var cell = this.GetFirstSelectedCell();
    var box = cell.GetParentBox();
    return box?.GetLayout();
  }

  GetLayout() {
    return this.GetSelectedLayout();
  }

  GetDeleteColumns() {
    var cell = this.GetFirstSelectedCell();
    if (!cell) {
      return null;
    }
    var layout = cell.GetLayout();
    var chartBox = cell.GetParentBox();
    var boxCells = chartBox.fLayoutCells;
    return this.DoGetDeleteColumns(layout, boxCells);
  }

  GetDeleteRows() {
    var cell = this.GetFirstSelectedCell();
    if (!cell) {
      return null;
    }
    var layout = cell.GetLayout();
    var chartBox = cell.GetParentBox();
    var boxCells = chartBox.fLayoutCells;
    return this.DoGetDeleteRows(layout, boxCells);
  }

  DoRowInsert(insertAbove) {
    var cell = this.GetFirstSelectedCell();
    if (!cell) {
      return;
    }
    var layout = cell.GetLayout();
    this.ApplyRowInsert(layout, insertAbove);
    this.UpdateCurrentSelectedLayout();
  }

  DoColInsert(insertLeft) {
    var cell = this.GetFirstSelectedCell();
    if (!cell) {
      return;
    }
    var layout = cell.GetLayout();
    this.ApplyColumnInsert(layout, insertLeft);
    this.UpdateCurrentSelectedLayout();
  }

  DoRowDelete() {
    var cell = this.GetFirstSelectedCell();
    if (!cell) {
      return;
    }

    var layout = cell.GetLayout();
    this.ApplyRowDelete(layout);
    this.UpdateCurrentSelectedLayout();
  }

  DoColumnDelete() {
    var cell = this.GetFirstSelectedCell();
    if (!cell) {
      return;
    }
    var layout = cell.GetLayout();
    this.ApplyColumnDelete(layout);
    this.UpdateCurrentSelectedLayout();
  }

  DoMergeCells() {
    this.ApplyMergeCells();
    this.UpdateCurrentSelectedLayout();
  }

  DoUnmergeCells() {
    this.ApplyUnMergeCells();
    this.UpdateCurrentSelectedLayout();
  }

  GetSelectedCellDescs() {
    var cell = this.GetFirstSelectedCell();
    if (!cell) return [];

    var chartBox = cell.GetParentBox();
    var boxCells = chartBox.fLayoutCells;
    return this.DoGetSelectedCellDescs(boxCells);
  }

  UpdateCurrentSelectedLayout() {
    var cell = this.GetFirstSelectedCell();
    if (!cell) return;
    var box = cell.GetParentBox();
    box.SaveCellSelection(box.GetLayout());
    this.fWSMgr.ApplyLayoutChange();
  }

  GetOuterSelectionColumn(leftMost) {
    var cell = this.GetFirstSelectedCell();
    if (!cell) {
      return -1;
    }
    var layout = cell.GetLayout();
    var chartBox = cell.GetParentBox();
    var boxCells = chartBox.fLayoutCells;
    return this.DoGetOuterSelectionColumn(layout, boxCells, leftMost);
  }

  GetOuterSelectionRow(topMost) {
    var cell = this.GetFirstSelectedCell();
    if (!cell) {
      return -1;
    }
    var layout = cell.GetLayout();
    var chartBox = cell.GetParentBox();
    var boxCells = chartBox.fLayoutCells;
    return this.DoGetOuterSelectionRow(layout, boxCells, topMost);
  }

  BoxSelectEnabled() {
    return this.fBoxSelectEnabled;
  }

  BoxIsControl() {
    var toolMode = this.GetPanelToolMode();
    var panelMode = this.GetPanelMode();
    var implicitControl =
      (panelMode === enumPanelMode.implicitMode ||
        this.fBoxDragMode !== enumBoxDragMode.dragModeOff) &&
      toolMode === "ToolMode1";
    var explicitControl = toolMode === "ToolMode3" || toolMode === "ToolMode5";
    return implicitControl || explicitControl;
  }

  GetBoxCmdCode() {
    return this.fBoxCmdCode;
  }

  GetPLMNode(sourceElement) {
    var pageLayoutMgr = this.GetPageLayoutMgr();
    var objID = sourceElement.GetDataObjID();
    var theNode;

    if (objID == null) theNode = pageLayoutMgr.FindNodeFromBox(sourceElement);
    else theNode = pageLayoutMgr.FindNodeFromDataObjID(objID);
    return theNode;
  }

  GetPLMRootNode(sourceElement) {
    var pageLayoutMgr = this.GetPageLayoutMgr();
    var rootNode = null;

    var objID = sourceElement.GetDataObjID();
    if (objID != null) {
      var theNode = pageLayoutMgr.FindNodeFromDataObjID(objID);
      rootNode = pageLayoutMgr.GetRootNode(theNode);
    } else rootNode = pageLayoutMgr.FindNodeFromBox(sourceElement);

    return rootNode;
  }

  DragCommon(sourceElement, panelX, panelY, bounds, releaseCmd) {
    var sx, sy, sw, sh;

    var dragBox = new QDragBoxElement();

    sx = bounds.fLeft;
    sy = bounds.fTop;
    sw = bounds.fRight - bounds.fLeft;
    sh = bounds.fBottom - bounds.fTop;

    dragBox.InitDragBox(releaseCmd, null);
    dragBox.PositionElement(sx, sy);
    dragBox.SizeElement(sw, sh);
    dragBox.SetDragPosition(panelX, panelY);
    this.fDragSource = sourceElement;
    this.fDragBox = dragBox;
    this.SetElementLayer(dragBox);
    this.PushElement(dragBox);
    this.fWSMgr.SetTarget(this, dragBox);
    this.fDropElement = null;
    this.SetDropMode(true);
  }

  StartChartDrag(sourceElement, panelX, panelY) {
    var pageLayoutMgr = this.GetPageLayoutMgr();
    var theNode = this.GetPLMNode(sourceElement);
    var bounds = pageLayoutMgr.GetBranchBounds(theNode, false);
    this.DragCommon(sourceElement, panelX, panelY, bounds, "ChartMoveDragRelease");
    return true;
  }

  StartUnlinkBranch(sourceElement, panelX, panelY) {
    var l = sourceElement.GetLayout();
    var bounds = l.GetBoundingRect();
    this.DragCommon(sourceElement, panelX, panelY, bounds, "BranchUnlinkDragRelease");
    return true;
  }

  GetDropType() {
    return this.fDropType;
  }

  SetDropMode(dropMode) {
    this.fDropMode = dropMode;
    this.fDropType = enumDropType.none;
    this.ClearDragOver();
  }

  InDropMode(dropMode) {
    return this.fDropMode;
  }

  DragOver(theElement, x) {
    //	console.log("DragOver");
    this.fDropElement = theElement;
    this.fDropType = theElement.DropTypeTest(this.GetLocalX(x));
    this.DoSelectElement(theElement, true);
  }

  ClearDragOver() {
    this.fDropElement = null;
  }

  GetDragOver() {
    return this.fDropElement;
  }

  GetDragDropElement() {
    return this.fDropElement;
  }

  PrepInteractWithElement(controlElement, x, y) {
    var dragMode = this.fBoxDragMode;
    if (dragMode == enumBoxDragMode.dragModeOff || !this.fDragBox) return;
    x = this.GetLocalX(x);
    y = this.GetLocalY(y);
    var delta = this.fDragBox.DeltaFromDragPosition(x, y, true);
    if (this.fDragBox instanceof QElement) this.fDragBox.MoveElement(delta.fX, delta.fY);
  }

  DropOnParent(startBox, dstBox) {
    var startDataObj = startBox.GetDataObj();
    if (startDataObj == null)
      // From Orphan Panel
      return false;

    var destDataObj = dstBox.GetDataObj();
    var plm = this.GetPageLayoutMgr();
    var startNode = plm.FindNodeFromDataObjID(startDataObj.GetObjectID());
    var parentNode = plm.GetParentNode(startNode);

    if (parentNode != null) {
      var parentDataObj = parentNode.GetDataObj();
      if (destDataObj == parentDataObj) return true;
    }

    return false;
  }

  SubChartMoveOK(startDataObj, destDataObj, srcElement) {
    var theDoc = this.fDoc;

    var dstChart = theDoc.IsInMainSubChart(destDataObj);
    if (dstChart == false) {
      var srcChart = theDoc.IsInMainSubChart(startDataObj);
      // Moving to secondary to secondary
      if (srcChart == false) return true;

      // Sub-chart arrow at the top of the box
      //if (srcElement.BottomOfSubChartBreak(true))
      //	return false;

      // Sub-chart arrow at the bottom of the box (or subordinate box)
      /*
		var plm = this.GetPageLayoutMgr();
		var startNode = this.GetPLMNode(srcElement);
		if (plm.HasSubordinateSubChart(startNode))
			return false;

         */
    }

    return true;
  }

  ValidMoveNonPlanning(moveType) {
    var dropElement = this.GetDragOver();
    var dragStart = this.fDragSource;
    var moveLeft = moveType == enumDropType.leftcoworker;
    var moveRight = moveType == enumDropType.rightcoworker;
    var addSub = moveType == enumDropType.subordinate;
    const doc = this.fWSMgr.GetDoc();
    const isPositionDoc = doc.IsPositionDocument();

    // Drop on nothing
    if (dropElement == null) return false;

    var isDraggingContainer =
      dragStart && dragStart.GetContainerType() == enumContainerType.standardmrb;
    const isDraggingPositionBox = isPositionDoc && dragStart?.IsPositionBox?.();

    if (isDraggingContainer && !isDraggingPositionBox) return false;

    var isDroppingOnContainer =
      dropElement.GetContainerType &&
      dropElement.GetContainerType() == enumContainerType.standardmrb;
    const isDroppingOnPositionBox = isPositionDoc && dropElement?.IsPositionBox?.();

    if (isDroppingOnContainer && !isDroppingOnPositionBox) return false;

    var startNodeRef = dragStart?.nodeRef;
    var dropNodeRef = dropElement?.nodeRef;

    var isDraggingGroupedBox =
      startNodeRef &&
      startNodeRef?.GetContainerNode()?.GetContainerType() == enumContainerType.standardmrb;
    const isDraggingHolderOrPosBox =
      isPositionDoc && (startNodeRef?.GetDataObj?.().IsHolderObj() || isDraggingPositionBox);

    if (isDraggingGroupedBox && !isDraggingHolderOrPosBox) return false;

    var isDraggingToGroupedBox =
      dropNodeRef &&
      dropNodeRef?.GetContainerNode()?.GetContainerType() == enumContainerType.standardmrb;

    const isDraggingToVacantBox = dropNodeRef?.GetChartBox()?.IsVacantBox?.();
    const isDraggingToHolderOrPosBox =
      isPositionDoc &&
      (dropNodeRef?.GetDataObj?.()?.IsHolderObj() ||
        isDroppingOnPositionBox ||
        isDraggingToVacantBox);

    if (isDraggingToGroupedBox && !isDraggingToHolderOrPosBox) return false;

    // Holders into arrows
    if (
      isPositionDoc &&
      startNodeRef?.GetDataObj?.().IsHolderObj() &&
      dropNodeRef?.GetDataObj?.() === undefined
    )
      return false;

    const isDraggingHolder = isPositionDoc && startNodeRef?.GetDataObj?.()?.IsHolderObj();
    const isDraggingToHolder = isPositionDoc && dropNodeRef?.GetDataObj?.()?.IsHolderObj();
    if (moveType == enumDropType.drillunder) {
      if (isDraggingHolder && isDraggingToHolder) {
        var destDataObj = dropNodeRef.GetDataObj().GetParent();
      } else {
        var destDataObj = dropElement.fChartBox.GetRootDataObj();
      }
    } else if (moveType == enumDropType.drillup) {
      if (dropElement.fChartBox == dragStart) return false;
      var destDataObj = dropElement.fChartBox.GetRootDataObj().GetParent();
    } else {
      if (isDraggingHolder && isDraggingToHolder) {
        return true;
      }
      var destDataObj = dropElement.GetRootDataObj();
    }

    if (isDraggingToVacantBox) {
      destDataObj = dropElement.GetContainerNode().GetDataObj();
    } else if (isDraggingPositionBox && destDataObj?.IsHolderObj()) {
      destDataObj = destDataObj.GetParent();
    }

    // Add a left or right co-worker to root node
    if (moveLeft || moveRight) {
      if (dropElement.BottomOfSubChartBreak(true)) return false;
    }

    if (dragStart == null) {
      // From the parking lot
      if (addSub) {
        if (dropElement.TopOfSubChartBreak()) return false;
      }
      return true;
    }

    // Drop on self
    if (dropElement == dragStart) return false;

    // Drop on parent - only interesting for dropping as a subordinate
    if (addSub) {
      if (this.DropOnParent(dragStart, dropElement)) return false;

      if (dropElement.TopOfSubChartBreak()) return false;
    }

    //TODO: Not sure that the checks for move type are the correct thing here? Should this be handled differently?

    // Drop grandparent on its own descendant
    var startDataObj = dragStart.GetDataObj();
    if (startDataObj == null) return false;
    //TODO: Update this- should not be able to drag parent into children container.
    var objDB = doc.GetObjDB();
    if (objDB.CheckforCircularReference(startDataObj, destDataObj)) return false;

    // Moving a branch containing a sub-chart to a secondary chart
    if (!this.SubChartMoveOK(startDataObj, destDataObj, dragStart)) return false;

    return true;
  }

  ValidMovePlanning(moveType) {
    var planMgr = this.fWSMgr.fPlanManager;
    var dropElement = this.GetDragOver();
    var dragStart = this.fDragSource;
    var wsMgr = this.fWSMgr;
    var target = wsMgr.fTargetPanel;
    var dbObj = target.GetDBObjClone();

    if (
      dragStart == null ||
      dragStart.GetDataObj() == null ||
      dbObj == null ||
      dropElement == null ||
      (dropElement instanceof QChartBox && dropElement.GetDataObj() == null)
    )
      return false;
    // dropElement ReWiring for subchart arrows.
    if (moveType == enumDropType.drillunder) dropElement = dropElement.fChartBox;
    else if (moveType == enumDropType.drillup) return false;

    var override = planMgr.GetActiveOverrideObj(dbObj);
    var changeType = override != null ? override.SmartGetFieldValue("Type") : null;
    var unmoveableChangeTypes = [
      enumOverrideType.delete,
      enumOverrideType.lock,
      enumOverrideType.moveout
    ];

    if (unmoveableChangeTypes.includes(changeType)) return false;

    return true;
  }

  ValidMove(moveType) {
    var isPlanningMode = Globals._uiConfig.GetUIMode() == enumUIModes.planning;
    return isPlanningMode ? this.ValidMovePlanning(moveType) : this.ValidMoveNonPlanning(moveType);
  }

  XferBranchStyle(startNode, dropDataObj) {
    var plm = this.GetPageLayoutMgr();

    var doc = this.fWSMgr.GetDoc();
    var sortConfig = doc.GetLastSort();
    var hasSortConfig = sortConfig != null && sortConfig.GetNumCriteria() > 0;

    if (hasSortConfig) {
      const a = new QAlert();
      a.InitAlert(
        appstr("Unable to sort"),
        appstr("Your chart has an automatic sort configuration applied"),
        null,
        this.fWSMgr,
        false
      );
    }

    var dropNode = plm.FindNodeFromDataObjID(dropDataObj.GetObjectID());
    startNode.TransferBranchStyle(dropNode);
  }

  DoMoveBranch(dropType, startDataObj, dropDataObj, childIndex, isRollUp) {
    var parentDataObj;
    var plm = this.GetPageLayoutMgr();
    var startNode = plm.FindNodeFromDataObjID(startDataObj.GetObjectID());
    var scm = this.GetSubChartMgr();

    if (dropType != enumDropType.drillunder && dropType != enumDropType.drillup && !isRollUp)
      plm.TagAutoSmartGroup(startNode);

    var change = { data: {} };
    var doc = this.fWSMgr.GetDoc();

    // For holders on position charts, use Employee PositionID field
    // instead of the position's Parent PositionID field
    var isHolderMove = doc.IsPositionDocument() && startDataObj.IsHolderObj?.();
    var superIDKey;
    if (isHolderMove) {
      var holderParentFieldDef = startDataObj.GetParentClass()?.GetParentIDFieldDef?.();
      superIDKey = holderParentFieldDef?.GetFieldName?.();
    }
    if (!superIDKey) {
      superIDKey = dropDataObj.GetRecordParentIDFieldDef().GetFieldName();
    }

    var updateHolderAssignment = targetPosObj => {
      if (!isHolderMove || !targetPosObj) return;
      var curPosObj = startDataObj.GetParentPosition?.();
      if (curPosObj && curPosObj !== targetPosObj) {
        var curList = curPosObj.GetHolderList() ?? [];
        curPosObj.SetHolderList(curList.filter(h => h !== startDataObj));
      }
      startDataObj.SetParentPosition(targetPosObj);
      var newList = targetPosObj.GetHolderList() ?? [];
      if (!newList.includes(startDataObj)) {
        newList.push(startDataObj);
        targetPosObj.SetHolderList(newList);
      }
    };

    switch (dropType) {
      case enumDropType.subordinate:
        parentDataObj = dropDataObj;
        var bStyle = plm.DetermineBranchStyle(parentDataObj, null);
        startNode.SetBranchStyle(bStyle);
        break;
      case enumDropType.rightcoworker:
        parentDataObj = dropDataObj.GetParent();
        childIndex = parentDataObj.IndexOfChild(dropDataObj, null) + 1;
        this.XferBranchStyle(startNode, dropDataObj);
        break;
      case enumDropType.leftcoworker:
        parentDataObj = dropDataObj.GetParent();
        childIndex = parentDataObj.IndexOfChild(dropDataObj, null);
        this.XferBranchStyle(startNode, dropDataObj);
        break;
      case enumDropType.mrbadd:
        //TODO: Should detect shuffling.
        //Should be placed in between indexes of elements near it
        var dropElement = this.GetDragDropElement();
        var dragStart = this.GetDragSourceElement();
        if (!dropDataObj) dropDataObj = dropElement.GetRootDataObj();

        parentDataObj = dropDataObj.GetParent();
        var oldParentMRB = startDataObj.GetParent();
        plm.SetParent(startDataObj, parentDataObj, childIndex);
        if (parentDataObj != startDataObj.GetParent()) {
          change["data"][superIDKey] = parentDataObj.GetObjectRecordID();
          doc.SetBoxOverride(startDataObj, enumOverrideType.move, change);
        }
        updateHolderAssignment(parentDataObj);
        this.CaptureSortOverride(parentDataObj);
        if (oldParentMRB && oldParentMRB !== parentDataObj) this.CaptureSortOverride(oldParentMRB);
        var curMRB = dropElement;
        if (dropElement.GetContainerType() == enumContainerType.nonmrb)
          curMRB = dropElement.GetContainerNode().fChartBox;
        this.DoRemoveFromMRBGroup(dragStart);
        this.AddToMRB(curMRB, [dragStart]);

        var fullSelection = curMRB.GetAssociatedNodes().map(function (a) {
          return a.fChartBox;
        });
        //Handling SC node changes.
        this.SwitchToSubChart(curMRB.GetLayout().GetSubChartID());
        return;
      case enumDropType.drillunder:
      case enumDropType.drillup:
        var oldParentDrill = startDataObj.GetParent();
        startDataObj.SetParent(dropDataObj, childIndex);
        var cursubchartID = scm.GetCurrentSubChartID();
        var nodeIndex = scm.FindSCSubNodeIndex(dropDataObj.GetObjectID(), cursubchartID);
        if (nodeIndex > -1) {
          var otherNode = scm.GetIndexSCNode(nodeIndex);
          var otherSCID = otherNode.GetSubChartID();
          parentDataObj = enumDropType.drillunder ? dropDataObj : dropDataObj.GetParent();
          change["data"][superIDKey] = parentDataObj.GetObjectRecordID();
          doc.SetBoxOverride(startDataObj, enumOverrideType.move, change);
          updateHolderAssignment(parentDataObj);
          if (startNode.GetContainerNode()) {
            var dragStart = this.GetDragSourceElement();
            this.DoRemoveFromMRBGroup(dragStart);
            plm.SetParent(startDataObj, parentDataObj, childIndex);
            //return
          }
          this.CaptureSortOverride(parentDataObj);
          if (oldParentDrill && oldParentDrill !== parentDataObj)
            this.CaptureSortOverride(oldParentDrill);
          this.MoveBranchToSubChart(startNode, cursubchartID, otherSCID);
          this.SwitchToSubChart(cursubchartID, false);
          doc.DoAfterMove({ dbObj: startDataObj });
        }
        return;
    }
    if (parentDataObj != startDataObj.GetParent()) {
      change["data"][superIDKey] = parentDataObj.GetObjectRecordID();
      doc.SetBoxOverride(startDataObj, enumOverrideType.move, change);
    }
    updateHolderAssignment(parentDataObj);
    var oldParent = startDataObj.GetParent();
    var oldRootNode = plm.GetRootNode(startNode);
    plm.SetParent(startDataObj, parentDataObj, childIndex);

    this.CaptureSortOverride(parentDataObj);
    if (oldParent && oldParent !== parentDataObj) this.CaptureSortOverride(oldParent);

    if (startNode.GetContainerNode() != null) {
      var dragStart = this.GetDragSourceElement();
      //TODO: Don't do this when you're the only box left in a group?
      this.DoRemoveFromMRBGroup(dragStart);
      this.SwitchToSubChart(startNode.GetLayout().GetSubChartID());
      return;
    }

    if (startDataObj.GetParent()) {
      //TODO: Autoformatting won't work quite right when dragging boxes out of the top cohead group.
      //A bit more code is needed to handle this scenario.
      var oldManagerNode = plm.FindNodeFromDataObjID(startDataObj.GetParent().GetObjectID());
      this.AutoFormat(oldManagerNode);
    }

    this.RecalculateChartLayout(startNode);

    var newRootNode = plm.GetRootNode(startNode);

    if (newRootNode != oldRootNode && oldRootNode != startNode)
      this.RecalculateChartLayout(oldRootNode);

    doc.DoAfterMove({ dbObj: startDataObj, doFocus: !isRollUp });
  }

  CaptureSortOverride(parentDataObj) {
    if (!parentDataObj) return;
    var doc = this.fWSMgr.GetDoc();
    if (doc.IsPlanChart()) return;

    var docMgr = this.fWSMgr.GetDocMgr();
    var iWizData = docMgr.GetIWizDataInteractive();
    if (!iWizData) return;

    var parentRecordID = parentDataObj.GetObjectRecordID();
    if (!parentRecordID) return;

    var children = parentDataObj.GetChildList(false);
    if (!children || children.length === 0) {
      iWizData.ClearSortOverrideForParent(String(parentRecordID));
      return;
    }

    var childRecordIDs = [];
    for (var i = 0; i < children.length; i++) {
      var childID = children[i].GetObjectRecordID();
      if (childID) childRecordIDs.push(childID);
    }

    iWizData.SetSortOverrideForParent(String(parentRecordID), childRecordIDs.map(String));
  }

  GetDragSourceElement() {
    return this.fDragSource;
  }

  MoveBranch() {
    var dropType = this.GetDropType();

    var dropElement = this.GetDragDropElement();
    var dragStart = this.GetDragSourceElement();

    var startDataObj = dragStart.GetDataObj();
    if (dropType == enumDropType.drillunder)
      var dropDataObj = dropElement.fChartBox.GetRootDataObj();
    else if (dropType == enumDropType.drillup)
      var dropDataObj = dropElement.fChartBox.GetRootDataObj().GetParent();
    else var dropDataObj = dropElement.GetRootDataObj();

    this.DoMoveBranch(dropType, startDataObj, dropDataObj, -1);
  }

  IsValidMove(theNode, delta) {
    var cb = theNode.GetChartBox();
    var scaleMethod = cb.GetScalingMethod();
    switch (scaleMethod) {
      case enumScalingMethod.scalevert:
      case enumScalingMethod.scalehorz:
      case enumScalingMethod.scaletopage:
        return false;
        break;
    }

    var plm = this.GetPageLayoutMgr();
    var bounds = plm.GetBranchBounds(theNode, false);

    bounds.fLeft = bounds.fLeft + delta.fX;
    bounds.fTop = bounds.fTop + delta.fY;
    bounds.fRight = bounds.fRight + delta.fX;
    bounds.fBottom = bounds.fBottom + delta.fY;

    if (bounds.fLeft < 0) return false;
    if (bounds.fTop < 0) return false;

    if (bounds.fRight > this.fCanvasSizeX) return false;
    if (bounds.fBottom > this.fCanvasSizeY) return false;

    return true;
  }

  MoveChart(theNode, deltaX, deltaY) {
    var plm = this.GetPageLayoutMgr();
    plm.MoveChart(theNode, deltaX, deltaY);

    this.RepositionCells();
    //this.RecalculateConnectingLines();
  }

  GetDragDelta() {
    if (this.fDragBox) {
      const delta = this.fDragBox.DeltaFromDragOrigin();

      // Convert the delta from screen space to canvas space.
      // fDragBox does not know about the scale, and it
      // gets updated with screen space mouse coordinates.
      delta.Scale(1.0 / this.GetScaleFactor());
      return delta;
    } else {
      return new QPoint();
    }
  }

  GetDragNode() {
    var theNode = this.GetPLMNode(this.fDragSource);
    return theNode;
  }

  GetDragMode() {
    return this.fBoxDragMode;
  }

  DragBranchComplete(validDrop) {
    if (!validDrop && this.GetDragOver() == null) {
      this.fDragSource = null;
      this.SetDropMode(false);
      return;
    }
    this.ClearSelection();
    if (validDrop) {
      //this.MoveBranch(this.fDropIndicator);
      this.MoveBranch();
    }
    this.fDragSource = null;
    this.SetDropMode(false);
  }

  DragComplete(validDrop) {
    this.ClearSelection();

    if (validDrop) {
      var delta = this.GetDragDelta();
      var theNode = this.GetDragNode();

      var plm = this.GetPageLayoutMgr();
      plm.SetUserCentering(theNode);
      this.MoveChart(theNode, delta.fX, delta.fY);
      plm.ResetEdgeDistance(theNode);
    } else {
      if (this.fBoxDragMode == enumBoxDragMode.unlinkBranch) this.DoUnlinkBranch();
    }
    if (this.fDragBox instanceof QElement) this.RemoveElement(this.fDragBox);
    else this.fDragBox?.NukePanel();
    this.fDragSource = null;
    this.SetDropMode(false);
  }

  // This should move down from the presentation layer
  SetAsOrphan(dataObj, setAsOrphan) {
    dataObj.SetAsOrphan(setAsOrphan);
    /*
	var isOrphan = 0;
	if (setAsOrphan)
		isOrphan = 1;

	var fieldDef = dataObj.GetFieldDefByID("Orphan");
	dataObj.SetFieldValue(fieldDef,isOrphan);
	*/
  }

  OrphanBranch(doRecalc) {
    var theNode = this.GetDragNode();
    var plm = this.GetPageLayoutMgr();
    var scm = this.GetSubChartMgr();
    var dataObj = theNode.GetDataObj();
    var oldRootNode = plm.GetRootNode(theNode);

    // Make sure the nodes peers are resized if needed
    plm.TagAutoSmartGroup(theNode);

    // Next unlink the orphan root and set it as a orphan
    dataObj.SetParent(null, -1);
    this.SetAsOrphan(dataObj, true);
    this.DoRemoveFromMRBGroup(theNode.fChartBox);

    // Select the branch (under the orphan) - including the orphan
    this.SelectBranch(this.fDragSource, true);
    var selectedBoxes = this.GetSelectedBoxes(false);

    // Delete nodes and boxes from the canvas
    plm.RemoveNode(theNode, true);
    this.DoOrphanSelection(selectedBoxes, false);

    plm.RemoveSubChartBreak(theNode, false);

    var removeList = [];
    dataObj.GetAllChildren(removeList);

    scm.RemoveChildrenSubCharts(removeList);
    this.PDeleteBox(theNode.fChartBox);

    for (var anObjIDx in removeList) {
      var theObjToRemove = removeList[anObjIDx];
      var SCNodes = scm.FindSCSubNodesFromDataObj(theObjToRemove);
      for (var aNodeIdx in SCNodes) {
        var theSubNode = SCNodes[aNodeIdx];
        var nodeSCID = theSubNode.GetSubChartID();
        scm.RemoveSCNode(theSubNode.GetDataObjID(), nodeSCID);
      }
    }

    var thisSCID = theNode.GetLayout().GetSubChartID();
    //I know turning this off will remove a bit of behavior from MRB. Need this fixed in the primary use case.
    //this.SwitchToSubChart(thisSCID);
    if (oldRootNode != theNode && doRecalc) this.RecalculateChartLayout(oldRootNode);
  }

  DoUnlinkBranch() {
    var theNode = this.GetDragNode();
    var dataObj = theNode.GetDataObj();
    var plm = this.GetPageLayoutMgr();

    var oldRootNode = plm.GetRootNode(theNode);

    var breaksBelow = TestSubChartBreaksBelow(dataObj);

    if (breaksBelow) {
      var w = new QAlert();
      w.InitAlert(appstr("Alert"), appstr("unlinkerr1"), null, this.fWSMgr);
    } else {
      plm.TagAutoSmartGroup(theNode);
      this.fWSMgr.StartCheckPoint();
      plm.SetParent(dataObj, null, -1);
      var delta = this.GetDragDelta();
      plm.SetUserCentering(theNode);
      this.MoveChart(theNode, delta.fX, delta.fY);
      this.fWSMgr.EndCheckPoint();
      this.RecalculateChartLayout(oldRootNode);
    }
  }

  GetChartBoxFromDBObj(dbObj) {
    var numElements = this.GetNumElements();
    for (var i = 0; i < numElements; i++) {
      var theElement = this.fElements[i];
      if (this.IsElementChartBox(theElement, false)) {
        if (theElement.GetDataObj() == dbObj) return theElement;
      }
    }

    return null;
  }

  GetSelectedBoxes(includeTextBoxes) {
    var s = this.GetSelectionCache();
    var selectedElements = null;

    var numElements = s.length;
    for (var i = 0; i < numElements; i++) {
      var anElement = s[i];
      if (this.IsElementChartBox(anElement, includeTextBoxes)) {
        if (selectedElements == null) selectedElements = [];
        selectedElements.push(anElement);
      }
    }
    //TODO: a null result seems less proper than empty list here.
    return selectedElements;
  }

  GetSelectedLines() {
    var s = this.GetSelectionCache();
    let selectedElements = [];

    var numElements = s.length;
    for (var i = 0; i < numElements; i++) {
      var anElement = s[i];
      if (anElement.GetElementType() == enumChartElement.connectingLine)
        selectedElements.push(anElement);
    }
    return selectedElements;
  }

  GetElementsOfType(elementType) {
    var elements = null;
    var numElements = this.GetNumElements();
    for (var i = 0; i < numElements; i++) {
      var anElement = this.fElements[i];
      var curType = anElement.GetElementType();
      if (curType == elementType) {
        if (elements == null) elements = [];
        elements.push(anElement);
      }
    }

    return elements;
  }

  SelectIfActive(anElement) {
    if (this.IncludeElementInSelection(anElement)) this.DoSelectElement(anElement, true);
  }

  PRemoveChartBox(theBox) {
    theBox.RemoveConnectingLines();
    theBox.RemoveBoxResizer();
    theBox.RemoveSubChartDrill();
    this.RemoveElement(theBox);

    // The box might be the keyboard target...
    theBox["invalid"] = true;
  }

  MoveBranchToSubChart(startNode, cursubchartID, targetSCID) {
    var scm = this.GetSubChartMgr();
    var plm = this.GetPageLayoutMgr();

    var theDataObj = startNode.GetDataObj();
    const isPositions = theDataObj.IsPositionObj();
    var layout = startNode.GetLayout();
    var startSubChartID = scm.FindSubChartSubID(theDataObj.GetObjectID(), cursubchartID);
    if (startSubChartID > -1) {
      var startSubChart = scm.FindSubChart(startSubChartID);
      //I have my own subchart, so I need to move to my new home.
      var dropSubChart = scm.FindSubChart(targetSCID);
      var curParent = scm.FindSubChart(cursubchartID);
      curParent.RemoveChild(startSubChart);
      dropSubChart.AppendChildSubChart(startSubChart);
      scm.IncChangeCount();
      //scm.fSCMJournal.SubChartListChanged(scm.fSubChartList);
    }
    layout.SetSubChartID(targetSCID);

    if (isPositions) {
      scm.RemoveSCNode(theDataObj.GetObjectID(), cursubchartID);
      theDataObj.fHolderList?.forEach(holder => {
        scm
          .FindSCSubNodesFromDataObj(holder)
          .filter(n => n.fLayout.fSubChartID === cursubchartID)
          .forEach(node => {
            node.fLayout.SetSubChartID(targetSCID);
          });
      });
    }

    // Positions boxes removal logic is handled elsewhere
    if (!isPositions) this.PRemoveChartBox(startNode.fChartBox);

    var n = startNode.GetNumChildren();
    for (var i = 0; i < n; i++) {
      var childNode = plm.GetIndexChildNode(startNode, i);
      this.MoveBranchToSubChart(childNode, cursubchartID, targetSCID);
    }
  }

  DoOrphanSelection(selectedBoxes, deepRemove) {
    if (selectedBoxes == null) return;

    var plm = this.GetPageLayoutMgr();
    var numBoxes = selectedBoxes.length;

    for (var i = 0; i < numBoxes; i++) {
      var theElement = selectedBoxes[i];
      this.DoRemoveFromMRBGroup(theElement);
      var theNode = this.GetPLMNode(theElement);
      plm.RemoveNode(theNode, deepRemove);
      this.PRemoveChartBox(theElement);
    }
  }

  AdjustBranchStyle(delNode) {
    var delObj = delNode.GetDataObj();
    var plm = this.GetPageLayoutMgr();
    var numChildren = delObj.GetNumChildren();
    var dbObjParent = delObj.GetParent();

    if (numChildren > 0) {
      var bStyle = plm.DetermineBranchStyle(dbObjParent, null);
      for (var i = 0; i < numChildren; i++) {
        var childNode = delNode.GetIndexChild(i);
        plm.TagAutoSmartGroup(childNode);
        if (!childNode.IsAssistant()) childNode.SetBranchStyle(bStyle);
      }
    }
  }

  IsTopOfSubChart(theElement) {
    var scm = this.GetSubChartMgr();
    var dbObjID = theElement.GetDataObjID();
    return scm.IsTopOfSubChart(dbObjID);
  }

  DoDeleteRoot(theElement, selectedRoots) {
    // Has to be the root of the chart (not the root of the subchart)
    var theNode = this.GetPLMNode(theElement);
    var parentLayout = theNode.GetLayout();
    var delObj = theNode.GetDataObj();
    if (delObj == null) return false;

    var numChildren = delObj.GetNumChildren();
    var dbObjParent = delObj.GetParent();
    if (numChildren != 1) return false;
    if (dbObjParent != null) return false;
    var scFlag = parentLayout.GetSubChartFlag();
    if (scFlag == enumSubChartFlag.drilldown) return false;

    var childObj = delObj.GetIndexChild(0);
    var plm = this.GetPageLayoutMgr();
    var scm = this.GetSubChartMgr();
    var childNode = plm.FindNodeFromDataObjID(childObj.GetObjectID());
    var childLayout = childNode.GetLayout();
    childObj.SetParent(null, -1);

    var currentSubChart = scm.GetCurrentSubChart();
    currentSubChart.SetTopDBObj(childObj);

    selectedRoots.push(childNode);

    // Make sure the branch style and centering are set correctly
    childLayout.TransferBranchStyle(parentLayout);
    childLayout.CopyCentering(parentLayout);
    plm.TagAutoSmartGroup(childNode);

    // Clean out the root
    plm.RemoveNode(theNode, true);

    // Finally mark the dbobject as non-persistent and remove link to hierarchy
    delObj.SetParent(null, -1);
    delObj.SetPersistent(false);

    // Remove the UI elements
    this.PRemoveChartBox(theElement);

    this.RecalcChartLayoutForRoots(selectedRoots);
    return true;
  }

  PDeleteBox(theElement, selectedRoots) {
    var theNode = this.GetPLMNode(theElement);
    if (theNode == null)
      // Object already deleted (for example MRB selected with a contained box selected
      return;
    var delObj = theNode.GetDataObj();
    var plm = this.GetPageLayoutMgr();

    // Text Object or image - no db reference
    if (delObj == null) {
      var mrbRemove = this.DoRemoveMRB(theElement, selectedRoots);
      if (!mrbRemove) {
        plm.RemoveNode(theNode, true);
        this.PRemoveChartBox(theElement);
      }
      return true;
    }

    var numChildren = delObj.GetNumChildren();
    var dbObjParent = delObj.GetParent();

    // Can't delete the bottom half of a subchart break (or the root of the chart)
    var bottomBreak = theElement.BottomOfSubChartBreak(true);
    if (bottomBreak) {
      var canDelete = false;
      var isTopOfSC = this.IsTopOfSubChart(theElement);
      if (!isTopOfSC) {
        // We can delete the root of a chart that is not the main chart
        if (numChildren == 0) canDelete = true;
      }

      if (!canDelete) return false;
    }

    var scFlag = theElement.GetSubChartFlag();
    if (scFlag == enumSubChartFlag.drillup) return false;

    // Capture root of charts that are getting delete action
    this.AddSelectedRoot(selectedRoots, theElement);

    // Fix the branch of children because we are going to attach to a new parent
    var topBreak = theElement.TopOfSubChartBreak();
    if (!topBreak) this.AdjustBranchStyle(theNode);

    this.DoRemoveFromMRBGroup(theElement);

    // Remove the node (deep remove from both current subchart and master node list)
    this.AutoFormat(theNode.GetParent());
    plm.TagAutoSmartGroup(theNode);
    plm.RemoveNode(theNode, true);

    // We might be removing a root node and therefore we don't want to include the recalc layout step
    var indexRoot = selectedRoots.indexOf(theNode);
    if (indexRoot != -1) selectedRoots.splice(indexRoot, 1);

    if (topBreak) {
      // Remove all elements below subchart break
      plm.RemoveSubChartBreak(theNode, false);

      var scm = this.GetSubChartMgr();
      scm.RemoveChildren(delObj);
    } else if (numChildren > 0) {
      // Move children to parent of node being deleted
      if (dbObjParent != null) dbObjParent.AdoptChildren(delObj, this.fOrphanList);
    }

    // Finally mark the dbobject as non-persistent and remove link to hierarchy
    delObj.SetParent(null, -1);
    delObj.SetPersistent(false);

    // Remove the UI elements
    this.PRemoveChartBox(theElement);

    return true;
  }

  HandleOrphanList() {
    var l = this.fOrphanList;
    var n = l.length;
    var subList = [];
    for (var i = 0; i < n; i++) {
      var o = l[i];
      if (o.IsPersistent()) subList.push(o);
    }

    n = subList.length;
    var subList2 = [];
    for (var i = 0; i < n; i++) {
      var o = subList[i];
      if (!ParentInChain(subList, o)) subList2.push(o);
    }

    var n = subList2.length;
    for (var i = 0; i < n; i++) {
      var o = subList2[i];
      this.fDragSource = this.GetChartBoxFromDBObj(o);
      this.OrphanBranch(i == n - 1);
    }

    var parkingPanel = this.fWSMgr.GetTabPanel("Parking");
    parkingPanel.SetLevelToTop();
    parkingPanel.UpdatePanel(null);
  }

  DoDeleteSelection(selectedBoxes, ctrlKey) {
    if (selectedBoxes == null) return;

    this.fOrphanList = null;
    if (ctrlKey) this.fOrphanList = [];

    var selectedRoots = [];
    var numBoxes = selectedBoxes.length;
    if (numBoxes == 1) {
      if (this.DoDeleteRoot(selectedBoxes[0], selectedRoots)) return;
    }

    var retryList = [];
    for (var i = 0; i < numBoxes; i++) {
      var theElement = selectedBoxes[i];
      var legal = this.PDeleteBox(theElement, selectedRoots);
      if (!legal) retryList.push(theElement);
    }

    var numBoxes = retryList.length;
    for (var i = 0; i < numBoxes; i++) {
      var theElement = retryList[i];
      this.PDeleteBox(theElement, selectedRoots);
    }

    if (this.fOrphanList) this.HandleOrphanList();

    this.RecalcChartLayoutForRoots(selectedRoots);
  }

  RollUpCheck(isCut) {
    const selectedBoxes = this.GetSelectedBoxes(true) ?? [];

    // OCN5-6486: count boxes with subordinates inline OR in a drilldown (inline were missed).
    const hasSubs = box =>
      box.GetLayout().GetSubChartFlag() == enumSubChartFlag.drilldown ||
      (box.GetDataObj()?.GetNumChildren() ?? 0) > 0;
    const nBoxesWithSubs = selectedBoxes.filter(hasSubs).length;
    const firstBoxWithSubs = selectedBoxes.find(hasSubs);

    let cmdParams = { isCut: isCut, chartBox: null, chartBoxes: selectedBoxes };
    let alertParams;

    // If Position Document, and there are boxes with subordinates, the message is more detailed (OCN5-4964)
    if (this.fDoc.IsPositionDocument() && nBoxesWithSubs > 0) {
      const multipleRollups = nBoxesWithSubs > 1;
      const firstDataObjTitle = firstBoxWithSubs.GetDataObj().SmartGetFieldValue(kPositionTitleID);

      alertParams = {
        header: appstr("Rollup or delete subordinate positions"),
        message:
          appstr(
            multipleRollups
              ? "Multiple parent positions rollup alert"
              : "Would you like to rollup or delete the subordinates for {position}?",
            { position: firstDataObjTitle }
          ) +
          "\n\n" +
          appstr("Employees in deleted position will be unassigned in the Employee List."),
        confirm: appstr("Rollup subordinates"),
        dismiss: appstr("Delete subordinates")
      };
    } else if (nBoxesWithSubs === 1) {
      cmdParams.chartBox = firstBoxWithSubs;
      alertParams = {
        header: appstr("Alert"),
        message: appstr("What would you like to do with the subordinates of this manager?"),
        confirm: appstr("Roll Up"),
        dismiss: appstr("Move to Staging")
      };

      if (isCut) {
        alertParams = {
          header: appstr("Alert"),
          message: appstrp("Do you want to roll up the subordinates from other pages", "?"),
          confirm: null,
          dismiss: null
        };
      }
    }

    if (alertParams) {
      new QAlert().InitConfirmAlert(
        alertParams.header,
        alertParams.message,
        this,
        this.fWSMgr,
        "doRollUp",
        "ForceDeleteBranch",
        alertParams.confirm,
        alertParams.dismiss,
        cmdParams,
        cmdParams
      );

      return false;
    }

    return true;
  }

  ValidateCut(rollUpCheck) {
    const doc = this.fDoc;
    if (!doc.IsWritable() || !doc.CanCurrentUserEdit()) {
      return false;
    }

    if (rollUpCheck != false)
      // If no argument is passed, defaults to validating.
      return this.RollUpCheck(true);
    return true;
  }

  async DeleteSelection(
    ignoreAlert,
    options = { ignoreCheckPoint: false, transferSubsToStaging: false, rollupSubs: false }
  ) {
    // Delete boxes selected on canvas as well as create corresponding overrides.

    if (!ignoreAlert && !this.RollUpCheck())
      // ignore alert true -> Skip rollup check. The rollup check will generate the alert.
      return;

    var wsMgr = this.fWSMgr;
    var backgroundMgr = wsMgr.fBackgroundMgr;
    var doc = wsMgr.GetDoc();
    const isPositionDoc = doc.IsPositionDocument();

    wsMgr.StartCheckPoint();

    //var refocus = false;

    var selectedBoxes = this.GetSelectedBoxes(true);

    // Capture parent subchart in case we delete the current subchart's topbox.
    const scm = this.GetSubChartMgr();
    const curSubChart = scm.GetCurrentSubChart();
    const parentSubChart = curSubChart?.GetSubChartParent() ?? null;
    const curTopDBObj = curSubChart?.GetTopDBObj() ?? null;
    const isDeletingTopbox =
      parentSubChart != null &&
      curTopDBObj != null &&
      selectedBoxes?.some(box => box.GetDataObj() === curTopDBObj);

    const holdersToUnassign = [];
    const positionsToDelete = [];
    for (var box of selectedBoxes) {
      const boxType = box.GetDataObj()?.InferObjectType();
      var elementLayer = box.GetLayer();

      // data overrides for deletes are tagged and filtered out (like filtering)
      if (box.ShouldDoOverrides()) {
        var dbObj = box.GetDataObj();

        // Delete holder is equivalent to unassigning it.
        const isHolderBox = isPositionDoc && boxType === enumObjectTypes.holder;
        if (isHolderBox) {
          holdersToUnassign.push(dbObj);
          continue;
        }

        if (isPositionDoc) {
          holdersToUnassign.push(...(dbObj.GetHolderList() ?? []));
        }

        const superFieldDef = dbObj.GetRecordParentIDFieldDef();
        const superKey = superFieldDef.GetFieldName();

        if (options.transferSubsToStaging) {
          if (isPositionDoc) {
            const children = dbObj.GetEverySubordinate(false, false);
            children.forEach(childPosition => {
              holdersToUnassign.push(...childPosition.GetHolderList());
              positionsToDelete.push(childPosition);
            });
          } else {
            const children = [...dbObj.GetChildListNoFilter(false)];
            doc.ResolveSubordinatesDeletedParent(dbObj);

            wsMgr.SetSidebarTool("staging");
            wsMgr.fSetHighlightedRecordIds?.(children.map(c => c.GetObjectRecordID()));
          }
        } else if (options.rollupSubs) {
          const parentObj = dbObj.GetParent();
          if (!isPositionDoc && box.GetLayout().GetSubChartFlag() === enumSubChartFlag.drilldown) {
            this.RemoveSubChartBreak();
          }

          if (parentObj) {
            const parentID = parentObj.GetObjectRecordID();
            [...dbObj.GetChildListNoFilter(true)].forEach(child => {
              const change = { data: { [superKey]: parentID } };
              if (isPositionDoc) {
                if (child.IsDeleted()) return;
                doc.SetBoxOverride(child, enumOverrideType.move, change);
                doc.MoveBranch(child, parentObj);
              } else if (child.IsDeleted()) {
                if (scm.FindSCSubNodesFromDataObj(child).length > 0) {
                  doc.SetBoxOverride(child, enumOverrideType.move, change);
                }
              } else {
                this.DoMoveBranch(enumDropType.subordinate, child, parentObj, null, true);
              }
            });
          }
        }

        if (isPositionDoc) {
          positionsToDelete.push(dbObj);
        } else {
          doc.SetBoxOverride(dbObj, enumOverrideType.delete);
        }
        this.DoSelectElement(box, false);
      } else if (elementLayer === enumElementLayer.master) {
        var theNode = this.GetPLMNode(box);
        var parentLayout = theNode.GetLayout();
        var objId = parentLayout.GetDBObjID();
        backgroundMgr.RemoveLayout(objId);
        // need fallback handling here?
      } else {
        // everything else is deleted directly here (background elements, etc)
        this.DoDeleteSelection(selectedBoxes, false);
      }
    }

    if (holdersToUnassign.length > 0) {
      await OverrideFactory.UnassignHolders(holdersToUnassign);
    }

    // If we delete the boxes before the top box, the undo breaks the subchart.
    // Also, unassigning holders from deleted positions can cause unpredictable behavior.
    positionsToDelete.forEach(position => {
      doc.SetBoxOverride(position, enumOverrideType.delete);
    });

    wsMgr.EndCheckPoint();

    if (isDeletingTopbox) {
      this.SwitchToSubChartByObj(parentSubChart);
    } else {
      wsMgr.ReloadCurrentSubChart(false);
    }
    wsMgr.PrepRenderWS();
    wsMgr.RenderWS();
  }

  PGetParent(dbObj) {
    var p = dbObj.GetParent();
    if (p == null) return null;

    var plm = this.GetPageLayoutMgr();
    var node = plm.FindNodeFromDataObjID(p.GetObjectID());
    if (node == null) return null;

    return p;
  }

  PGetNumChildren(dbObj) {
    var n = dbObj.GetNumChildren();
    if (n == 0) return 0;

    var plm = this.GetPageLayoutMgr();
    var child = dbObj.GetIndexChild(0);
    var node = plm.FindNodeFromDataObjID(child.GetObjectID());
    if (node == null) return 0;

    return n;
  }

  GoRight(dbObj) {
    var parentDBObj = this.PGetParent(dbObj);
    if (parentDBObj == null) return dbObj;
    var index = parentDBObj.IndexOfChild(dbObj) + 1;
    var n = this.PGetNumChildren(parentDBObj);
    if (index < n) return parentDBObj.GetIndexChild(index);
    else return this.GoRight(parentDBObj);
  }

  FindNextDBObj(dbObj) {
    // Go Down to leftmost node
    if (this.PGetNumChildren(dbObj) > 0) return dbObj.GetIndexChild(0);

    return this.GoRight(dbObj);
  }

  RecurseRight(dbObj) {
    var n = this.PGetNumChildren(dbObj);
    if (n > 0) {
      var rChild = dbObj.GetIndexChild(n - 1);
      return this.RecurseRight(rChild);
    } else return dbObj;
  }

  FindPrevDBObj(dbObj) {
    var parentDBObj = this.PGetParent(dbObj);
    if (parentDBObj == null) return this.RecurseRight(dbObj);

    var index = parentDBObj.IndexOfChild(dbObj) - 1;
    if (index >= 0) {
      var leftSib = parentDBObj.GetIndexChild(index);
      var n = this.PGetNumChildren(leftSib);
      if (n > 0) return leftSib.GetIndexChild(n - 1);
      else return leftSib;
    }

    return parentDBObj;
  }

  FindNextBox(currentBox, forward) {
    var dbObj = currentBox.GetDataObj();
    if (dbObj == null) return currentBox;

    var nextDBObj;
    if (forward) nextDBObj = this.FindNextDBObj(dbObj);
    else nextDBObj = this.FindPrevDBObj(dbObj);
    var nextBox = this.GetChartBoxFromDBObj(nextDBObj);

    return nextBox;
  }

  SelectNextBox(currentBox, forward) {
    var nextBox = this.FindNextBox(currentBox, forward);
    if (!nextBox) return currentBox;

    this.ClearSelection();
    this.DoSelectElement(nextBox, true);
    return nextBox;
  }

  EditFirstCell(thebox) {
    this.ClearSelection();
    var firstCell = thebox.GetFirstEditableCell();
    if (firstCell) this.DoSelectElement(firstCell, true);

    return firstCell;
  }

  // Version 2.0.2
  TabToNextElement(forward, targetElement, wsMgr) {
    var selectedBox = this.GetFirstSelectedBox(true);
    if (selectedBox != null) {
      this.SelectNextBox(selectedBox, forward);
      return true;
    }

    var selectedCell = this.GetFirstSelectedElement(enumChartElement.boxCell);
    if (selectedCell != null) {
      this.ClearSelection();
      var parentBox = selectedCell.GetParentBox();
      var nextCell = parentBox.FindNextCell(selectedCell, forward);
      if (nextCell != null) this.DoSelectElement(nextCell, true);
      else {
        var nextBox = this.SelectNextBox(parentBox, forward);
        this.ClearSelection();
        var nextCell = null;
        if (forward) nextCell = nextBox.GetFirstCell();
        else nextCell = nextBox.GetLastCell();
        if (nextCell == null) nextCell = parentBox;

        this.DoSelectElement(nextCell, true);
      }
      return true;
    }

    return false;
  }

  PassHandleKeyboard(keyInfo, wsMgr) {
    var topTab = wsMgr.GetTopTab();
    if (topTab === "Subcharts") {
      var KeyBoardPanel = this.fWSMgr.GetTabPanel("Subcharts");
      KeyBoardPanel.PassHandleKeyboard(keyInfo);
    } else if (topTab === "Shortcuts") {
      KeyBoardPanel = this.fWSMgr.GetTabPanel("Shortcuts");
      KeyBoardPanel.PassHandleKeyboard(keyInfo);
    }
  }

  SelectEntryBoxForKeyboard() {
    if (this.GetNumSelectedElements() > 0) return false;
    const topBox = this.GetPageLayoutMgr().GetRootNodeForSubChart()?.GetChartBox();
    if (!topBox) return false;
    this.SelectBoxForKeyboard(topBox);
    return true;
  }

  SelectBoxForKeyboard(box) {
    this.RememberKeyboardRefocus(box);
    this.fKeyboardEntered = false;
    this.fKeyboardFocusDrill = null;
    this.ClearSelection();
    this.DoSelectElement(box, true);
    if (!this.ElementInViewPort(box, true)) {
      this.PanToObject(box.GetPositionX(), box.GetPositionY(), box.GetWidth(), box.GetHeight());
    }
    this.AnnounceSelectedBox();
  }

  // Left/Right try the centre-line ray first; holders only via their position; wraps at the edges.
  FindBoxInDirection(current, keyCode, wrapLead = null) {
    const vertical = keyCode === kUpArrow || keyCode === kDownArrow;
    const sign = keyCode === kDownArrow || keyCode === kRightArrow ? 1 : -1;
    const fromX = current.GetPositionX();
    const fromY = current.GetPositionY();
    const fromLead = wrapLead ?? (vertical ? fromY : fromX);
    const fromEndX = fromX + current.GetWidth();
    const fromEndY = fromY + current.GetHeight();
    const rayY = (fromY + fromEndY) / 2;
    const currentData = current.GetDataObj?.();
    const currentOwner = currentData?.GetParentPosition();
    const currentPosition = currentOwner ?? currentData;
    const canEnterHolders = !!currentOwner || keyCode === kDownArrow;
    const drillOwner = vertical && (currentOwner ? this.GetPositionBox(current) : current);
    const ownDrill = drillOwner?.IsDrillActive?.() ? drillOwner.fSubChartDrill : null;

    let rayHit = null;
    let rayAlong = Infinity;
    let rayHitY = Infinity;
    let best = null;
    let bestScore = Infinity;
    let bestPerpendicular = Infinity;
    let minLead = Infinity;
    let maxLead = -Infinity;
    const numElements = this.GetNumElements();
    for (let i = 0; i < numElements; i++) {
      const el = this.fElements[i];
      if (el !== ownDrill) {
        const data = el !== current && this.IsElementChartBox(el, false) ? el.GetDataObj() : null;
        if (!data) continue;
        const owner = data.GetParentPosition();
        if (owner && (owner !== currentPosition || !canEnterHolders)) continue;
      }

      const x = el.GetPositionX();
      const y = el.GetPositionY();
      const lead = vertical ? y : x;
      if (lead < minLead) minLead = lead;
      if (lead > maxLead) maxLead = lead;
      const along = sign * (lead - fromLead);
      if (along <= 0) continue;

      if (!vertical && y <= rayY && rayY <= y + el.GetHeight()) {
        if (along < rayAlong || (along === rayAlong && y < rayHitY)) {
          rayHit = el;
          rayAlong = along;
          rayHitY = y;
        }
        continue;
      }
      if (rayHit || along > bestScore) continue;

      const across = vertical
        ? rangeGap(fromX, fromEndX, x, x + el.GetWidth())
        : rangeGap(fromY, fromEndY, y, y + el.GetHeight());
      const score = along + kOffAxisWeight * across;
      const perpendicular = vertical ? x : y;
      if (score < bestScore || (score === bestScore && perpendicular < bestPerpendicular)) {
        best = el;
        bestScore = score;
        bestPerpendicular = perpendicular;
      }
    }

    const next = rayHit ?? best;
    if (next || wrapLead !== null || minLead > maxLead) return next;
    return this.FindBoxInDirection(current, keyCode, sign > 0 ? minLead - 1 : maxLead + 1);
  }

  DrillBox(box) {
    const arrow = box?.fSubChartDrill;
    if (!arrow || !box.IsDrillActive()) return false;

    this.RememberKeyboardRefocus(box);
    this.fWSMgr.ExecuteCommand(this.fWSMgr.AssembleCommand(arrow, this, null));
    return true;
  }

  DrillSelectedBox(down) {
    const box = this.GetFirstSelectedBox(false);
    const wantedFlag = down ? enumSubChartFlag.drilldown : enumSubChartFlag.drillup;
    return box?.GetSubChartFlag() === wantedFlag && this.DrillBox(box);
  }

  // The owning box stays selected while its drill triangle has keyboard focus.
  FocusDrillForKeyboard(arrow) {
    const owner = arrow.GetAssociatedBox();
    if (owner !== GetToolbarTargetBox(this)) this.SelectBoxForKeyboard(owner);
    this.fKeyboardFocusDrill = arrow;
    if (!this.ElementInViewPort(arrow, true)) {
      this.PanToObject(
        arrow.GetPositionX(),
        arrow.GetPositionY(),
        arrow.GetWidth(),
        arrow.GetHeight()
      );
    }
    const drillsUp = owner.GetSubChartFlag() === enumSubChartFlag.drillup;
    this.fWSMgr.BroadcastTopic("canvasAnnounce", appstr(drillsUp ? "Drill up" : "Drill down"));
  }

  GetKeyboardFocusDrill() {
    const arrow = this.fKeyboardFocusDrill;
    return arrow && arrow.GetAssociatedBox() === GetToolbarTargetBox(this) ? arrow : null;
  }

  // Kept by record, not box: page switches and saves rebuild every box, then re-select it.
  RememberKeyboardRefocus(box) {
    const dbObj = box.GetDataObj();
    this.fKeyboardNavActive = true;
    this.fKeyboardRefocus = { recordId: dbObj?.GetObjectRecordID(), dbObjId: dbObj?.GetObjectID() };
  }

  IsKeyboardFocusOnly(box) {
    return this.fKeyboardNavActive && !this.fKeyboardEntered && GetToolbarTargetBox(this) === box;
  }

  ForgetKeyboardSelection() {
    this.fKeyboardNavActive = false;
    this.fKeyboardFocusDrill = null;
    this.fKeyboardRefocus = null;
  }

  RestoreKeyboardSelection() {
    const refocus = this.fKeyboardRefocus;
    if (!refocus) return false;

    this.fKeyboardFocusDrill = null;
    this.ClearSelection();
    if (!this.FocusObject(refocus)) this.SelectEntryBoxForKeyboard();
    this.AnnounceSelectedBox();
    return true;
  }

  ReleaseKeyboardSelection() {
    if (!this.fKeyboardNavActive || this.fChartToolbarOpen) return false;
    this.ForgetKeyboardSelection();
    this.ClearSelection();
    this.AnnounceSelectedBox();
    return true;
  }

  // Mirrors the app's focus outline (2px brand colour, just outside), at a constant screen size.
  RenderKeyboardFocus(ctx) {
    if (!this.fKeyboardNavActive || Globals._publishRef) return;
    const box = GetToolbarTargetBox(this);
    if (!box) return;

    const zoom = this.fDisableHiResScaling ? 1 : this.fScaleFactor;
    const width = kFocusRingWidth / zoom;
    const inset = kFocusRingGap / zoom + width / 2;
    const drill = this.GetKeyboardFocusDrill();
    const bounds = drill
      ? {
          fLeft: drill.GetPositionX(),
          fTop: drill.GetPositionY(),
          fRight: drill.GetPositionX() + drill.GetWidth(),
          fBottom: drill.GetPositionY() + drill.GetHeight()
        }
      : box.GetBoundingRect(false);

    ctx.save();
    ctx.strokeStyle = uFocusRingColor;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.roundRect(
      bounds.fLeft - inset,
      bounds.fTop - inset,
      bounds.fRight - bounds.fLeft + inset * 2,
      bounds.fBottom - bounds.fTop + inset * 2,
      inset
    );
    ctx.stroke();
    ctx.restore();
  }

  AnnounceSelectedBox() {
    const box = GetToolbarTargetBox(this);
    this.fWSMgr.BroadcastTopic("canvasAnnounce", box ? box.GetAccessibleDescription() : "");
  }

  // Returns undefined to fall through to the legacy handling in PanelHandleKeyboard.
  KeyboardNavHandleKeyboard(keyInfo) {
    if (keyInfo.charCode === kTabKey) {
      if (keyInfo.shiftKey || !this.fChartToolbarOpen) return false;
      this.fWSMgr.BroadcastTopic("canvasToolbarFocus");
      return true;
    }

    const isArrow = isArrowKey(keyInfo.keyCode);
    const isVertical = keyInfo.keyCode === kUpArrow || keyInfo.keyCode === kDownArrow;
    if (isArrow && (keyInfo.ctrlKey || (keyInfo.altKey && !isVertical))) return false;

    const isActivate = keyInfo.charCode === kReturnKey || keyInfo.charCode === kSpaceCharCode;
    if (isActivate || isArrow) this.fKeyboardNavActive = true;

    if (isActivate) {
      const drill = this.GetKeyboardFocusDrill();
      if (drill) {
        this.DrillBox(drill.GetAssociatedBox());
        return true;
      }
      if (!GetToolbarTargetBox(this)) return this.SelectEntryBoxForKeyboard();
      this.fKeyboardEntered = true;
      ShowChartBoxToolbar(this);
      if (this.fChartToolbarOpen) this.fWSMgr.BroadcastTopic("canvasToolbarFocus");
      return true;
    }

    if (!isArrow) return undefined;

    if (keyInfo.altKey) {
      this.DrillSelectedBox(keyInfo.keyCode === kDownArrow);
      return true;
    }

    if (this.SelectEntryBoxForKeyboard()) return true;

    const current =
      this.GetKeyboardFocusDrill() ?? GetToolbarTargetBox(this) ?? this.GetFirstSelectedBox(false);
    const next = current && this.FindBoxInDirection(current, keyInfo.keyCode);
    if (!next) return true;
    if (this.IsElementChartBox(next, false)) this.SelectBoxForKeyboard(next);
    else this.FocusDrillForKeyboard(next);
    return true;
  }

  PanelHandleKeyboard(keyInfo, targetElement, wsMgr) {
    if (Globals._uiConfig.InChartKeyboardNavMode()) {
      const navHandled = this.KeyboardNavHandleKeyboard(keyInfo);
      if (navHandled !== undefined) return navHandled;
    }

    var handled = true;
    //var keyCode = keyInfo.keyCode;
    var charCode = keyInfo.charCode;
    var ctrlKey = keyInfo.ctrlKey;
    var keyCode = keyInfo.keyCode;

    var doTab = false;
    var tabForward = true;
    var roMode = Globals._accountInfo.ReadOnlyMode();

    switch (charCode) {
      case kBackspaceKey:
      case kDeleteKey: // ObjectShortCut:
        if (!roMode) this.DeleteSelection(false);
        break;
      case kReturnKey:
        if (targetElement) {
          var selectIt = !(targetElement instanceof QChartBoxCell);
          this.DoSelectElement(targetElement, selectIt);
          break;
        }
      // intentional fall thru
      case kTabKey:
        doTab = true;
        tabForward = !keyInfo.shiftKey;
        break;
      case kSelectAllShortCut:
        if (ctrlKey) this.SelectAll();
        else handled = false;
        break;
      default:
        handled = false;
        break;
    }

    if (!handled) {
      handled = true;
      switch (keyCode) {
        case kLeftArrow:
        case kUpArrow:
        case kRightArrow:
        case kDownArrow:
          doTab = true;
          tabForward = keyCode == kRightArrow || keyCode == kDownArrow;
          break;
        default:
          handled = false;
          break;
      }
    }

    if (doTab) {
      var didTab = this.TabToNextElement(tabForward, targetElement, wsMgr);
      if (!didTab) this.PassHandleKeyboard(keyCode, wsMgr);
    }

    return handled;
  }

  DoIncludeElementInSelection(theElement) {
    var isSelectable = theElement.IsSelectable();

    if (!isSelectable) return false;

    var elementType = theElement.GetElementType();
    var includeIt = false;
    switch (elementType) {
      case enumChartElement.connectingLine:
      case enumChartElement.chartBox:
      case enumChartElement.textBox:
        includeIt = true;
        break;
      case enumChartElement.boxCell:
        includeIt = Globals._uiConfig.InBackgroundEditorMode();
        break;
    }

    return includeIt;
  }

  CustomShiftSelect(targetElement) {
    var toSelectType = targetElement.GetElementType();
    if (toSelectType == enumChartElement.boxCell && !targetElement.IsSelected()) {
      var parentBox = targetElement.GetParentBox();
      var parentSelected = parentBox.IsSelected();
      if (parentSelected) {
        this.DoSelectElement(parentBox, false);
        return true;
      }
    }

    return false;
  }

  AllowSelect(elementToSelect) {
    var selectedElement = this.GetFirstSelectedElement(null);
    if (selectedElement == null || elementToSelect == selectedElement) return true;

    var selectType = selectedElement.GetElementType();
    var toSelectType = elementToSelect.GetElementType();

    var allowSelect = false;
    switch (selectType) {
      case enumChartElement.boxCell:
        if (selectedElement.InEditMode()) selectedElement.ExitEditMode();
        if (toSelectType == enumChartElement.boxCell) allowSelect = true;
        break;
      case enumChartElement.connectingLine:
      case enumChartElement.chartBox:
      case enumChartElement.textBox:
        if (
          toSelectType == enumChartElement.connectingLine ||
          toSelectType == enumChartElement.chartBox ||
          toSelectType == enumChartElement.textBox
        )
          allowSelect = true;
        break;
    }

    return allowSelect;
  }

  IsMainPanel() {
    return true;
  }

  SupportsZoom() {
    return true;
  }

  //=================================================================================================
  // Command Handling
  UndoRedoTextEdit(doUndo) {
    var selectedCell = this.GetFirstSelectedElement(enumChartElement.boxCell);
    if (selectedCell && selectedCell.InEditMode()) {
      if (doUndo) selectedCell.Undo();
      else selectedCell.Redo();
      return true;
    }

    return false;
  }

  ExecuteCommand(aCmd) {
    var theCode = aCmd.GetCmdCode();
    var handled = true;

    if (theCode) {
      var cmdProcessor = theCode.split("-");
      if (cmdProcessor.length > 1 && cmdProcessor != theCode) {
        theCode = cmdProcessor.shift();
        var subCode = cmdProcessor.join("-");
      }
    }

    switch (theCode) {
      case "CollapseBranch":
        var theBox = src.GetAssociatedBox();
        this.CollapseBranch(theBox);
        break;
      case "ChgMinMax":
        break;
      case "UpdateAutoText":
        var src = aCmd.GetSourcePanel();
        var target = src.fBackgroundPanel;
        var curBox = target.fSrcElement;
        if (!curBox || (curBox && !(curBox instanceof QChartBox))) return;
        var autoTextType = kAutoTextStrs.indexOf(subCode);
        var layout = curBox.GetLayout();
        var c1 = layout.GetCellDesc(1, 1);
        c1.SetAutoTextType(autoTextType);
        // recalc done during chart box prep render so no need to recalc layout here
        this.DoSelectElement(curBox, true);
        break;
      case "UploadNewFile":
        var w = this.fWSMgr;
        var selectedImage = this.GetFirstSelectedElement(null);
        aCmd["dropTarget"] = selectedImage;
        aCmd["directory"] = "Masters";
        aCmd["fileTypes"] = ["jpg", "png"];
        aCmd["skipFM"] = true;
        w.UploadFile(aCmd);
        break;
      //case "CanvasRightClick":
      //    handled = this.fWSMgr.DoExecuteCommand(aCmd);
      //    break;
      default:
        handled = false;
        break;
    }

    return handled;
  }

  ExecuteAlertCommand(aCmd) {
    var theCode = aCmd.GetCmdCode();
    var handled = this.fWSMgr.HandleChangeDoc(theCode);
    var wsMgr = this.fWSMgr;
    if (!handled) {
      var src = aCmd.GetSourceElement();
      var isCut = aCmd["isCut"];
      var chartBox = aCmd["chartBox"];
      if (chartBox) {
        this.DoSelectElement(chartBox, true);
      }
      var handled = true;
      var tempIgnoreCheckpoint;
      let transferSubsToStaging = false;
      let rollupSubs = false;
      //var portPanel = this.fPortPanel;
      switch (theCode) {
        case "ForceDeleteBranch":
          transferSubsToStaging = true;
          tempIgnoreCheckpoint = false;
          break;
        case "doRollUp":
          tempIgnoreCheckpoint = true;
          rollupSubs = true;
          break;
        default:
          handled = false;
          break;
      }
      if (handled) {
        // Handled -> Should Delete
        if (isCut) wsMgr.DoCutCopy(null, false);

        this.DeleteSelection(true, { tempIgnoreCheckpoint, transferSubsToStaging, rollupSubs });
        if (tempIgnoreCheckpoint) wsMgr.EndCheckPoint();
      }
    }
    wsMgr.RenderWS();
    return handled;
  }

  GetTreeForBox(aBox) {
    var plm = this.GetPageLayoutMgr();
    var numNodes = plm.GetNumNodes();

    var rootNode = this.GetPLMRootNode(aBox);

    var theList = [];
    for (var i = 0; i < numNodes; i++) {
      var theNode = plm.GetIndexNode(i);
      var curRoot = this.GetPLMRootNode(theNode);
      if (rootNode == curRoot) {
        if (theNode.IsPositionsNode()) {
          var holderNodes = plm.GetHolderNodes(theNode);
          holderNodes.forEach(holder => theList.push(holder.fChartBox));
        }

        theList.push(theNode.fChartBox);
      }
    }

    return theList;
  }

  //=================================================================================================
  // Page Size
  GetMinPageWidth() {
    // Page Width in inches
    return this.fPageSizeX;
  }

  GetMinPageHeight() {
    // Page Height in inches
    return this.fPageSizeY;
  }

  //=================================================================================================
  TagSelectedIntoAutoSmartGroup() {
    var selectedBoxes = this.GetSelectedBoxes(false);
    var plm = this.GetPageLayoutMgr();

    for (var i in selectedBoxes) {
      var aNode = this.GetPLMNode(selectedBoxes[i]);
      plm.TagAutoSmartGroup(aNode);
    }
  }

  CheckPeerStyles(branchStyle) {
    // Assumption that selection has been extended to all peers (under a manager)
    // Make sure that there is not more than one peer assistant left or right
    var selectedBoxes = this.GetSelectedBoxes(false);
    var n = selectedBoxes.length;
    var parentObjList = new Array();
    for (var i = 0; i < n; i++) {
      var aBox = selectedBoxes[i];
      var dataObject = aBox.GetDataObj();
      var parentObject = dataObject.GetParent();
      var index = parentObjList.indexOf(parentObject);
      if (index == -1) {
        parentObjList.push(parentObject);
        if (parentObject != null) {
          var pBox = this.GetChartBoxFromDBObj(parentObject);
          if (!ValidPeerSub(pBox, branchStyle)) return false;
        }
      } else return false;
    }

    // Now check to see if there is already a right or left peer assistant
    n = parentObjList.length;
    for (var i = 0; i < n; i++) {
      var p = parentObjList[i];
      if (p != null) {
        var numChildren = p.GetNumChildren();
        for (var j = 0; j < numChildren; j++) {
          var childObj = parentObject.GetIndexChild(j);
          var aBox = this.GetChartBoxFromDBObj(childObj);
          if (aBox && !aBox.IsSelected()) {
            var bStyle = aBox.GetBranchStyle();
            if (branchStyle == bStyle) return false;
          }
        }
      }
    }

    return true;
  }

  ActionError(errCode) {
    var w = new QAlert();
    w.InitAlert(appstr("Alert"), appstr(errCode), null, this.fWSMgr);
    return false;
  }

  ValidBranchStyleChange(branchStyle) {
    var valid = true;
    switch (branchStyle) {
      case enumBranchStyle.assistantpeerleft:
      case enumBranchStyle.assistantpeerright:
        valid = true; //this.CheckPeerStyles(branchStyle);
        break;
      default:
        break;
    }

    if (!valid) {
      var w = new QAlert();
      w.InitAlert(appstr("Alert"), appstr("branchstyleerr1"), null, this.fWSMgr);
    }

    return valid;
  }

  ExtendSelectPeers(aBox) {
    var dataObject = aBox.GetDataObj();
    var parentObject = dataObject.GetParent();
    if (parentObject == null) return;

    var parentBox = this.GetChartBoxFromDBObj(parentObject);
    var containerNode = parentBox.GetContainerNode();
    var associatedBoxes = [];
    if (containerNode !== null) {
      var associatedNodes = containerNode.GetAssociatedNodes();
      associatedBoxes = PageNodesToBoxList(associatedNodes);
    } else {
      associatedBoxes.push(parentBox);
    }

    for (var j = 0; j < associatedBoxes.length; j++) {
      parentObject = associatedBoxes[j].GetDataObj();
      if (parentObject === null) continue;
      var numChildren = parentObject.GetNumChildren();

      for (var i = 0; i < numChildren; i++) {
        var childObj = parentObject.GetIndexChild(i);
        var nextBox = this.GetChartBoxFromDBObj(childObj);
        var nextAssist = nextBox.IsAssistant();
        if (!nextBox.IsSelected() && !nextAssist) this.DoSelectElement(nextBox, true);
      }
    }
  }

  ExtendBSSelection(branchStyle) {
    var selectedBoxes = this.GetSelectedBoxes(false);

    const getBoxesFromLayout = aLayout => {
      return this.GetChartBoxFromDBObj(aLayout.fDBObjRef);
    };

    if (!selectedBoxes) {
      let lineSelection = this.GetSelectedLines();
      if (lineSelection.length > 0) {
        let theLine = lineSelection[0];
        let selectedLayouts = theLine.fAssociatedLayouts;
        selectedBoxes = selectedLayouts.map(function (aLayout) {
          return getBoxesFromLayout(aLayout);
        });
      }
    }
    for (var i in selectedBoxes) {
      if (IsAssistantStyle(branchStyle) == null) this.ExtendSelectPeers(selectedBoxes[i]);
    }
  }

  SelectBranch(aBox, clearSelection) {
    if (clearSelection) this.ClearSelection();

    var dataObject = aBox.GetDataObj();
    this.DoSelectElement(aBox, true);

    var numChildren = dataObject.GetNumChildren();
    for (var i = 0; i < numChildren; i++) {
      var childObj = dataObject.GetIndexChild(i);
      var nextBox = this.GetChartBoxFromDBObj(childObj);
      if (nextBox) this.SelectBranch(nextBox, false);
    }
  }

  //=================================================================================================
  // Do Find
  Advance(currentBox, currentCell, forward) {
    var nextCell = currentBox.FindNextCell(currentCell, forward);
    if (nextCell == null) {
      var nextBox = this.FindNextBox(currentBox, forward);
      if (forward) nextCell = nextBox.GetFirstCell();
      else nextCell = nextBox.GetLastCell();
    }

    return nextCell;
  }

  FindNext(forward, findStr, replaceStr) {
    var foundMatch = 0;

    var selectedCell = null;

    var startBox = this.GetFirstSelectedBox(true);
    if (startBox == null) {
      selectedCell = this.GetFirstSelectedElement(enumChartElement.boxCell);
      if (selectedCell == null) {
        startBox = this.FindRootBox();
        if (startBox) selectedCell = startBox.GetFirstCell();
      } else {
        startBox = selectedCell.GetParentBox();
        selectedCell = this.Advance(startBox, selectedCell, forward);
        startBox = selectedCell.GetParentBox();
      }
    } else selectedCell = startBox.GetFirstCell();

    if (selectedCell == null) return;

    var startCell = selectedCell;
    var nextCell = selectedCell;
    var nextBox = startBox;

    this.ClearSelection();

    var done = false;
    while (!done) {
      done = nextCell.Match(findStr, replaceStr);
      if (done) {
        this.ClearSelection();
        this.DoSelectElement(nextCell, true);
        foundMatch = 1;
      } else {
        nextCell = this.Advance(nextBox, nextCell, forward);
        nextBox = nextCell.GetParentBox();
        //nextCell = selectedCell.GetParentBox();
        if (nextCell == startCell) done = true;
      }
    }

    return foundMatch;
  }

  FindRootBox() {
    var numElements = this.GetNumElements();
    for (var i = 0; i < numElements; i++) {
      var theElement = this.fElements[i];
      if (this.IsElementBox(theElement)) {
        if (theElement.IsRoot()) return theElement;
      }
    }

    return null;
  }

  FindAll(findStr, replaceStr) {
    this.ClearSelection();
    var count = 0;
    var numElements = this.GetNumElements();
    var replaceMode = replaceStr != null;
    for (var i = 0; i < numElements; i++) {
      var theElement = this.fElements[i];
      var elementType = theElement.GetElementType();
      if (elementType == enumChartElement.boxCell) {
        var parentBox = theElement.GetParentBox();
        if (!parentBox.IsSelected() || replaceMode) {
          var match = theElement.Match(findStr, replaceStr);
          if (match) {
            if (!parentBox.IsSelected()) this.DoSelectElement(parentBox, true);
            count++;
          }
        }
      }
    }

    return count;
  }

  DoFind(action, findStr, replaceStr) {
    var numberChanges = 0;
    var numberFound = 0;
    var reportNum = false;

    switch (action) {
      case "FindPrevious":
        numberFound = this.FindNext(false, findStr, null);
        break;
      case "FindNext":
        numberFound = this.FindNext(true, findStr, null);
        break;
      case "FindAll":
        numberFound = this.FindAll(findStr, null);
        reportNum = true;
        break;
      case "Replace":
        numberFound = this.FindNext(true, findStr, replaceStr);
        numberChanges = numberFound;
        break;
      case "ReplaceAll":
        numberFound = this.FindAll(findStr, replaceStr);
        numberChanges = numberFound;
        reportNum = true;
        break;
    }

    if (numberChanges > 0) this.RecalculateChartLayout(null);

    var message = "";
    if (numberFound == 0) {
      message = appstr("nomatches"); // "No matches found.";
    } else if (reportNum) {
      if (numberFound == 1) {
        message = appstr("onematch"); // "1 match found.";
      } else {
        message = numberFound + " " + appstr("matchesfound"); // "matches found."
      }
    }

    return message;
  }

  //=================================================================================================
  // Align Selection
  AlignSelection(alignHow) {
    var roots = this.GetRootBoxes(true, true);

    var n = roots.length;
    var fullBounds = null;
    var plm = this.GetPageLayoutMgr();

    for (var i = 0; i < n; i++) {
      var bounds = plm.GetBranchBounds(roots[i], false);

      if (fullBounds == null) fullBounds = bounds;
      else fullBounds.UnionRect(bounds);
    }

    for (var i = 0; i < n; i++) {
      var aRoot = roots[i];
      var rBounds = plm.GetBranchBounds(aRoot, false); // aRoot.GetBoundingRect();
      var dx = 0;
      var dy = 0;

      switch (alignHow) {
        case "alignleft":
          dx = fullBounds.fLeft - rBounds.fLeft;
          break;
        case "aligncenter":
          var cx = fullBounds.fLeft + Math.floor((fullBounds.fRight - fullBounds.fLeft) / 2);
          var ox = rBounds.fLeft + Math.floor((rBounds.fRight - rBounds.fLeft) / 2);
          dx = cx - ox;
          break;
        case "alignright":
          dx = fullBounds.fRight - rBounds.fRight;
          break;
        case "aligntop":
          dy = fullBounds.fTop - rBounds.fTop;
          break;
        case "alignmiddle":
          var cy = fullBounds.fTop + Math.floor((fullBounds.fBottom - fullBounds.fTop) / 2);
          var oy = rBounds.fTop + Math.floor((rBounds.fBottom - rBounds.fTop) / 2);
          dy = cy - oy;
          break;
        case "alignbottom":
          dy = fullBounds.fBottom - rBounds.fBottom;
          break;
      }

      plm.SetUserCentering(aRoot);
      if (dx !== 0 || dy != 0) {
        this.MoveChart(aRoot, dx, dy);
        plm.ResetEdgeDistance(aRoot);
      }
    }
  }

  ReorderSelection(renderHow) {
    var renderMode = this.GetRenderMode();
    var sc = this.GetSubChartMgr().GetCurrentSubChart();
    if (
      renderMode == enumPanelRenderMode.masterOnly ||
      renderMode == enumPanelRenderMode.backgroundAndGhostChart
    ) {
      this.GetSubChartMgr().SetCurrentSubChart(0);
    }
    var roots = this.GetRootBoxes(true, true); // selected elements

    var plm = this.GetPageLayoutMgr();
    plm.ShiftRenderOrder(roots, renderHow);
    this.GetSubChartMgr().SetCurrentSubChart(sc.GetSubChartID());
  }

  //=================================================================================================
  // Clipboard support
  CanCustomCopy() {
    if (Globals._accountInfo.ReadOnlyMode()) return false;

    var selectedBox = this.GetFirstSelectedBox(true);
    return selectedBox != null;
  }

  CanCustomCut() {
    if (Globals._accountInfo.ReadOnlyMode()) return false;

    var selectedBox = this.GetFirstSelectedBox(true);
    return selectedBox != null;
  }

  CanCustomPaste() {
    if (Globals._accountInfo.ReadOnlyMode()) return false;

    var canCustomPaste = this.OnlyOneBoxSelected();
    if (canCustomPaste) {
      var selectedBox = this.GetFirstSelectedBox(false);
      if (selectedBox) canCustomPaste = !selectedBox.TopOfSubChartBreak();
      else canCustomPaste = false;
    }

    return canCustomPaste;
  }

  DoCustomCopy(theClipboard) {
    var selectedBoxes = this.GetSelectedBoxes(false);
    if (selectedBoxes == null) return null;

    var theData = theClipboard.DeepCopy(selectedBoxes);

    var cbBuffer = new QClipboardBuffer();
    cbBuffer.Init(theData, kCBChartBoxContent, false);

    var tracker = this.fWSMgr.GetUsageTracker();
    if (tracker) {
      tracker.ReportRecordClipboardEvent(theData, "Copy");
    }

    return cbBuffer;
  }

  DoCustomPaste(theClipboard) {
    var selectedBox = this.GetFirstSelectedBox(false);
    if (selectedBox == null) return;

    var theContent = theClipboard.GetContent();
    var tracker = this.fWSMgr.GetUsageTracker();
    if (tracker) {
      tracker.ReportRecordClipboardEvent(theContent, "Paste");
    }

    var theDoc = this.fDoc;
    var objDB = theDoc.GetObjDB();
    var parentObj = selectedBox.GetDataObj();
    var plm = this.GetPageLayoutMgr();
    var subChartMgr = this.GetSubChartMgr();

    var bStyle = plm.DetermineBranchStyle(parentObj, null);
    theClipboard.AddClipboardToDoc(objDB, parentObj, subChartMgr, bStyle);

    this.ReloadCurrentSubChart();
    // Reselected that selectedBox...
    //this.SetDocDirty();
    var theElement = this.GetChartBoxFromDBObj(parentObj);

    this.DoSelectElement(theElement, true);
    // this.PerformSelect(theElement, true);
  }

  ObjectName() {
    return this.fObjectName;
  }

  DynamicResizeComplete(srcElement) {
    srcElement.ResizeComplete();
    var srcBox = srcElement.GetAssociatedBox();
    var srcLayout = srcBox.GetLayout();
    var sizingMode = srcLayout.GetWidthSizingMode();
    var srcWidth = srcLayout.GetBoxWidth();
    var subType1 = srcLayout.GetBoxSubType();
    if (subType1 != enumBoxSubTypes.chartBox) return;

    var selectedBoxes = this.GetSelectedBoxes(true);
    var n = selectedBoxes.length;
    for (var i = 0; i < n; i++) {
      var aBox = selectedBoxes[i];
      var subType2 = aBox.GetBoxSubType();

      if (subType2 == enumBoxSubTypes.chartBox && srcBox != aBox) {
        var dstLayout = aBox.GetLayout();
        dstLayout.SetWidthSizingMode(enumBoxWidthMode.fixedwidth);

        var curWidth = dstLayout.GetBoxWidth();
        var deltaX = srcWidth - curWidth;
        dstLayout.ChangeBoxWidth(deltaX);
        dstLayout.CalculateLayout();
        aBox.ResetBoxLayout(true);
      }
    }

    this.RecalcChartLayoutForSelection();
  }

  GetFirstSelectedBox(includeTextBox) {
    var selCache = this.GetSelectionCache();
    var numElements = selCache.length;
    for (var i = 0; i < numElements; i++) {
      var theElement = selCache[i];
      if (this.IsElementChartBox(theElement, includeTextBox)) return theElement;
    }

    return null;
  }

  //=================================================================================================
  // GetCursor
  GetCursor() {
    const panelMode = this.GetPanelMode();

    if (![enumPanelMode.combinedMode, enumPanelMode.implicitMode].includes(panelMode)) {
      return this.fCurCursor;
    }

    const scaleFactor = this.GetScaleFactor();
    const scaledDimensions = {
      width: Math.round(this.fCanvasSizeX * scaleFactor),
      height: Math.round(this.fCanvasSizeY * scaleFactor)
    };

    const availableSpace = {
      width: this.fBackgroundPanel.GetWidth(),
      height: this.fBackgroundPanel.fHeight
    };

    const isCanvasOffscreen =
      availableSpace.width < scaledDimensions.width ||
      availableSpace.height < scaledDimensions.height;

    this.fCurCursor = "default";

    if (
      isCanvasOffscreen &&
      !(panelMode === enumPanelMode.implicitMode && this.fWSMgr.ShiftDown())
    ) {
      this.fCurCursor = "move";
    }

    return this.fCurCursor;
  }

  // Collect a box's sub-elements (connecting lines, resizer, drill) into the target array,
  // nulling references to prevent double-removal. Used by batch removal paths.
  _collectBoxSubElements(box, target) {
    if (box.fConnectingLineGroup && box.fConnectingLineGroup.fElements) {
      var groupElems = box.fConnectingLineGroup.fElements;
      for (var g = 0; g < groupElems.length; g++) {
        // Clear each line's group backref so BatchRemoveElements' SetElementGroup(null)
        // won't call PRemoveElementFromGroup on the group we're about to discard
        groupElems[g].fElementGroup = null;
        target.push(groupElems[g]);
      }
      box.fConnectingLineGroup = null;
    }
    if (box.fBoxResizer) {
      target.push(box.fBoxResizer);
      box.fBoxResizer = null;
    }
    if (box.fSubChartDrill) {
      target.push(box.fSubChartDrill);
      box.fSubChartDrill = null;
    }
  }

  PPrepSwitchSubChart(removeWhat) {
    this.ClearSelection();

    const numElements = this.GetNumElements();
    if (numElements === 0) return;

    const plm = this.GetPageLayoutMgr();
    const elementsToRemove = [];
    const keepNodeList = new QNodeArray();

    const isElementBox = this.PIsElementBox.bind(this);

    for (let i = 0; i < numElements; i++) {
      const anElement = this.fElements[i];

      if (!isElementBox(anElement, true)) continue;

      const eLayer = anElement.GetLayer();

      if (eLayer === removeWhat) {
        elementsToRemove.push(anElement);
      } else {
        const keepNode = this.GetPLMNode(anElement);
        if (keepNode) {
          keepNode.SetMasterNode(true);
          keepNodeList.AddNode(keepNode);
        }
      }
    }

    // Collect all elements to remove (boxes + their sub-elements) into a single batch
    var allToRemove = [];
    for (let i = 0; i < elementsToRemove.length; i++) {
      var box = elementsToRemove[i];
      this._collectBoxSubElements(box, allToRemove);
      allToRemove.push(box);
      box["invalid"] = true;
    }
    this.BatchRemoveElements(allToRemove);

    // Planning Specific
    this.RemoveActivityContent();

    plm.ReplaceActiveNodeList(keepNodeList);
  }

  //=================================================================================================
  // SubCharts
  PrepSwitchSubChart() {
    this.PPrepSwitchSubChart(enumElementLayer.content);
  }

  HideChildren(aBox) {
    var dataObject = aBox.GetDataObj();
    var numChildren = dataObject.GetNumChildren();
    for (var i = 0; i < numChildren; i++) {
      var childObj = dataObject.GetIndexChild(i);
      var nextBox = this.GetChartBoxFromDBObj(childObj);
      if (nextBox) {
        var l = nextBox.GetLayout();
        l.SetHidden(true);
        this.HideChildren(nextBox);
      }
    }
  }

  FindParentSCNode(fullList, anObj) {
    var scm = this.GetSubChartMgr();
    var parentObj = anObj.GetParent();
    if (parentObj == null) return null;

    var parentID = parentObj.GetObjectID();
    var n = fullList.length;
    for (var i = 0; i < n; i++) {
      var scn = fullList[i];
      if (scn.fDataObjID == parentID) return scn;
    }

    return null;
  }

  IsParentCollaped(fullList, anObj) {
    while (anObj != null) {
      var pNode = this.FindParentSCNode(fullList, anObj);
      if (pNode == null) return false;

      var l = pNode.GetLayout();
      if (l.GetCollapsed()) return true;

      anObj = anObj.GetParent();
    }

    return false;
  }

  ShowChildren(aBox) {
    var baseLayout = aBox.GetLayout();
    var baseObj = aBox.GetDataObj();
    var subChartID = baseLayout.GetSubChartID();

    var scm = this.GetSubChartMgr();
    var fullList = scm.GetNodesForSubChart(subChartID, false); //GetSCNodes(subChartID);
    if (fullList == null) return;

    var n = fullList.length;
    for (var i = 0; i < n; i++) {
      var scNode = fullList[i];
      var aLayout = scNode.GetLayout();
      var anObj = scNode.GetDataObj();
      var layout_subChartID = aLayout.GetSubChartID();
      var isHidden = aLayout.GetHidden();
      if (subChartID == layout_subChartID && isHidden) {
        if (anObj.InParentChain(baseObj)) {
          if (!this.IsParentCollaped(fullList, anObj)) aLayout.SetHidden(false);
        }
      }
    }
  }

  CollapseBranch(theBox) {
    var layout = theBox.GetLayout();
    var isCollapsed = !layout.GetCollapsed();
    layout.SetCollapsed(isCollapsed);
    if (isCollapsed) this.HideChildren(theBox);
    else this.ShowChildren(theBox);

    var subChartID = layout.GetSubChartID();
    this.SwitchToSubChart(subChartID);
  }

  GetLinkBookmark(subChartID) {
    var scm = this.GetSubChartMgr();

    var aSubChart = scm.FindSubChart(subChartID);
    var bmLink = null;
    var proceed = false;
    var br = scm.fBranchRoot;
    var pageCount = scm.fBranchSubChartCount;
    if (br == null) proceed = true;
    else {
      var currentSC = scm.GetActiveSubChart();
      var parentSC = currentSC.GetSubChartParent();
      if (parentSC && parentSC.GetSubChartID() == kIndexSCID && parentSC.fChildren.length > 0)
        pageCount += parentSC.fChildren.length;
      var p = br.GetSubChartParent();
      proceed = p != aSubChart && pageCount > 1;
    }

    if (proceed && aSubChart) {
      var theDoc = this.fDoc;
      var forceShow = theDoc.GetShowSubChartNumBM();
      bmLink = scm.GetSubChartPath(aSubChart, forceShow);
    }
    var isCompound = this.fDoc.fDocMgr.GetCompoundDoc();
    if (isCompound && bmLink) {
      bmLink = this.fDoc.GetDivisionName().replace("\n", "\\") + "\\" + bmLink;
    }

    return bmLink;
  }

  SubChartDrill(theBox) {
    var subChartID = theBox.DrillSubChartID();
    this.ClearSelection();
    this.SwitchToSubChart(subChartID);
    // Broadcast drill target so React URL sync keeps ?topOfChart= aligned
    var newSC = this.GetSubChartMgr()?.FindSubChart(subChartID);
    var topRef = newSC?.GetTopDBObjRef?.();
    if (topRef != null) {
      this.fWSMgr?.BroadcastTopic?.("navToBoxApplied", { targetID: topRef });
    }
  }

  InsertSubChartBreak() {
    var target = this.GetFirstSelectedBox(false);
    var dbObj = target.GetDataObj();
    var layout = target.GetLayout();
    var plm = this.GetPageLayoutMgr();
    var theNode = this.GetPLMNode(target);
    plm.InsertSubChartBreak(theNode);
    this.GetSubChartMgr().SaveBreakInfo(theNode.GetDataObj(), "insert", null);
    this.SwitchToSubChart(layout.GetSubChartID(), false);
    var box = this.GetChartBoxFromDBObj(dbObj);
    this.DoSelectElement(box, true);
    this.SetDocDirty(true);
  }

  ClearMasterPage() {
    this.ClearSelection();
    var scm = this.GetSubChartMgr();
    scm.ClearSubChart(0);
    var pageMgr = this.GetPageMgr();
    var pp = pageMgr.GetPageProps();
    if (!pageMgr["staticBackgroundColor"]) {
      pp.fBackgroundColor = "#FFFFFF";
      pp.fBackgroundFillMode = enumFillMode.solidFill;
      this.ChangePageBackground(pp);
    }
    this.PPrepSwitchSubChart(enumElementLayer.master);
  }

  ApplyStdMasterPage() {
    this.ClearMasterPage();

    var scm = this.GetSubChartMgr();
    var currentSubChart = scm.GetCurrentSubChart();
    var subChartID = currentSubChart.GetSubChartID();
    var priorRM = this.GetRenderMode();

    this.SwitchToSubChart(0);
    this.SetRenderMode(enumPanelRenderMode.masterOnly);
    this.AddStdMasterPage();

    this.SetRenderMode(priorRM);
    this.SwitchToSubChart(subChartID);
  }

  ApplyBackgroundToCanvas(mpSCNodes, pageProps, targetDoc, resetPageSize) {
    var wsMgr = this.fWSMgr;
    var backgroundMgr = wsMgr.fBackgroundMgr;
    var selCache = this.GetSelectionCache();
    var cellList = this.GetSelectedCellDescs();
    var cellSelCache = cellList.slice(0);
    var selCacheCopy = selCache.slice(0);
    var selectedIds = selCacheCopy?.map(box => box.GetLayout()?.GetDBObjID());

    this.ClearMasterPage();
    var scm = this.GetSubChartMgr();
    let currentSubChart = scm.GetCurrentSubChart();

    var priorRM = this.GetRenderMode();
    this.SwitchToSubChart(0, false);
    this.SetRenderMode(enumPanelRenderMode.masterOnly);
    var needsResize = false;
    if (pageProps) {
      if (!resetPageSize) {
        var plm = this.GetPageLayoutMgr();
        pageProps.fPageWidth = plm.GetPageWidth();
        pageProps.fPageHeight = plm.GetMinPageHeight();
        needsResize = true;
      }
      if (
        Globals._pubSettings.fShowActivity != "Off" &&
        Globals._uiConfig.GetUIMode() === enumUIModes.planning &&
        Globals._publishRef != null
      ) {
        var planMgr = this.fWSMgr.fPlanManager;
        if (planMgr != null) {
          planMgr.AddMasterPubMargin(pageProps, this.fWSMgr.fBackgroundMgr.GetCurBackgroundName());
        }
      }
      this.ChangePageProps(pageProps, enumPageSizeUnits.pixels);
      // BG color is stored in the active background page props always
      this.ChangePageBackground(wsMgr.fBackgroundMgr.GetActivePageProps() ?? pageProps);
      if (needsResize) this.DoCustomResize();
    }

    var elements = [];
    for (var node of mpSCNodes) {
      var elem = this.AddBackgroundElementToDoc(targetDoc, node);
      elements.push(elem);
    }

    this.SetRenderMode(priorRM);
    if (currentSubChart) {
      this.SetCurrentHighlightAsInitial();
      var saveSubChartID = currentSubChart.GetSubChartID();
      this.SwitchToSubChart(saveSubChartID, false);
    }

    var elemIds = elements.map(box => box?.GetLayout()?.GetDBObjID());
    for (var id of elemIds) {
      if (selectedIds.includes(id)) {
        backgroundMgr.SelectBackgroundElement(id, false);
      }
    }

    backgroundMgr.GainElementFocus(true);

    var chartBox = this.GetFirstSelectedBox(true);
    var selectedCell = false;
    if (cellSelCache.length > 0 && chartBox != null) {
      for (var cellDesc of cellSelCache) {
        var cI = cellDesc.GetColIndex();
        var rI = cellDesc.GetRowIndex();
        var selected = this.SelectCell(cI, rI);
        if (!selectedCell && selected) selectedCell = true;
      }
      if (selectedCell) {
        this.DoSelectElement(chartBox, false);
      }
    }

    this.fWSMgr.BroadcastTopic("backgroundApplied");
  }

  GetChartBox() {
    var firstBox = this.GetFirstSelectedBox(true);
    if (!firstBox) {
      var firstCell = this.GetFirstSelectedCell();
      if (!firstCell) return null;
      firstBox = firstCell.GetParentBox();
    }
    return firstBox;
  }

  SelectCell(colIndex, rowIndex) {
    var chartBox = this.GetFirstSelectedBox(true);
    if (!chartBox) return false;
    var layoutCells = chartBox.fLayoutCells;
    return this.DoSelectCell(layoutCells, colIndex, rowIndex);
  }

  AddBackgroundElementToDoc(targetDoc, scNode, selectedId) {
    var aLayout = scNode.GetLayout();
    var nLayout = aLayout.Clone({ includeId: true });
    if (targetDoc != null) targetDoc.FinishLoadLayout(nLayout);

    var x = nLayout.GetPositionX();
    var y = nLayout.GetPositionY();

    var backgroundElement;
    if (
      nLayout.GetBoxSubType() !== enumBoxSubTypes.enumeratedTable ||
      nLayout.GetEnumeratedTableRef()
    ) {
      backgroundElement = this.AddChartBox(
        x,
        y,
        nLayout,
        null,
        null,
        false,
        true,
        false,
        true,
        scNode.GetDataObjID(),
        false
      );
    }
    return backgroundElement;
  }

  OptimizeBeforeRemove(target, focus = true) {
    var childSCID = target.DrillSubChartID();
    var scm = this.GetSubChartMgr();
    var childSC = scm.FindSubChart(childSCID);
    if (childSC.GetNeedOptimize()) {
      var curSC = scm.GetCurrentSubChart();
      this.SwitchToSubChart(childSCID, focus);
      this.SwitchToSubChart(curSC.GetSubChartID(), focus);
    }
  }

  RemoveSubChartBreak() {
    var target = this.GetFirstSelectedBox(false);

    var dbObj = target.GetDataObj();
    var layout = target.GetLayout();
    var plm = this.GetPageLayoutMgr();
    var theNode = this.GetPLMNode(target);

    var scm = this.GetSubChartMgr();
    scm.SaveBreakInfo(dbObj, "remove", null);

    this.OptimizeBeforeRemove(target, false);
    plm.RemoveSubChartBreak(theNode, true, true);

    this.SwitchToSubChart(layout.GetSubChartID(), false);
    var box = this.GetChartBoxFromDBObj(dbObj);
    this.DoSelectElement(box, true);
    this.SetDocDirty(true);
  }

  DoOptimizePage(aspectRatio, allowSort, treeSingles, branchPrefs, param, optimizeAll) {
    var plm = this.GetPageLayoutMgr();

    var allRoots = this.GetRootBoxes(false, false);

    var firstSelNode = null;
    if (gOptimizeDebug) {
      var firstBox = this.GetFirstSelectedBox(false);
      if (firstBox) firstSelNode = firstBox["nodeRef"];
      if (optimizeAll) firstSelNode = null;
    }
    var realRoot = null;
    if (allRoots.length == 0) return;
    else if (allRoots.length == 1) realRoot = allRoots[0];
    else {
      var n = allRoots.length;
      for (var i = n - 1; i >= 0; i--) {
        realRoot = allRoots[i];
        if (realRoot.GetDataObj().IsRoot()) {
          break;
        }
      }
    }
    var scm = this.GetSubChartMgr();
    var curSC = scm.GetCurrentSubChart();
    if (curSC && !curSC.fBranchStyleOverride) {
      DoOptimizePage(
        realRoot,
        plm,
        aspectRatio,
        allowSort,
        treeSingles,
        branchPrefs,
        param,
        firstSelNode,
        this.fDoc
      );
    }

    this.RecalculateChartLayout(null);

    if (optimizeAll) {
      scm.OptimizeAllSubcharts();
    }
    this.PositionAltBackground();
  }

  //=================================================================================================
  // AutoText
  SetAutoTextType(autoTextType) {
    this.fNextAutoText = autoTextType;
  }

  AddAutoText(whichAutoText, pinHow) {
    this.SetAutoTextType(whichAutoText);
    var defaultLayout = this.GetAutoTextLayout();
    var newBox = this.AddChartBox(
      0,
      0,
      defaultLayout,
      null,
      null,
      false,
      true,
      false,
      true,
      null,
      false
    );
    this.PositionTextBoxFromCmd(newBox, pinHow);
  }

  ChangePageProps(pageProps, units) {
    var newPageWidth = pageProps.fPageWidth;
    var newPageHeight = pageProps.fPageHeight;
    var newMargins = pageProps.fMargins;
    var newOrientation = pageProps.fPageOrientation;

    var showMargins = pageProps.fShowMargins;
    var newHeaderHeight = pageProps.fHeaderHeight;
    var newFooterHeight = pageProps.fFooterHeight;
    var showHeaderFooter = pageProps.fShowHeaderFooter;

    this.ChangePageSize(
      newPageWidth,
      newPageHeight,
      newMargins,
      newOrientation,
      newHeaderHeight,
      newFooterHeight,
      units
    );
    this.SetShowPageMargins(showMargins);
    this.SetShowHeaderFooter(showHeaderFooter);
    this.ChangePageBackground(pageProps);
  }

  ChangePageBackground(pageProps) {
    var backgroundColor = pageProps.fBackgroundColor;
    var backgroundFillMode = pageProps.fBackgroundFillMode;
    this.SetBackgroundColor(backgroundColor);
    this.SetBackgroundFillMode(backgroundFillMode);
    if (backgroundFillMode == enumFillMode.linearGradientDown && pageProps.fBackgroundColor2) {
      this.SetBackgroundColor2(pageProps.fBackgroundColor2);
    }
  }

  AddStdMasterPage() {
    var pageMgr = this.GetPageMgr();
    var pp = new QPageProps();

    pp.fPageWidth = pageMgr.GetMinPageWidth();
    pp.fPageHeight = pageMgr.GetMinPageHeight();
    pp.fMargins = GetStdMargins();
    pp.fPageOrientation = pageMgr.GetPageOrientation();
    //pp.fAutoSizePDF = false;
    pp.fShowMargins = true;
    pp.fHeaderHeight = GetStdHeaderHeight(enumUnitVal.pixels);
    pp.fFooterHeight = GetStdFooterHeight(enumUnitVal.pixels);
    pp.fShowHeaderFooter = true;
    pp.fBackgroundColor = "#FFFFFF";
    pp.fBackgroundFillMode = enumFillMode.solidFill;

    this.ChangePageProps(pp, enumPageSizeUnits.pixels);

    this.AddAutoText(enumAutoTextType.companyname, "pinLeftTop");
    this.AddAutoText(enumAutoTextType.subchartname, "pinRightTop");
    this.AddAutoText(enumAutoTextType.currentdate, "pinLeftBottom");
    this.AddAutoText(enumAutoTextType.pagexofy, "pinRightBottom"); //subchartnum
  }

  //=================================================================================================
  // Search
  NavToBox(
    dataObj,
    topOfSubChart,
    personId,
    navToBox = true,
    selectIt = true,
    highlightIt = false
  ) {
    return this.FocusObject({
      dbObject: dataObj,
      navigateToBox: navToBox,
      topOfSubChart,
      selectElement: selectIt,
      highlightElement: highlightIt,
      recordId: personId
    });
  }

  FocusObject({
    recordId,
    dbObjId,
    dbObject,
    selectElement = true,
    highlightElement = false,
    navigateToBox = false,
    topOfSubChart
  }) {
    var plm = this.GetPageLayoutMgr();

    if (navigateToBox) {
      if (dbObject == null) return false;
      var scm = this.GetSubChartMgr();
      var scID = scm.FindSubChartID(dbObject, topOfSubChart);
      if (scID == null) return false;
      this.SwitchToSubChart(scID, false);
    }

    var focusNode;
    if (recordId) {
      focusNode = plm.FindNodeFromRecordID(recordId);
    } else {
      focusNode = plm.FindNodeFromDataObjID(dbObjId ?? dbObject?.GetObjectID());
    }

    if (focusNode) {
      var chartBox = focusNode.GetChartBox();
      if (chartBox) {
        if (!this.ElementInViewPort(chartBox, true)) {
          this.PanToObject(
            chartBox.GetPositionX(),
            chartBox.GetPositionY(),
            chartBox.GetWidth(),
            chartBox.GetHeight()
          );
        }
        this.DoSelectElement(chartBox, selectElement);
        this.DoHighlightElement(chartBox, highlightElement);
      }
      return true;
    }
    return false;
  }

  FocusParent(dbObj) {
    var wsMgr = this.fWSMgr;
    var doc = wsMgr.GetDoc();
    var objDB = doc.GetObjDB();
    var boxClass = objDB.GetClassByName("Box");
    var superId = dbObj.SmartGetFieldValue("SupervisorID");
    var parentDBObj = boxClass.FindObject(boxClass.GetRecordIDFieldDef(), superId);
    if (parentDBObj) {
      return this.FocusObject({ recordId: superId });
    }
    return false;
  }

  PanToObject(objX, objY, objW, objH) {
    var portHeight = this.fPortHeight;
    var portWidth = this.fPortWidth;
    var scaleFactor = this.GetScaleFactor();
    var canvasWidth = this.fCanvasSizeX;
    var canvasHeight = this.fCanvasSizeY;
    var unscaledPortWidth = Math.round(portWidth / scaleFactor);
    var unscaledPortHeight = Math.round(portHeight / scaleFactor);
    var maxPanX = canvasWidth - unscaledPortWidth;
    var maxPanY = canvasHeight - unscaledPortHeight;
    var newCenterX = Math.round(objX - CenterIt(unscaledPortWidth, objW));
    var newCenterY = Math.round(objY - CenterIt(unscaledPortHeight, objH));
    var panX = Math.min(newCenterX, maxPanX);
    var panY = Math.min(newCenterY, maxPanY);
    if (panX < 0) panX = 0;
    if (panY < 0) panY = 0;
    this.SetPanX(panX);
    this.SetPanY(panY);
  }

  GetMarginLineColor() {
    var wsMgr = this.fWSMgr;
    var docMgr = wsMgr.GetDocMgr();
    var bgFillColor = docMgr.GetBackgroundColor();
    return DetermineBrightness(bgFillColor) < gBrightnessThreshold ? "#EEEEEE" : "#3d3d4a";
  }

  GetLegendLayout() {
    var kInitialWidth = 144;

    var theLayout = new QBoxLayout();
    theLayout.InitBoxLayout(kInitialWidth, 2, 3);
    theLayout.SetBoxPadding(1);

    theLayout.SetBorderStyle(enumElementBorderStyle.noBorder);
    theLayout.SetBoxType(enumBoxType.nonstandard);
    theLayout.SetBoxSubType(enumBoxSubTypes.refBoxLegend);
    theLayout.SetBoxFillMode(enumFillMode.noFill);
    theLayout.SetDocRef(this.fDoc);

    theLayout.ResizeColumn(1, -40, false);

    SetupTableCell(theLayout, 2, 1, appstr("Label"), null, false);
    SetupTableCell(theLayout, 2, 2, appstr("Label"), null, false);
    SetupTableCell(theLayout, 2, 3, appstr("Label"), null, false);

    SetupTableCell(theLayout, 1, 1, "", "GreenAccent", false);
    SetupTableCell(theLayout, 1, 2, "", "LightTurquoise", false);
    SetupTableCell(theLayout, 1, 3, "", "GoldStrongAccent", false);

    theLayout.CalculateLayout();

    return theLayout;
  }

  GetTableLayout() {
    var kInitialWidth = 164;

    var theLayout = new QBoxLayout();
    theLayout.InitBoxLayout(kInitialWidth, 2, 4);
    theLayout.SetBoxPadding(1);
    theLayout.SetWidthMax(768);

    theLayout.SetBorderStyle(enumElementBorderStyle.singleBorder);
    theLayout.SetBoxType(enumBoxType.nonstandard);
    theLayout.SetBoxSubType(enumBoxSubTypes.refBoxTable);
    theLayout.SetBoxFillMode(enumFillMode.noFill);
    theLayout.SetDocRef(this.fDoc);

    this.BuildInitialLayoutCells(theLayout);

    return theLayout;
  }

  BuildInitialLayoutCells(
    theLayout // fxn of qboxlayout?
  ) {
    SetupTableCell(theLayout, 1, 1, appstr("Header1"), "Gray15", true);
    SetupTableCell(theLayout, 2, 1, appstr("Header2"), "Gray15", true);

    SetupTableCell(theLayout, 1, 2, appstr("Label"), null, false);
    SetupTableCell(theLayout, 2, 2, appstr("Value"), null, false);
    SetupTableCell(theLayout, 1, 3, appstr("Label"), null, false);
    SetupTableCell(theLayout, 2, 3, appstr("Value"), null, false);
    SetupTableCell(theLayout, 1, 4, appstr("Label"), null, false);
    SetupTableCell(theLayout, 2, 4, appstr("Value"), null, false);

    theLayout.CalculateLayout();
  }

  GetTextBoxLayout() {
    var kInitialWidth = 16;
    var kTextDefSize = 18;

    var theLayout = new QBoxLayout();
    theLayout.InitBoxLayout(kInitialWidth, 1, 1);

    theLayout.SetBorderStyle(enumElementBorderStyle.noBorder);
    theLayout.SetWidthSizingMode(enumBoxWidthMode.autoself);
    theLayout.SetHeightSizingMode(enumBoxHeightMode.autoself);

    theLayout.SetWidthMin(kInitialWidth);
    theLayout.SetWidthMax(768);
    theLayout.SetBoxPadding(1);
    theLayout.SetBoxType(enumBoxType.nonstandard);

    theLayout.SetBoxSubType(enumBoxSubTypes.textBox);
    theLayout.SetBoxFillMode(enumFillMode.noFill);

    var c1 = theLayout.GetCellDesc(1, 1);
    c1.SetAlignment("left");
    c1.SetFontSize(kTextDefSize);
    c1.SetCellContentType(enumCellContentType.FreeText);

    var initialVal = appstr("textbox");
    c1.SetFreeText(initialVal);

    theLayout.CalculateLayout();

    return theLayout;
  }

  RescaleImage(imageWidth, imageHeight, maxWidth, maxHeight) {
    if (maxWidth === Infinity) {
      var scaleFactor = maxHeight / imageHeight;
      var newWidth = Math.round(imageWidth * scaleFactor);
      return { width: newWidth, height: maxHeight };
    }
    var widthScaleFactor = maxWidth / imageWidth;
    var heightScaleFactor = maxHeight / imageHeight;
    var scaleFactor = Math.min(widthScaleFactor, heightScaleFactor);
    var newWidth = Math.round(imageWidth * scaleFactor);
    var newHeight = Math.round(imageHeight * scaleFactor);
    return { width: newWidth, height: newHeight };
  }

  /**
   * @param {{
   *   fDirectory: string,
   *   fFileName: string,
   *   scaleImage?: boolean,
   *   maxWidth?: number,
   *   maxHeight?: number
   * }} dataRef
   */
  GetPictureLayout(dataRef) {
    var theDoc = this.fDoc;
    var theImage = theDoc.FindPicture(dataRef.fDirectory, dataRef.fFileName);
    var oWidth = theImage.width;
    var oHeight = theImage.height;

    if (dataRef?.scaleImage) {
      var scaledDimensions = this.RescaleImage(
        oWidth,
        oHeight,
        dataRef?.maxWidth,
        dataRef?.maxHeight
      );
      oWidth = scaledDimensions.width;
      oHeight = scaledDimensions.height;
    }

    var iWidth = oWidth;
    var iHeight = oHeight;

    var xr = 1;
    var yr = 1;

    var xa = this.fCanvasSizeX - 2 * kInitialPicPosX;
    if (iWidth > xa) xr = xa / iWidth;

    var ya = this.fCanvasSizeY - 2 * kInitialPicPosY;

    if (iHeight > ya) yr = ya / iHeight;

    var r = Math.min(xr, yr);
    iWidth = Math.floor(iWidth * r);
    iHeight = Math.floor(iHeight * r);

    var theLayout = new QBoxLayout();
    theLayout.InitBoxLayout(iWidth, 1, 1);

    theLayout.SetBorderStyle(enumElementBorderStyle.noBorder);
    theLayout.SetWidthSizingMode(enumBoxWidthMode.fixedwidth);
    theLayout.SetHeightSizingMode(enumBoxHeightMode.fixedheight);

    theLayout.SetBoxPadding(1);
    theLayout.SetBoxType(enumBoxType.nonstandard);
    theLayout.SetBoxSubType(enumBoxSubTypes.picture);

    theLayout.SetBoxFillMode(enumFillMode.noFill);

    var c1 = theLayout.GetCellDesc(1, 1);
    c1.SetAlignment("left");
    c1.SetVAlignment("top", true);
    c1.SetCellContentType(enumCellContentType.Picture);

    c1.SetSPictureDirectory(dataRef.fDirectory);
    c1.SetSPictureFile(dataRef.fFileName);
    c1.SetCellHeight(iHeight);
    c1.SetSPictureSize(oWidth, oHeight);

    theLayout.SetFixedHeight(iHeight);
    theLayout.CalculateLayout();

    return theLayout;
  }

  GetAutoTextLayout(autoTextType = null) {
    var kInitialWidth = 16;
    var kTextDefSize = 12;

    var theLayout = new QBoxLayout();
    theLayout.InitBoxLayout(kInitialWidth, 1, 1);

    theLayout.SetBorderStyle(enumElementBorderStyle.noBorder);
    theLayout.SetWidthSizingMode(enumBoxWidthMode.autoself);
    theLayout.SetHeightSizingMode(enumBoxHeightMode.autoself);

    theLayout.SetWidthMin(kInitialWidth);
    theLayout.SetWidthMax(768);
    theLayout.SetBoxPadding(1);
    theLayout.SetBoxType(enumBoxType.nonstandard);
    theLayout.SetBoxSubType(enumBoxSubTypes.autoText);

    theLayout.SetBoxFillMode(enumFillMode.noFill);
    theLayout.SetDocRef(this.fDoc);

    var c1 = theLayout.GetCellDesc(1, 1);
    //c1.SetAlignment("left");
    c1.SetAlignment("center");
    c1.SetFontSize(kTextDefSize);
    c1.SetCellContentType(enumCellContentType.AutoText);
    c1.SetAutoTextType(autoTextType ?? this.fNextAutoText);
    theLayout.CalculateLayout();

    return theLayout;
  }

  GetEmptyLayout() {
    var kEmptyInitialWidth = 120;
    var theLayout = new QBoxLayout();
    theLayout.InitBoxLayout(kEmptyInitialWidth, 1, 2); //2
    theLayout.SetWidthSizingMode(enumBoxWidthMode.autosmart);
    theLayout.SetHeightSizingMode(enumBoxHeightMode.autosmart);
    theLayout.SetWidthMin(kEmptyInitialWidth);
    theLayout.SetWidthMax(144); //
    theLayout.SetPosition(0, 0);

    return theLayout;
  }

  GetDefaultBoxLayout() {
    var kEmptyInitialWidth = 120;
    var theLayout = this.fWSMgr.GetBoxStylesMgr().GetSystemDefaultLayout().Clone();
    theLayout.SetPosition(0, 0);

    return theLayout;
  }

  IsTargetChartBox(elem) {
    return elem && elem.ObjectName() === "QChartBox";
  }

  HandleReactClickPad(elem) {
    if (elem && this.IsTargetChartBox(elem)) {
      const isContainer = elem.GetBoxSubType() === enumBoxSubTypes.refMRBox;
      const isPosition = isContainer && elem.IsPositionBox();
      if (isContainer && !isPosition) {
        this.fWSMgr.BroadcastTopic("canvasClickToolbar", null);
        return;
      }
      this.fWSMgr.BroadcastTopic("canvasClickToolbar", { box: elem, isPosition });
      return;
    }
    this.fWSMgr.BroadcastTopic("canvasClickToolbar", null);
  }

  HandleHover(hoverElement, elementX, elementY, yTop, mouseX, mouseY) {
    var canvasPanel = this;
    let docRef = this.fDoc;

    if (hoverElement && hoverElement.fParentBox) {
      //Hover Element was a cell - drilling up to parent box
      hoverElement = hoverElement.fParentBox;
    }

    if (hoverElement?.GetLayer() === enumElementLayer.master) return;

    var isPlan = docRef.IsPlanChart();
    var isPositionsDoc = docRef.IsPositionDocument();

    if (isPlan && isPositionsDoc) return;

    if (this.IsTargetChartBox(hoverElement)) {
      // Group containers (standardmrb, comgr) have no data object — skip the hover toolbar.
      const containerType = hoverElement.GetContainerType();
      if (
        containerType === enumContainerType.standardmrb ||
        containerType === enumContainerType.comgr
      ) {
        this.fActiveHover = true;
        return;
      }

      hoverElement.fParentPanel.fBoxDragMode = enumBoxDragMode.moveBranch; //TODO: Why?
      canvasPanel.ClearHighlightCache();
      canvasPanel.DoHighlightElement(hoverElement, true);

      // Box actions live in the React click toolbar (ChartBoxContextToolbar); hover only highlights.
      this.fActiveHover = true;
      return;
    } else {
      if (this.fActiveHover) {
        //TODO: This would have been easier to accomplish with a clearTimer?
        setTimeout(function () {
          killHoverPanel(this);
        }, 1000);
      }
    }
    this.fActiveHover = false;
  }

  RemoveActivityContent() {
    if (this.fActivityBoxes == null) this.fActivityBoxes = [];

    var priorBoxesLength = this.fActivityBoxes.length;
    if (priorBoxesLength === 0) return;

    var allToRemove = [];
    for (var j = 0; j < priorBoxesLength; j++) {
      var theBox = this.fActivityBoxes[j];
      this._collectBoxSubElements(theBox, allToRemove);
      allToRemove.push(theBox);
      theBox["invalid"] = true;
    }
    this.BatchRemoveElements(allToRemove);
  }

  AddActivityContent(planMgr, contentMode, basePage) {
    //Scan through all the boxes on my page and detect if they have some overrides attached.
    this.RemoveActivityContent();

    var scm = this.GetSubChartMgr();
    var curSCID = scm.GetCurrentSubChartID();
    var nodes = scm.GetNodesForSubChart(curSCID, false); //GetSCNodes(subChartID);

    var binDay = {};
    var overridesForThisPage = [];
    for (var i = 0; i < nodes.length; i++) {
      var theNode = nodes[i];
      var dataObj = theNode.GetDataObj(); //We show nodes twice if it has a SC break. Maybe change later?
      if (dataObj == null) continue;
      var trackObj = planMgr.GetActiveOverrideObj(dataObj);
      if (trackObj) {
        overridesForThisPage.push(trackObj);
      }
    }
    if (overridesForThisPage.length == 0) return;

    //Only show on top of boxes if it has a subchart break.
    if (contentMode == "History") {
      overridesForThisPage.sort(function (a, b) {
        return planMgr.SortChangeDate(a, b);
      });
      binDay = overridesForThisPage.reduce((acc, override) => {
        const changeObj = override.lastActiveChange();
        if (!changeObj) {
          return acc;
        }

        const dateString = changeObj.GetChangeDate();
        const d = new Date(convertStdDateString(dateString).toDateString());
        acc[d] = acc[d] ?? [];
        acc[d].push(override);

        return acc;
      }, {});
    } else {
      overridesForThisPage.sort(function (a, b) {
        return planMgr.SortChangeEffectiveDate(a, b);
      });
      binDay = overridesForThisPage.reduce((acc, override) => {
        const changeObj = override.lastActiveChange();
        if (!changeObj) {
          return acc;
        }

        const d = changeObj.GetChangeEffectiveDate();
        acc[d] = binDay[d] ?? [];
        acc[d].push(override);

        return acc;
      });
    }

    var theDoc = this.fWSMgr.GetDoc();
    var pageMgr = theDoc.GetPageMgr();
    var pageProps = pageMgr.GetPageProps();
    var margins = pageProps.fMargins;
    var out_PageSize = basePage;

    var xCoordinate = this.GetWidth() - margins.fRight;
    var yCoordinate = pageProps.fHeaderHeight;

    var binnedTrackList = [];
    xCoordinate += 45;
    for (i = 0; i < overridesForThisPage.length; i++) {
      var theOverride = overridesForThisPage[i];
      var mostRecentChange = theOverride.lastActiveChange();
      if (mostRecentChange == null) continue;
      var changeDate =
        contentMode == "History"
          ? convertStdDateString(mostRecentChange.GetChangeDate())
          : mostRecentChange.GetChangeEffectiveDate();

      var binID = changeDate.GetBinIDFromDate(binDay, false);
      //TODO: Alter for History context.
      binID = contentMode == "History" ? binID : "Changes Effective " + binID;
      if (binnedTrackList.indexOf(binID) === -1) {
        if (binnedTrackList.length > 0) yCoordinate += 16;
        binnedTrackList.push(binID);
        var theLayout = this.GetChangeDividerLayout(binID);
        var box = this.AddChartBox(
          xCoordinate,
          yCoordinate,
          theLayout,
          null,
          null,
          false,
          false,
          false,
          false,
          null,
          false
        );
        this.fActivityBoxes.push(box);
        yCoordinate += MeasureTextHeight("Verdana", 15) + 10;
      }
      var theDataObj = theOverride.SmartGetFieldValue("RefObj");

      var theLayout = this.GetActivityLayout(
        theDataObj,
        theOverride,
        mostRecentChange,
        margins.fRight
      );

      var box = this.AddChartBox(
        xCoordinate + 25,
        yCoordinate,
        theLayout,
        null,
        null,
        false,
        false,
        false,
        false,
        null,
        false
      );
      var overlayType = planMgr.AssignIconToBox(theDataObj);
      this.fActivityBoxes.push(box);

      var theIcon = box.AddElement(overlayType);

      theIcon.SetOverrideWidth(40);
      theIcon.SetIconSize(14);
      theIcon.fDeltaDefX = 0;
      theIcon.fDeltaDefY = 0;
      theIcon.SetPositionX(xCoordinate);
      theIcon.SetPositionY(yCoordinate);

      var boxHeight = box.GetHeight();
      yCoordinate += boxHeight + 6;
    }
    //Recalc appears safe.
    ///this.RecalculateChartLayout(null);
    //clearTimeout(planMgr.fPollListener)
  }

  GetActivityLayout(dataObj, theOverride, mostRecentChange, maxWidth) {
    // We're building a table of cells to display activity data
    // One row for the name
    var numRows = 1;
    var theComment = theOverride.SmartGetFieldValue("Comment");
    var changeType = theOverride.SmartGetFieldValue("Type");
    // And add one row for the comment
    if (theComment) {
      numRows += 1;
    }
    var PersonSupplementText = "";
    var fieldList = [];
    if (changeType == enumOverrideType.change) {
      var fields = mostRecentChange.fDelta;

      for (var field in fields) {
        fieldList.push(field);
      }
      ///Title, Location, and X change
      if (fieldList.length === 1) {
        PersonSupplementText = fieldList[0] + " change";
        // Add rows for the fields
        numRows += 1;
      } else if (fieldList.length < 4) {
        numRows += fieldList.length;
        PersonSupplementText = formatArray(fieldList) + " change";
      } else {
        PersonSupplementText = fieldList.length.toString() + " fields changed";
        numRows += fieldList.length;
      }
    } else if (changeType == enumOverrideType.comment) {
      PersonSupplementText = "comment";
    }
    //Use label/value for field value changes

    var theLayout = new QBoxLayout();
    theLayout.InitBoxLayout(maxWidth - 75, 1, numRows);
    theLayout.SetBoxType(enumBoxType.nonstandard);
    theLayout.SetBoxStyleID(-2);
    theLayout.SetBorderStyle(enumElementBorderStyle.noBorder);
    theLayout.SetWidthSizingMode(enumBoxWidthMode.fixedwidth);
    theLayout.SetHeightSizingMode(enumBoxHeightMode.autoself);
    theLayout.SetPosition(0, 0);

    theLayout.SetWidthMin(maxWidth - 75);
    theLayout.SetWidthMax(maxWidth - 75);
    theLayout.SetBoxPadding(1);
    theLayout.SetBoxFillMode(enumFillMode.noFill);

    var c1 = theLayout.GetCellDesc(1, 1);
    c1.SetAlignment("left");
    c1.SetFontSize(13);
    c1.SetCellContentType(enumCellContentType.FreeText);
    c1.SetWrapText(enumWrapTextMode.fourlines);

    var nameIDtext = dataObj.FormatEmployeeLabel() + " " + PersonSupplementText;
    c1.SetFreeText(nameIDtext);

    for (var aFieldIdx in fieldList) {
      var theField = fieldList[aFieldIdx];
      var theValue = mostRecentChange.fDelta[theField];
      var c1 = theLayout.GetCellDesc(1, parseInt(aFieldIdx) + 2);
      c1.SetAlignment("left");
      c1.SetFontSize(13);
      c1.SetCellContentType(enumCellContentType.FreeText);
      c1.SetWrapText(enumWrapTextMode.fourlines);

      var fieldValText = theField + "  🡺  " + theValue;
      c1.SetFreeText(fieldValText);
    }

    if (theComment) {
      //The comment has to be the last row.
      var commentRow = theLayout.GetCellDesc(1, numRows);
      commentRow.SetAlignment("left");
      commentRow.SetTextItalic(true);
      commentRow.SetTextBold(true);
      commentRow.SetFontSize(13);
      commentRow.SetCellContentType(enumCellContentType.FreeText);
      commentRow.SetFreeText(theComment);
      commentRow.SetWrapText(enumWrapTextMode.fourlines);
    }

    theLayout.CalculateLayout();
    theLayout.SetLayer(enumElementLayer.master);
    return theLayout;
  }

  GetChangeDividerLayout(string) {
    var theLayout = new QBoxLayout();
    theLayout.InitBoxLayout(kIndexPageLabelWidth - 45, 1, 1);
    theLayout.SetBoxType(enumBoxType.nonstandard);
    theLayout.SetBoxStyleID(-2);
    theLayout.SetBorderStyle(enumElementBorderStyle.noBorder);
    theLayout.SetWidthSizingMode(enumBoxWidthMode.fixedwidth);
    theLayout.SetHeightSizingMode(enumBoxHeightMode.fixedheight);
    theLayout.SetPosition(0, 0);

    theLayout.SetWidthMin(kIndexPageLabelWidth);
    theLayout.SetWidthMax(768);
    theLayout.SetBoxPadding(1);
    theLayout.SetBoxFillMode(enumFillMode.noFill);

    var c1 = theLayout.GetCellDesc(1, 1);
    c1.SetTextBold(true);
    c1.SetAlignment("left");
    c1.SetFontSize(15);
    c1.SetCellContentType(enumCellContentType.FreeText);

    var freeText = string;
    c1.SetFreeText(freeText);

    theLayout.SetLayer(enumElementLayer.master);

    theLayout.CalculateLayout();

    return theLayout;
  }
}
